import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { useAnalytics, serializeDateRange, hydrateDateRange } from '../contexts/AnalyticsContext';
import { useTheme } from '../contexts/ThemeContext';
import { getMapThemeOverride, setMapThemeOverride } from '../utils/mapTheme';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Calendar } from '../components/ui/calendar';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { NotificationsConfig } from '../components/events/NotificationsConfig';
import { NotificationTemplateModal } from '../components/events/NotificationTemplateModal';
import { AlertsHelpPanel, AlertFieldTip } from '../components/events/AlertRuleHelp';
import { WebhookActionConfig } from '../components/events/WebhookActionConfig';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { APIProvider, Map as GoogleMapComponent, AdvancedMarker, InfoWindow as GInfoWindow } from '@vis.gl/react-google-maps';
import { useMapStaleGuard } from '../hooks/useMapStaleGuard';
import { GoogleMapController, GoogleTrafficLayer, GoogleIdleListener, GooglePolyline } from '../components/maps/GoogleMapsWrappers';
import { MapContainer, TileLayer, Polyline as LeafletPolyline, Marker as LeafletMarker, CircleMarker as LeafletCircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { snapToRoad } from '../utils/roadSnap';
import { formatDT, formatDateOnly, formatTimeOnly, resolveTimezone, getZonedParts, zonedDateTimeToUtc, getTimeValueInTimezone, formatDistance, formatSpeed, convertDistance, convertSpeed, distanceUnitLabel, speedUnitLabel, getFileTimestamp } from '../utils/timezone';
import { PRESET_COLORS } from '../components/geofencing/GeofencingUtils';
import { Checkbox } from '../components/ui/checkbox';
import { PaginationFooter, usePersistedPageSize } from '../components/shared/PaginationFooter';
import { MobileSectionMenu } from '../components/shared/MobileSectionMenu';
import { DateRangePresetFilter } from '../components/shared/DateRangePresetFilter';
import { useUrlPage, useUrlPageSize } from '../components/shared/useUrlPagination';
import { ForensicTelemetryCard } from '../components/forensic/ForensicTelemetryCard';
import { deviceDisplayName } from '../utils/deviceName';
import { buildDevicesById, formatRecordDeviceLabel } from '../utils/deviceLabel';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Brush, ReferenceLine, Legend,
} from 'recharts';
import { getTimezoneLabel } from '../utils/timezoneList';
import { loadLogoPng } from '../utils/tripExport';
import { exportImagePdf } from '../utils/graphMapPdf';
import { NOMINATIM_BASE } from '../utils/nominatim';

// Module-level cache: fetch the logo once, embed as base64 in every subsequent HTML export
// so the exported file has zero external dependencies (no CDN, works offline forever).
let __exportLogoDataUrlCache = null;
const getExportLogoDataUrl = async () => {
  if (__exportLogoDataUrlCache) return __exportLogoDataUrlCache;
  try {
    const logo = await loadLogoPng();
    if (logo?.dataUrl) __exportLogoDataUrlCache = logo.dataUrl;
  } catch { /* fall through — export continues without logo */ }
  return __exportLogoDataUrlCache || '';
};

// Module-level vendor cache: fetch Leaflet + Chart.js source ONCE from our own /vendor/
// dir and inline into exported HTML so exports have zero external script/style dependencies.
let __vendorCache = { leafletCss: null, leafletJs: null, chartJs: null };
const fetchText = async (url) => { try { const r = await fetch(url); return r.ok ? await r.text() : ''; } catch { return ''; } };
const getExportVendorAssets = async (need) => {
  const out = {};
  if (need.leafletCss) out.leafletCss = __vendorCache.leafletCss || (__vendorCache.leafletCss = await fetchText('/vendor/leaflet/leaflet.css'));
  if (need.leafletJs)  out.leafletJs  = __vendorCache.leafletJs  || (__vendorCache.leafletJs  = await fetchText('/vendor/leaflet/leaflet.js'));
  if (need.chartJs)    out.chartJs    = __vendorCache.chartJs    || (__vendorCache.chartJs    = await fetchText('/vendor/chart/chart.umd.js'));
  return out;
};
import { subDays } from 'date-fns';
import EventDetailModal from '../components/events/EventDetailModal';
import {
  BarChart3,
  FileText,
  Calendar as CalendarIcon,
  History,
  Bell,
  Zap,
  Download,
  RefreshCw,
  Loader2,
  Plus,
  Settings,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  XCircle,
  MapPin,
  Gauge,
  Clock,
  BatteryLow,
  FileClock,
  Power,
  Wrench,
  DollarSign,
  Car,
  Fuel,
  Map,
  Activity,
  Users,
  UserRound,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Square,
  Upload,
  Route,
  Navigation,
  Timer,
  TrendingUp,
  Filter,
  Search,
  Shield,
  Satellite,
  X,
  Eye,
  EyeOff,
  Crosshair,
  Thermometer,
  Radio,
  ChevronLeft,
  ChevronRight,
  Wifi,
  WifiOff,
  CreditCard,
} from 'lucide-react';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { EventRulesManager } from '../components/events/EventRulesManager';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const GOOGLE_MAPS_MAP_ID_NO_POI = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID_NO_POI || GOOGLE_MAPS_MAP_ID;

// Fix for default Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/leaflet/marker-icon-2x.png',
  iconUrl: '/leaflet/marker-icon.png',
  shadowUrl: '/leaflet/marker-shadow.png',
});

// Leaflet Map Controller (only recenters when "center" actually changes meaningfully)
const LeafletMapController = ({ center, zoom, onMapReady }) => {
  const map = useMap();
  const lastCenterRef = useRef(null);

  useEffect(() => {
    if (onMapReady) onMapReady(map);
    return () => { if (onMapReady) onMapReady(null); };
  }, [map, onMapReady]);

  useEffect(() => {
    if (!map || !center || typeof zoom !== 'number') return;

    // Avoid re-setting view if center hasn't actually changed
    const last = lastCenterRef.current;
    const same =
      last &&
      Array.isArray(center) &&
      Array.isArray(last) &&
      Math.abs(center[0] - last[0]) < 1e-9 &&
      Math.abs(center[1] - last[1]) < 1e-9;

    if (!same) {
      map.setView(center, zoom, { animate: true });
      lastCenterRef.current = center;
    }
  }, [map, center, zoom]);

  return null;
};

// Alert type icons mapping
const alertTypeIcons = {
  geofence: MapPin,
  geofence_entry: MapPin,
  geofence_exit: MapPin,
  speed: Gauge,
  speeding: Gauge,
  idle: Clock,
  excessive_idling: Clock,
  sos: AlertCircle,
  low_battery: BatteryLow,
  disconnect: Power,
  device_disconnection: Power,
  maintenance: Wrench,
  maintenance_spend: DollarSign,
  document_expiry: FileClock,
  evidence_viewed: Eye,
  harsh_braking: AlertTriangle,
  harsh_acceleration: TrendingUp,
  harsh_cornering: Navigation,
  harsh_driving: AlertTriangle,
  crash_detection: AlertTriangle,
  video_event: AlertTriangle,
  sensor_io_event: Zap,
  stopped: MapPin,
  moving: Activity,
  excessive_idle: Clock,
  underspeed: Gauge,
  zone_in: Map,
  zone_out: Map,
  place_in: MapPin,
  place_out: MapPin,
  route_entering: Map,
  route_exiting: Map,
  ignition_on: Power,
  ignition_off: Power,
  gprs_connected: Wifi,
  gprs_not_connected: WifiOff,
  gps_connected: Navigation,
  gps_not_connected: Navigation,
  vehicle_proximity: Radio,
  vehicle_proximity_exit: Radio,
  driver_bound: Users,
  unmatched_rfid_scan: CreditCard,
  disabled_rfid_scan: CreditCard,
  unallocated_rfid_scan: CreditCard,
  driver_change: Users,
  asset_in_transit: Zap,
  asset_movement_watch: Zap,
  asset_dropoff: Zap,
  left_behind_asset: Zap,
  person_arrival: Users,
  person_departure: Users,
  occupancy_departure: Users,
  person_bound: Users,
  tow_detection: Car,
  gps_jamming: AlertTriangle,
  power_removal: Power,
};

// Default notification block for a condition/health alert config — mirrors the
// Event Rules notification shape so the shared NotificationsConfig behaves identically.
const DEFAULT_ALERT_NOTIFICATIONS = {
  system_message: true,
  auto_hide: false,
  sound_alert: false,
  sound_file: 'default',
  sound_duration: 'once',
  push_notification: false,
  email_enabled: false,
  email_addresses: '',
  email_template_id: '',
  sms_enabled: false,
  sms_numbers: '',
  sms_template_id: '',
  change_device_card_color: false,
  device_card_color: '#ef4444',
  device_card_color_dark: '#5e2230',
};

// Default Actions block for a condition alert (Webhook only for now; device
// Commands stay in Event Rules, ready to enable for the future Service Module).
const DEFAULT_ALERT_ACTIONS = {
  webhook_enabled: false,
  webhook_method: 'POST',
  webhook_url: '',
  webhook_auth_type: 'none',
  webhook_auth_token: '',
  webhook_headers: [],
  webhook_payload_mode: 'auto',
  webhook_custom_payload: '',
  webhook_retry_count: 0,
  webhook_retry_delay_secs: 5,
};


const severityColors = {
  low: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300',
  medium: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400',
  high: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400',
  critical: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400',
};

const statusColors = {
  new: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400',
  active: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400',
  acknowledged: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400',
  resolved: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400',
};

const stopStateMeta = {
  idle: {
    label: 'Idle',
    textClass: 'text-amber-600 dark:text-amber-400',
  },
  stopped: {
    label: 'Stopped',
    textClass: 'text-red-600 dark:text-red-400',
  },
};

// Primary timestamp of a timeline row for day-grouping purposes.
const getTimelineEventPrimaryTs = (evt) => {
  if (!evt) return null;
  if (evt.type === 'event' && evt.event) return evt.event.triggered_at || null;
  return evt.start_time || evt.timestamp || null;
};

// Stable day key (YYYY-MM-DD) in the active timezone — used to detect day
// boundaries when rendering the History timeline / trip list.
const getTimelineDayKey = (ts, timezone) => {
  if (!ts) return '';
  const parts = getZonedParts(ts, { timezone });
  if (!parts) return '';
  return `${parts.year}-${parts.month}-${parts.day}`;
};

// Sticky day-header label — e.g. "Monday, 12/08/2026" (weekday respects
// the user's locale, date portion respects the app's date-format setting).
const formatTimelineDayHeader = (ts, timezone) => {
  if (!ts) return '';
  try {
    const date = ts instanceof Date ? ts : new Date(ts);
    if (isNaN(date.getTime())) return '';
    const weekday = new Intl.DateTimeFormat(undefined, { timeZone: timezone, weekday: 'long' }).format(date);
    const datePart = formatDateOnly(ts, { timezone });
    return `${weekday}, ${datePart}`;
  } catch {
    return '';
  }
};

const getHistoryDeviceLabel = (device) => deviceDisplayName(device);

const getHistoryStopSvgMarkup = (state = 'stopped', size = 22) => {
  if (state === 'idle') {
    return `<svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="${size}" height="${size}" viewBox="0 0 512 512" enable-background="new 0 0 512 512" xml:space="preserve"><path fill="#fbbf24" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563 L257.225,512l188.509-125.673c11.637-9.31,20.944-23.272,20.944-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><g><path fill="#FFFFFF" d="M236,116.897h40v197.323h-40z"/></g></svg>`;
  }
  return `<svg width="${size}" height="${size}" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="#2563eb" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.509-125.673c11.637-9.31,20.944-23.272,20.944-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M189.555,314.22V116.897h63.935c24.228,0,40.02,0.989,47.378,2.961c11.307,2.961,20.772,9.401,28.4,19.315c7.626,9.916,11.441,22.726,11.441,38.428c0,12.114-2.2,22.299-6.596,30.554c-4.397,8.257-9.983,14.739-16.757,19.45c-6.776,4.711-13.662,7.83-20.661,9.354c-9.513,1.884-23.286,2.827-41.322,2.827h-25.978v74.433H189.555z M229.396,150.278v55.993h21.805c15.702,0,26.201-1.031,31.497-3.096c5.293-2.063,9.442-5.294,12.45-9.691c3.005-4.396,4.509-9.51,4.509-15.344c0-7.178-2.109-13.1-6.326-17.767c-4.219-4.665-9.557-7.582-16.018-8.749c-4.757-0.896-14.313-1.346-28.669-1.346H229.396z"/></svg>`;
};

const HistoryStopIcon = ({ state = 'stopped', size = 22 }) => (
  <div
    style={{ width: size, height: size }}
    dangerouslySetInnerHTML={{ __html: getHistoryStopSvgMarkup(state, size) }}
  />
);

const getHistoryStopGlowFilter = (state = 'stopped') => {
  const glowColor = state === 'idle' ? '#fbbf24' : '#2563eb';
  return `drop-shadow(0 0 8px ${glowColor}) drop-shadow(0 0 16px ${glowColor}) drop-shadow(0 0 24px ${glowColor})`;
};

const createCalendarAnchor = (year, month, day) => new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

const getCalendarParts = (anchorDate) => ({
  year: anchorDate.getUTCFullYear(),
  month: anchorDate.getUTCMonth() + 1,
  day: anchorDate.getUTCDate(),
});

const shiftCalendarAnchor = (anchorDate, days) => {
  const next = new Date(anchorDate);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

const buildHistoryPresetRange = (presetId, timezone) => {
  const nowParts = getZonedParts(new Date(), { timezone }) || getZonedParts(new Date(), { timezone: resolveTimezone() });
  const todayAnchor = createCalendarAnchor(nowParts.year, nowParts.month, nowParts.day);
  let startAnchor = todayAnchor;
  let endAnchor = todayAnchor;

  switch (presetId) {
    case 'last_1h':
    case 'last_3h':
    case 'last_6h':
    case 'last_12h': {
      const hoursMap = { 'last_1h': 1, 'last_3h': 3, 'last_6h': 6, 'last_12h': 12 };
      const hours = hoursMap[presetId];
      const now = new Date();
      return {
        start: new Date(now.getTime() - hours * 3600000),
        end: now,
      };
    }
    case 'today':
      break;
    case 'yesterday':
      startAnchor = shiftCalendarAnchor(todayAnchor, -1);
      endAnchor = startAnchor;
      break;
    case '2days':
      startAnchor = shiftCalendarAnchor(todayAnchor, -2);
      endAnchor = startAnchor;
      break;
    case '3days':
      startAnchor = shiftCalendarAnchor(todayAnchor, -3);
      endAnchor = startAnchor;
      break;
    case 'this_week': {
      const weekday = todayAnchor.getUTCDay();
      const daysSinceMonday = (weekday + 6) % 7;
      startAnchor = shiftCalendarAnchor(todayAnchor, -daysSinceMonday);
      break;
    }
    case 'last_week': {
      const weekday = todayAnchor.getUTCDay();
      const daysSinceMonday = (weekday + 6) % 7;
      const thisWeekStart = shiftCalendarAnchor(todayAnchor, -daysSinceMonday);
      startAnchor = shiftCalendarAnchor(thisWeekStart, -7);
      endAnchor = shiftCalendarAnchor(startAnchor, 6);
      break;
    }
    case 'this_month':
      startAnchor = createCalendarAnchor(nowParts.year, nowParts.month, 1);
      break;
    case 'last_month': {
      const prevMonth = nowParts.month === 1
        ? { year: nowParts.year - 1, month: 12 }
        : { year: nowParts.year, month: nowParts.month - 1 };
      startAnchor = createCalendarAnchor(prevMonth.year, prevMonth.month, 1);
      endAnchor = createCalendarAnchor(nowParts.year, nowParts.month, 0);
      break;
    }
    default:
      return null;
  }

  const startParts = { ...getCalendarParts(startAnchor), hour: 0, minute: 0, second: 0 };
  const endParts = { ...getCalendarParts(endAnchor), hour: 23, minute: 59, second: 59 };

  return {
    start: zonedDateTimeToUtc(startParts, timezone),
    end: zonedDateTimeToUtc(endParts, timezone),
  };
};

// Report types based on development plan
const reportCategories = [
  {
    category: 'General & Object',
    reports: [
      { value: 'general_info', label: 'General Information', icon: FileText },
      { value: 'object_info', label: 'Object Information', icon: Car },
      { value: 'current_position', label: 'Current Position', icon: MapPin },
    ]
  },
  {
    category: 'Route & Travel',
    reports: [
      { value: 'route_data', label: 'Route Data with Sensors', icon: Route },
      { value: 'driving_summary', label: 'Driving Summary', icon: Activity },
      { value: 'drives_stops', label: 'Drives and Stops', icon: Navigation },
      { value: 'travel_sheet', label: 'Travel Sheet', icon: FileText },
      { value: 'mileage_daily', label: 'Mileage (Daily)', icon: TrendingUp },
    ]
  },
  {
    category: 'Speed & Behavior',
    reports: [
      { value: 'overspeeds', label: 'Overspeeds', icon: Gauge },
      { value: 'driver_behavior', label: 'Driver Behavior (RAG)', icon: Activity },
    ]
  },
  {
    category: 'Geofence & Zone',
    reports: [
      { value: 'zone_in_out', label: 'Zone In/Out', icon: Map },
      { value: 'marker_in_out', label: 'Marker In/Out', icon: MapPin },
    ]
  },
  {
    category: 'Fuel & Expense',
    reports: [
      { value: 'fuel_consumption', label: 'Fuel Consumption', icon: Fuel },
      { value: 'fuel_fillings', label: 'Fuel Fillings', icon: Fuel },
      { value: 'idle_fuel_waste', label: 'Idle Fuel Waste', icon: Clock },
      { value: 'expenses', label: 'Expenses', icon: FileText },
    ]
  },
];

// Event types based on development plan
const eventTypes = [
  { value: 'all', label: 'All Events' },
  { value: 'speeding', label: 'Speeding Violations' },
  { value: 'geofence_entry', label: 'Geofence Entry' },
  { value: 'geofence_exit', label: 'Geofence Exit' },
  { value: 'ignition_on', label: 'Ignition On' },
  { value: 'ignition_off', label: 'Ignition Off' },
  { value: 'harsh_braking', label: 'Harsh Braking' },
  { value: 'harsh_acceleration', label: 'Harsh Acceleration' },
  { value: 'harsh_cornering', label: 'Harsh Cornering' },
  { value: 'harsh_driving', label: 'Harsh Driving' },
  { value: 'crash_detection', label: 'Crash Detection' },
  { value: 'video_event', label: 'Video Event' },
  { value: 'excessive_idling', label: 'Excessive Idling' },
  { value: 'low_battery', label: 'Low Battery' },
  { value: 'gps_jamming', label: 'GPS Jamming' },
  { value: 'device_disconnection', label: 'Device Disconnection' },
  { value: 'power_removal', label: 'Power Removal' },
  { value: 'tow_detection', label: 'Tow Detection' },
];

const eventLogFilterOptions = [
  { value: 'all', label: 'All Types' },
  { value: 'asset_dropoff', label: 'Asset Drop-off' },
  { value: 'asset_in_transit', label: 'Asset In Transit' },
  { value: 'asset_movement_watch', label: 'Asset Movement Watch' },
  { value: 'crash_detection', label: 'Crash Detection' },
  { value: 'device_event', label: 'Device Event' },
  { value: 'driver_bound', label: 'Driver Bound' },
  { value: 'driver_change', label: 'Driver Change' },
  { value: 'unmatched_rfid_scan', label: 'Unmatched RFID / iButton Scan' },
  { value: 'disabled_rfid_scan', label: 'Disabled RFID / iButton Scan' },
  { value: 'unallocated_rfid_scan', label: 'Unallocated RFID / iButton Scan' },
  { value: 'excessive_idle', label: 'Excessive Idling' },
  { value: 'left_behind_asset', label: 'Left-Behind Asset' },
  { value: 'occupancy_departure', label: 'Occupancy Departure' },
  { value: 'person_arrival', label: 'Person Arrival' },
  { value: 'person_departure', label: 'Person Departure' },
  { value: 'person_bound', label: 'Staff / Passenger Bound' },
  { value: 'gprs_connected', label: 'GPRS Connected' },
  { value: 'gprs_not_connected', label: 'GPRS Not Connected' },
  { value: 'gps_connected', label: 'GPS Connected' },
  { value: 'gps_not_connected', label: 'GPS Not Connected' },
  { value: 'harsh_driving', label: 'Harsh Driving' },
  { value: 'moving', label: 'Moving' },
  { value: 'overspeed_fixed', label: 'Overspeed (Fixed)' },
  { value: 'overspeed_road_sign', label: 'Overspeed (Road Sign)' },
  { value: 'place_in', label: 'Place Entering' },
  { value: 'place_out', label: 'Place Exiting' },
  { value: 'vehicle_proximity', label: 'Proximity Enter' },
  { value: 'vehicle_proximity_exit', label: 'Proximity Exit' },
  { value: 'route_entering', label: 'Route Entering' },
  { value: 'route_exiting', label: 'Route Exiting' },
  { value: 'sensor_io_event', label: 'Sensor / IO Event' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'underspeed', label: 'Underspeed' },
  { value: 'video_event', label: 'Video Event' },
  { value: 'zone_in', label: 'Zone Entering' },
  { value: 'zone_out', label: 'Zone Exiting' },
];

export const Analytics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { api, onDeviceUpdate } = useAuth();
  const { isDark } = useTheme();
  const { 
    features, 
    isSubpageEnabled,
    isFeatureEnabled,
    hasOSMMaps,
    hasGoogleMaps,
    hasSatelliteView,
    hasTerrainView,
    hasTrafficLayer,
    isNotificationChannelActive,
    getNotificationDisabledReason
  } = useFeatureFlags();

  // Check which subpages are enabled for this user
  const hasReports = isSubpageEnabled('analytics_reports');
  const hasEvents = isSubpageEnabled('analytics_events');
  const hasAlerts = isSubpageEnabled('analytics_alerts');
  const hasHistory = isSubpageEnabled('analytics_history');
  const hasForensicPanel = isSubpageEnabled('analytics_history_forensic_panel');
  const hasMaintenancePage = isSubpageEnabled('fleet_maintenance');
  const hasInvestigations = isSubpageEnabled('analytics_investigations');
  const hasSnapToRoad = isFeatureEnabled('snap_to_road');
  const hasEventActions = isFeatureEnabled('event_webhook_commands');
  const hasDarkMode = isFeatureEnabled('map_dark_mode');

  // Set default active tab to first available subpage - SECURITY FIX
  const getDefaultTab = () => {
    if (hasHistory) return 'history';
    if (hasEvents) return 'events';
    if (hasAlerts) return 'alerts';
    if (hasReports) return 'reports';
    if (hasInvestigations) return 'investigations';
    return null;  // FIXED: Return null if no subpages enabled (was: return 'reports')
  };

  const requestedTab = new URLSearchParams(location.search).get('tab');
  const getRequestedTab = () => {
    const allowedTabs = { history: hasHistory, events: hasEvents, alerts: hasAlerts, reports: hasReports, investigations: hasInvestigations };
    return allowedTabs[requestedTab] ? requestedTab : null;
  };

  // ── Persisted state hub (survives navigation + F5) ──
  // Destructure stable setters from context once (useState setters are guaranteed stable by React,
  // so omitting them from effect deps is safe and avoids infinite-loop renders via anCtx churn).
  const anCtx = useAnalytics();
  const {
    setPersistedActiveTab: ctxSetActiveTab,
    setMobileHistoryView: ctxSetMobileHistoryView,
    setMobileInvestigationView: ctxSetMobileInvestigationView,
    setEventFilter: ctxSetEventFilter,
    setEventStatusFilter: ctxSetEventStatusFilter,
    setEventSeverityFilter: ctxSetEventSeverityFilter,
    setEventSearch: ctxSetEventSearch,
    setEventDatePreset: ctxSetEventDatePreset,
    setEventDateRangeIso: ctxSetEventDateRangeIso,
    setEventsView: ctxSetEventsView,
    setEventsPage: ctxSetEventsPage,
    setAlertFilter: ctxSetAlertFilter,
    setAlertSeverityFilter: ctxSetAlertSeverityFilter,
    setAlertTypeFilter: ctxSetAlertTypeFilter,
    setAlertSearch: ctxSetAlertSearch,
    setAlertDatePreset: ctxSetAlertDatePreset,
    setAlertDateRangeIso: ctxSetAlertDateRangeIso,
    setHistoryDevice: ctxSetHistoryDevice,
    setHistoryTimezoneMode: ctxSetHistoryTimezoneMode,
    setHistoryDatePreset: ctxSetHistoryDatePreset,
    setHistoryDateRangeIso: ctxSetHistoryDateRangeIso,
    setSplitMode: ctxSetSplitMode,
    setStopThresholdSecs: ctxSetStopThresholdSecs,
    setIdleThresholdSecs: ctxSetIdleThresholdSecs,
    setHistoryMapCenter: ctxSetHistoryMapCenter,
    setHistoryMapZoom: ctxSetHistoryMapZoom,
    setHistoryShowPOI: ctxSetHistoryShowPOI,
    setHistoryDarkMode: ctxSetHistoryDarkMode,
    setShowRouteOverlay: ctxSetShowRouteOverlay,
    setShowDataPoints: ctxSetShowDataPoints,
    setShowIdleIcons: ctxSetShowIdleIcons,
    setShowStoppedIcons: ctxSetShowStoppedIcons,
    setShowEventIcons: ctxSetShowEventIcons,
    setSnapToRoadEnabled: ctxSetSnapToRoadEnabled,
    setForensicActiveTab: ctxSetForensicActiveTab,
    setMessageViewMode: ctxSetMessageViewMode,
    setGraphEnabledFields: ctxSetGraphEnabledFields,
    setGraphCustomColors: ctxSetGraphCustomColors,
    setInvAddress: ctxSetInvAddress,
    setInvLat: ctxSetInvLat,
    setInvLng: ctxSetInvLng,
    setInvDatePreset: ctxSetInvDatePreset,
    setInvDateRangeIso: ctxSetInvDateRangeIso,
    setInvRadius: ctxSetInvRadius,
    setSelectedReportType: ctxSetSelectedReportType,
    setReportFormat: ctxSetReportFormat,
    setReportDateRangeIso: ctxSetReportDateRangeIso,
  } = anCtx;

  const [activeTab, setActiveTab] = useState(() => {
    const requested = getRequestedTab();
    if (requested) return requested;
    const allowedTabs = { history: hasHistory, events: hasEvents, alerts: hasAlerts, reports: hasReports, investigations: hasInvestigations };
    if (anCtx.persistedActiveTab && allowedTabs[anCtx.persistedActiveTab]) return anCtx.persistedActiveTab;
    return getDefaultTab();
  });
  const [loading, setLoading] = useState(true);
  const [mobileHistoryView, setMobileHistoryView] = useState(anCtx.mobileHistoryView || 'list');
  const [mobileInvestigationView, setMobileInvestigationView] = useState(anCtx.mobileInvestigationView || 'search');
  const [pendingHistoryLaunch, setPendingHistoryLaunch] = useState(null);

  // Mirror activeTab → context (URL still wins on initial load)
  useEffect(() => { if (activeTab) ctxSetActiveTab(activeTab); }, [activeTab, ctxSetActiveTab]);
  // Mirror mobile toggles
  useEffect(() => { ctxSetMobileHistoryView(mobileHistoryView); }, [mobileHistoryView, ctxSetMobileHistoryView]);
  useEffect(() => { ctxSetMobileInvestigationView(mobileInvestigationView); }, [mobileInvestigationView, ctxSetMobileInvestigationView]);

  // SECURITY FIX: Update activeTab when feature flags load
  useEffect(() => {
    const requested = getRequestedTab();
    if (requested && activeTab !== requested) {
      setActiveTab(requested);
      return;
    }
    const allowedTabs = { history: hasHistory, events: hasEvents, alerts: hasAlerts, reports: hasReports, investigations: hasInvestigations };
    if (!activeTab || allowedTabs[activeTab] !== true) {
      const defaultTab = getDefaultTab();
      if (defaultTab) {
        setActiveTab(defaultTab);
      }
    }
  // getRequestedTab/getDefaultTab only read `requestedTab` and the has* flags, all
  // already in this dependency array, so the effect re-runs correctly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab, activeTab, hasReports, hasEvents, hasAlerts, hasHistory, hasInvestigations]);

  const handleAnalyticsTabChange = useCallback((tab) => {
    const allowedTabs = { history: hasHistory, events: hasEvents, alerts: hasAlerts, reports: hasReports, investigations: hasInvestigations };
    if (!allowedTabs[tab]) return;
    setActiveTab(tab);
    navigate(`/analytics?tab=${tab}`, { replace: true });
  }, [hasAlerts, hasEvents, hasHistory, hasInvestigations, hasReports, navigate]);

  // Reports state
  const [reports, setReports] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState(anCtx.selectedReportType || 'driving_summary');
  const [reportDateRange, setReportDateRange] = useState(() => hydrateDateRange(anCtx.reportDateRangeIso, {
    start: subDays(new Date(), 7),
    end: new Date(),
  }));
  const [reportFormat, setReportFormat] = useState(anCtx.reportFormat || 'pdf');
  const [generating, setGenerating] = useState(false);

  // Mirror Reports → context
  useEffect(() => { ctxSetSelectedReportType(selectedReportType); }, [selectedReportType, ctxSetSelectedReportType]);
  useEffect(() => { ctxSetReportFormat(reportFormat); }, [reportFormat, ctxSetReportFormat]);
  useEffect(() => { ctxSetReportDateRangeIso(serializeDateRange(reportDateRange)); }, [reportDateRange, ctxSetReportDateRangeIso]);

  // Events state
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [eventSelectAllLoading, setEventSelectAllLoading] = useState(false);
  const [eventExporting, setEventExporting] = useState(false);
  const [eventsPage, setEventsPage] = useUrlPage('page', { zeroIndexed: true, defaultPage: anCtx.eventsPage || 0 });
  const [eventsPageSize, setEventsPageSize] = useUrlPageSize('events', 25);
  const EVENTS_PAGE_SIZE = eventsPageSize;
  const [eventFilter, setEventFilter] = useState(anCtx.eventFilter || 'all');
  const [eventStatusFilter, setEventStatusFilter] = useState(anCtx.eventStatusFilter || 'all');
  const [eventSeverityFilter, setEventSeverityFilter] = useState(anCtx.eventSeverityFilter || 'all');
  const [eventSearch, setEventSearch] = useState(anCtx.eventSearch || '');
  const [eventDatePreset, setEventDatePreset] = useState(anCtx.eventDatePreset || 'all');
  const [eventDateRange, setEventDateRange] = useState(() => hydrateDateRange(anCtx.eventDateRangeIso, { start: null, end: null }));
  const [eventStats, setEventStats] = useState(null);
  const [eventsView, setEventsView] = useState(anCtx.eventsView || 'occurrences'); // 'occurrences' | 'rules'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  // Sticks to true after the first Event Log fetch so refetches don't blank the
  // table on every keystroke.
  const [eventsInitialLoadDone, setEventsInitialLoadDone] = useState(false);
  const [eventStartOpen, setEventStartOpen] = useState(false);
  const [eventEndOpen, setEventEndOpen] = useState(false);

  // Mirror Events → context
  useEffect(() => { ctxSetEventFilter(eventFilter); }, [eventFilter, ctxSetEventFilter]);
  useEffect(() => { ctxSetEventStatusFilter(eventStatusFilter); }, [eventStatusFilter, ctxSetEventStatusFilter]);
  useEffect(() => { ctxSetEventSeverityFilter(eventSeverityFilter); }, [eventSeverityFilter, ctxSetEventSeverityFilter]);
  useEffect(() => { ctxSetEventSearch(eventSearch); }, [eventSearch, ctxSetEventSearch]);
  useEffect(() => { ctxSetEventDatePreset(eventDatePreset); }, [eventDatePreset, ctxSetEventDatePreset]);
  useEffect(() => { ctxSetEventsView(eventsView); }, [eventsView, ctxSetEventsView]);
  useEffect(() => { ctxSetEventsPage(eventsPage); }, [eventsPage, ctxSetEventsPage]);
  useEffect(() => { ctxSetEventDateRangeIso(serializeDateRange(eventDateRange)); }, [eventDateRange, ctxSetEventDateRangeIso]);

  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertStats, setAlertStats] = useState(null);
  const [alertFilter, setAlertFilter] = useState(anCtx.alertFilter || 'all');
  const [alertSeverityFilter, setAlertSeverityFilter] = useState(anCtx.alertSeverityFilter || 'all');
  const [alertRules, setAlertRules] = useState([]);
  // Alerts list — Event-Log-style pagination/search/filter/export
  const [alertTypeFilter, setAlertTypeFilter] = useState(anCtx.alertTypeFilter || 'all');
  const [alertSearch, setAlertSearch] = useState(anCtx.alertSearch || '');
  const [alertDatePreset, setAlertDatePreset] = useState(anCtx.alertDatePreset || 'all');
  const [alertDateRange, setAlertDateRange] = useState(() => hydrateDateRange(anCtx.alertDateRangeIso, { start: null, end: null }));
  const [alertsPage, setAlertsPage] = useUrlPage('apage', { zeroIndexed: true, defaultPage: 0 });
  const [alertsPageSize, setAlertsPageSize] = useUrlPageSize('alerts', 25);
  const [alertsTotal, setAlertsTotal] = useState(0);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsInitialLoadDone, setAlertsInitialLoadDone] = useState(false);
  const [selectedAlertIds, setSelectedAlertIds] = useState([]);
  const [alertSelectAllLoading, setAlertSelectAllLoading] = useState(false);
  const [alertExporting, setAlertExporting] = useState(false);

  // Mirror Alert filters → context (persist across navigation like the Event Log)
  useEffect(() => { ctxSetAlertFilter(alertFilter); }, [alertFilter, ctxSetAlertFilter]);
  useEffect(() => { ctxSetAlertSeverityFilter(alertSeverityFilter); }, [alertSeverityFilter, ctxSetAlertSeverityFilter]);
  useEffect(() => { ctxSetAlertTypeFilter(alertTypeFilter); }, [alertTypeFilter, ctxSetAlertTypeFilter]);
  useEffect(() => { ctxSetAlertSearch(alertSearch); }, [alertSearch, ctxSetAlertSearch]);
  useEffect(() => { ctxSetAlertDatePreset(alertDatePreset); }, [alertDatePreset, ctxSetAlertDatePreset]);
  useEffect(() => { ctxSetAlertDateRangeIso(serializeDateRange(alertDateRange)); }, [alertDateRange, ctxSetAlertDateRangeIso]);
  const [isAddRuleDialogOpen, setIsAddRuleDialogOpen] = useState(false);
  const [batteryConfig, setBatteryConfig] = useState({
    is_active: false,
    severity: 'medium',
    threshold_percent: 20,
    hysteresis_percent: 5,
    notify_bell: true,
    notify_push: true,
    notifications: { ...DEFAULT_ALERT_NOTIFICATIONS },
    actions: { ...DEFAULT_ALERT_ACTIONS },
  });
  const [savingAlertSettings, setSavingAlertSettings] = useState(false);
  const [availableConditions, setAvailableConditions] = useState({ low_battery: true, disconnect: true, maintenance: false, maintenance_spend: true, document_expiry: true, evidence_viewed: false });
  const [disconnectConfig, setDisconnectConfig] = useState({
    is_active: false,
    severity: 'medium',
    offline_minutes: 0,
    escalate_after_hours: 0,
    escalate_severity: 'critical',
    notify_bell: true,
    notify_push: true,
    notifications: { ...DEFAULT_ALERT_NOTIFICATIONS },
    actions: { ...DEFAULT_ALERT_ACTIONS },
  });
  const [notifTemplates, setNotifTemplates] = useState([]);
  const [docExpiryConfig, setDocExpiryConfig] = useState({
    is_active: false,
    severity: 'medium',
    warn_days: 30,
    doc_types: ['licence', 'id', 'certificate', 'other'],
    include_license_expiry: true,
    include_person_documents: true,
    include_vehicle_documents: true,
    notify_bell: true,
    notify_push: true,
    notifications: { ...DEFAULT_ALERT_NOTIFICATIONS },
    actions: { ...DEFAULT_ALERT_ACTIONS },
  });
  const [spendConfig, setSpendConfig] = useState({
    is_active: false,
    severity: 'high',
    threshold: '',
    overrides: [],
    notify_bell: true,
    notify_push: true,
    notifications: { ...DEFAULT_ALERT_NOTIFICATIONS },
    actions: { ...DEFAULT_ALERT_ACTIONS },
  });
  const [evidenceViewedConfig, setEvidenceViewedConfig] = useState({
    is_active: false,
    severity: 'low',
    alert_on_download: true,
    notifications: { ...DEFAULT_ALERT_NOTIFICATIONS },
    actions: { ...DEFAULT_ALERT_ACTIONS },
  });
  const [alertTemplateModalOpen, setAlertTemplateModalOpen] = useState(false);
  const [alertTemplateInitialType, setAlertTemplateInitialType] = useState('email');
  const hasPushNotif = isNotificationChannelActive('notification_push');
  const hasEmailNotif = isNotificationChannelActive('notification_email');
  const hasSmsNotif = isNotificationChannelActive('notification_sms');
  const pushDisabledReason = getNotificationDisabledReason('notification_push');
  const emailDisabledReason = getNotificationDisabledReason('notification_email');
  const smsDisabledReason = getNotificationDisabledReason('notification_sms');
  const alertTemplateReturnRef = useRef(null);
  const [configureType, setConfigureType] = useState(null);
  // Manage-templates flow from inside a Configure modal: close it, open the shared
  // template manager, reopen the Configure modal when done.
  const openAlertTemplateManager = useCallback((type) => {
    setAlertTemplateInitialType(type || 'email');
    alertTemplateReturnRef.current = configureType;
    setConfigureType(null);
    setTimeout(() => setAlertTemplateModalOpen(true), 150);
  }, [configureType]);
  const fetchNotifTemplates = useCallback(async () => {
    try {
      const res = await api.get('/event-notification-templates');
      setNotifTemplates(res.data.templates || []);
    } catch { /* ignore */ }
  }, [api]);

  // History state
  const [historyDevice, setHistoryDevice] = useState(anCtx.historyDevice || '');
  const [deviceSearch, setDeviceSearch] = useState('');
  const [deviceDropdownOpen, setDeviceDropdownOpen] = useState(false);
  const [historyTimezoneMode, setHistoryTimezoneMode] = useState(anCtx.historyTimezoneMode || 'device');
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [historyDatePreset, setHistoryDatePreset] = useState(anCtx.historyDatePreset || 'today');
  const [historyDateRange, setHistoryDateRange] = useState(() => hydrateDateRange(anCtx.historyDateRangeIso, buildHistoryPresetRange('today', resolveTimezone())));
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [splitMode, setSplitMode] = useState(anCtx.splitMode || 'none');
  const [stopThresholdSecs, setStopThresholdSecs] = useState(anCtx.stopThresholdSecs || 120);
  const [idleThresholdSecs, setIdleThresholdSecs] = useState(anCtx.idleThresholdSecs || 120);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [detectedStops, setDetectedStops] = useState([]);
  const [detectedIdles, setDetectedIdles] = useState([]);
  const [selectedTimelineIdx, setSelectedTimelineIdx] = useState(null);
  const [highlightedSegment, setHighlightedSegment] = useState(null);
  const [highlightedStopIdx, setHighlightedStopIdx] = useState(null);
  const [highlightedMarker, setHighlightedMarker] = useState(null); // 'start' | 'end' | null
  const [mapBubble, setMapBubble] = useState(null); // { type: 'stop'|'event'|'start'|'end'|'point', lat, lng, data }

  // Mirror History filters → context
  useEffect(() => { ctxSetHistoryDevice(historyDevice); }, [historyDevice, ctxSetHistoryDevice]);
  useEffect(() => { ctxSetHistoryTimezoneMode(historyTimezoneMode); }, [historyTimezoneMode, ctxSetHistoryTimezoneMode]);
  useEffect(() => { ctxSetHistoryDatePreset(historyDatePreset); }, [historyDatePreset, ctxSetHistoryDatePreset]);
  useEffect(() => { ctxSetHistoryDateRangeIso(serializeDateRange(historyDateRange)); }, [historyDateRange, ctxSetHistoryDateRangeIso]);
  useEffect(() => { ctxSetSplitMode(splitMode); }, [splitMode, ctxSetSplitMode]);
  useEffect(() => { ctxSetStopThresholdSecs(stopThresholdSecs); }, [stopThresholdSecs, ctxSetStopThresholdSecs]);
  useEffect(() => { ctxSetIdleThresholdSecs(idleThresholdSecs); }, [idleThresholdSecs, ctxSetIdleThresholdSecs]);

  // Forensic Panel state
  const [forensicData, setForensicData] = useState(null); // { points, graph_fields, io_mappings }
  const [forensicLoading, setForensicLoading] = useState(false);
  const [selectedHistoryPoint, setSelectedHistoryPoint] = useState(null); // shared selected point index
  const manualPointSelectRef = useRef(false); // true only when user clicks a point (not slider/playback)
  // Wrapper: selecting a point manually (click/nav) pauses playback + flags for auto-scroll
  const selectHistoryPointManual = useCallback((idx) => {
    if (isPlaying) setIsPlaying(false);
    manualPointSelectRef.current = true;
    setSelectedHistoryPoint(idx);
  }, [isPlaying]);
  const [forensicActiveTab, setForensicActiveTab] = useState(anCtx.forensicActiveTab || 'telemetry'); // telemetry | graphical | messages
  // Graph state
  const [graphEnabledFields, setGraphEnabledFields] = useState(anCtx.graphEnabledFields || ['speed', 'ignition']); // default enabled fields
  const [graphCustomColors, setGraphCustomColors] = useState(anCtx.graphCustomColors || {}); // { fieldKey: '#hexcolor' }
  // Message Data state
  const [messageViewMode, setMessageViewMode] = useState(anCtx.messageViewMode || 'mapped'); // 'raw' | 'mapped'
  const [messagePage, setMessagePage] = useState(0);
  const [messageSortAsc, setMessageSortAsc] = useState(true);
  const MESSAGE_PAGE_SIZE = 50;
  // Map overlay toggles (only active after search)
  const [showRouteOverlay, setShowRouteOverlay] = useState(anCtx.showRouteOverlay !== undefined ? anCtx.showRouteOverlay : true);
  const [showDataPoints, setShowDataPoints] = useState(anCtx.showDataPoints || false);
  const [showIdleIcons, setShowIdleIcons] = useState(anCtx.showIdleIcons !== undefined ? anCtx.showIdleIcons : true);
  const [showStoppedIcons, setShowStoppedIcons] = useState(anCtx.showStoppedIcons !== undefined ? anCtx.showStoppedIcons : true);
  const [showEventIcons, setShowEventIcons] = useState(anCtx.showEventIcons !== undefined ? anCtx.showEventIcons : true);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [highlightedEventIdx, setHighlightedEventIdx] = useState(null);
  const [historyDarkMode, setHistoryDarkMode] = useState(anCtx.historyDarkMode || false);
  const [snapToRoadEnabled, setSnapToRoadEnabled] = useState(anCtx.snapToRoadEnabled || false);
  const [snappedRoutePath, setSnappedRoutePath] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);

  // Mirror Forensic + Overlays → context
  useEffect(() => { ctxSetForensicActiveTab(forensicActiveTab); }, [forensicActiveTab, ctxSetForensicActiveTab]);
  useEffect(() => { ctxSetMessageViewMode(messageViewMode); }, [messageViewMode, ctxSetMessageViewMode]);
  useEffect(() => { ctxSetGraphEnabledFields(graphEnabledFields); }, [graphEnabledFields, ctxSetGraphEnabledFields]);
  useEffect(() => { ctxSetGraphCustomColors(graphCustomColors); }, [graphCustomColors, ctxSetGraphCustomColors]);
  useEffect(() => { ctxSetShowRouteOverlay(showRouteOverlay); }, [showRouteOverlay, ctxSetShowRouteOverlay]);
  useEffect(() => { ctxSetShowDataPoints(showDataPoints); }, [showDataPoints, ctxSetShowDataPoints]);
  useEffect(() => { ctxSetShowIdleIcons(showIdleIcons); }, [showIdleIcons, ctxSetShowIdleIcons]);
  useEffect(() => { ctxSetShowStoppedIcons(showStoppedIcons); }, [showStoppedIcons, ctxSetShowStoppedIcons]);
  useEffect(() => { ctxSetShowEventIcons(showEventIcons); }, [showEventIcons, ctxSetShowEventIcons]);
  useEffect(() => { ctxSetHistoryDarkMode(historyDarkMode); }, [historyDarkMode, ctxSetHistoryDarkMode]);
  useEffect(() => { ctxSetSnapToRoadEnabled(snapToRoadEnabled); }, [snapToRoadEnabled, ctxSetSnapToRoadEnabled]);

  // Investigations state
  const [invAddress, setInvAddress] = useState(anCtx.invAddress || '');
  const [invLat, setInvLat] = useState(anCtx.invLat ?? null);
  const [invLng, setInvLng] = useState(anCtx.invLng ?? null);
  const [invDatePreset, setInvDatePreset] = useState(anCtx.invDatePreset || 'today');
  const [invDateRange, setInvDateRange] = useState(() => hydrateDateRange(anCtx.invDateRangeIso, buildHistoryPresetRange('today', resolveTimezone())));
  const [invStartOpen, setInvStartOpen] = useState(false);
  const [invEndOpen, setInvEndOpen] = useState(false);
  const [invRadius, setInvRadius] = useState(anCtx.invRadius || 500);
  const [invResults, setInvResults] = useState(null);
  const [invLoading, setInvLoading] = useState(false);
  const [invGeocoding, setInvGeocoding] = useState(false);

  // Mirror Investigations → context
  useEffect(() => { ctxSetInvAddress(invAddress); }, [invAddress, ctxSetInvAddress]);
  useEffect(() => { ctxSetInvLat(invLat); }, [invLat, ctxSetInvLat]);
  useEffect(() => { ctxSetInvLng(invLng); }, [invLng, ctxSetInvLng]);
  useEffect(() => { ctxSetInvDatePreset(invDatePreset); }, [invDatePreset, ctxSetInvDatePreset]);
  useEffect(() => { ctxSetInvDateRangeIso(serializeDateRange(invDateRange)); }, [invDateRange, ctxSetInvDateRangeIso]);
  useEffect(() => { ctxSetInvRadius(invRadius); }, [invRadius, ctxSetInvRadius]);

  // Investigations timezone: use user timezone (no device-specific context)
  const invTimezone = React.useMemo(() => resolveTimezone(), []);
  const invTimezoneLabel = React.useMemo(() => getTimezoneLabel(invTimezone), [invTimezone]);

  // Sync investigations date preset
  const applyInvDatePreset = useCallback((presetId) => {
    const range = buildHistoryPresetRange(presetId, invTimezone);
    setInvDateRange(range);
  }, [invTimezone]);
  useEffect(() => {
    if (invDatePreset !== 'custom') applyInvDatePreset(invDatePreset);
  }, [invDatePreset, applyInvDatePreset]);

  // Date preset handler
  const DATE_PRESETS = [
    { id: 'last_1h', label: 'Last 1 Hour' },
    { id: 'last_3h', label: 'Last 3 Hours' },
    { id: 'last_6h', label: 'Last 6 Hours' },
    { id: 'last_12h', label: 'Last 12 Hours' },
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: '2days', label: '2 Days Ago' },
    { id: '3days', label: '3 Days Ago' },
    { id: 'this_week', label: 'This Week' },
    { id: 'last_week', label: 'Last Week' },
    { id: 'this_month', label: 'This Month' },
    { id: 'last_month', label: 'Last Month' },
    { id: 'custom', label: 'Custom Range' },
  ];

  // History map controls
  const SYDNEY = { lat: -33.8688, lng: 151.2093 };
  const [historyMapCenter, setHistoryMapCenter] = useState(anCtx.historyMapCenter || SYDNEY);
  const [historyMapZoom, setHistoryMapZoom] = useState(anCtx.historyMapZoom || 12);

  // Mirror History map viewport → context
  useEffect(() => { ctxSetHistoryMapCenter(historyMapCenter); }, [historyMapCenter, ctxSetHistoryMapCenter]);
  useEffect(() => { ctxSetHistoryMapZoom(historyMapZoom); }, [historyMapZoom, ctxSetHistoryMapZoom]);

  // Determine default map type based on available providers
  const getDefaultHistoryMapType = () => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('preferred_map_type');
    if (saved) {
      // Validate saved preference is still available based on current privileges
      if (saved === 'osm' && hasOSMMaps) return 'osm';
      if (saved !== 'osm' && hasGoogleMaps) return saved;
    }
    // Default logic based on what's available
    if (hasGoogleMaps) return 'roadmap';
    return 'osm';
  };

  const [historyMapType, setHistoryMapTypeState] = useState(getDefaultHistoryMapType());
  const prevHistoryTimezoneRef = useRef(null);

  // Update map type when privileges change
  useEffect(() => {
    if (historyMapType === 'osm' && !hasOSMMaps && hasGoogleMaps) {
      setHistoryMapTypeState('roadmap');
    } else if (historyMapType !== 'osm' && !hasGoogleMaps && hasOSMMaps) {
      setHistoryMapTypeState('osm');
    } else if (historyMapType !== 'osm' && !hasGoogleMaps && !hasOSMMaps) {
      setHistoryMapTypeState('osm');
    }
  }, [hasOSMMaps, hasGoogleMaps, historyMapType]);

  // Wrapper to save preference when changing map type — captures current position first
  const setHistoryMapType = (type) => {
    // Capture current map position before switching
    if (historyMapType === 'osm' && historyLeafletMapRef.current?._mapPane && historyLeafletMapRef.current?._loaded) {
      const c = historyLeafletMapRef.current.getCenter();
      const z = historyLeafletMapRef.current.getZoom();
      if (c) {
        setHistoryMapCenter({ lat: c.lat, lng: c.lng });
        setHistoryMapZoom(z);
      }
    } else if (historyMapType !== 'osm' && historyMapRef.current) {
      const c = historyMapRef.current.getCenter();
      const z = historyMapRef.current.getZoom();
      if (c) {
        setHistoryMapCenter({ lat: c.lat(), lng: c.lng() });
        setHistoryMapZoom(z);
      }
    }
    localStorage.setItem('preferred_map_type', type);
    setHistoryMapTypeState(type);
  };
  const [historyShowPOI, setHistoryShowPOI] = useState(anCtx.historyShowPOI || false);
  const gmapEpoch = useMapStaleGuard();
  useEffect(() => { ctxSetHistoryShowPOI(historyShowPOI); }, [historyShowPOI, ctxSetHistoryShowPOI]);

  const handleHistoryTogglePOI = useCallback(() => {
    if (historyMapRef.current) {
      const c = historyMapRef.current.getCenter();
      const z = historyMapRef.current.getZoom();
      if (c) {
        setHistoryMapCenter({ lat: c.lat(), lng: c.lng() });
        setHistoryMapZoom(z);
      }
    }
    setHistoryShowPOI(prev => !prev);

  }, []);
  const [historyIsFullscreen, setHistoryIsFullscreen] = useState(false);
  const [historyAddressSearch, setHistoryAddressSearch] = useState('');
  const [historySearchMarker, setHistorySearchMarker] = useState(null);
  const [historyInfoWindowOpen, setHistoryInfoWindowOpen] = useState(null);
  // Stable ref — an inline array recreates every render, forcing the InfoWindow
  // open-effect to close/re-open (flicker + focus steal from open popovers)
  const HISTORY_BUBBLE_PIXEL_OFFSET = useRef([0, -28]).current;
  const historyMapRef = useRef(null);
  const historyLeafletMapRef = useRef(null);
  const historyGoogleAnimatingRef = useRef(false);
  const playbackIntervalRef = useRef(null);
  const timelineScrollRef = useRef(null);
  const [devices, setDevices] = useState([]);

  useEffect(() => {
    if (historyMapType === 'osm' && historyLeafletMapRef.current?._mapPane && historyLeafletMapRef.current?._loaded) {
      const center = historyLeafletMapRef.current.getCenter();
      const zoom = historyLeafletMapRef.current.getZoom();
      if (center) {
        setHistoryMapCenter({ lat: center.lat, lng: center.lng });
        setHistoryMapZoom(zoom);
      }
    } else if (historyMapRef.current) {
      const center = historyMapRef.current.getCenter();
      const zoom = historyMapRef.current.getZoom();
      if (center) {
        setHistoryMapCenter({ lat: center.lat(), lng: center.lng() });
        setHistoryMapZoom(zoom);
      }
    }

    // Restore the user's explicit map-mode choice (shared across the three map pages);
    // fall back to the master app theme only when they've never toggled a map.
    setHistoryDarkMode(hasDarkMode ? (getMapThemeOverride(isDark) ?? isDark) : false);
  }, [hasDarkMode, historyMapType, isDark]);

  // Live id→device map for re-labelling event/alert/investigation rows
  // with the current Asset/Unit. Renames propagate to historical rows
  // without a backend backfill.
  const devicesById = useMemo(() => buildDevicesById(devices), [devices]);

  // Sorted & filtered devices for the history device picker
  const sortedFilteredDevices = React.useMemo(() => {
    const trackable = devices.filter(d => !(d.device_type === 'beacon' && d.beacon_purpose === 'personal'));
    const sorted = trackable.sort((a, b) => getHistoryDeviceLabel(a).localeCompare(getHistoryDeviceLabel(b)));
    if (!deviceSearch.trim()) return sorted;
    const q = deviceSearch.toLowerCase();
    return sorted.filter(d => getHistoryDeviceLabel(d).toLowerCase().includes(q));
  }, [devices, deviceSearch]);

  // Visible non-moving markers: Trips mode shows idles only; Whole Period shows stops + idles
  const visibleNonMovingSegments = React.useMemo(() => {
    if (splitMode === 'ignition') {
      return selectedTrip?.idles || [];
    }
    const sortByStartTime = (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
    return [...detectedStops, ...detectedIdles].sort(sortByStartTime);
  }, [splitMode, selectedTrip, detectedStops, detectedIdles]);

  const selectedDeviceName = React.useMemo(() => {
    if (!historyDevice) return '';
    const d = devices.find(dev => dev.id === historyDevice);
    return getHistoryDeviceLabel(d) || historyDevice;
  }, [devices, historyDevice]);

  // Get the currently selected forensic point data
  const selectedForensicPoint = React.useMemo(() => {
    if (selectedHistoryPoint === null || !forensicData?.points) return null;
    return forensicData.points[selectedHistoryPoint] || null;
  }, [selectedHistoryPoint, forensicData]);

  // Map trip point indices → forensic point indices (for playback sync)
  const tripToForensicMap = React.useMemo(() => {
    if (!selectedTrip?.points?.length || !forensicData?.points?.length) return null;
    const tsToIdx = {};
    forensicData.points.forEach((p, idx) => { tsToIdx[p.timestamp] = idx; });
    return selectedTrip.points.map(tp => tsToIdx[tp.timestamp] ?? null);
  }, [selectedTrip, forensicData]);

  // Set of forensic indices belonging to the selected trip (for filtering clicks/markers)
  const tripForensicIndexSet = React.useMemo(() => {
    if (!tripToForensicMap) return null;
    return new Set(tripToForensicMap.filter(idx => idx != null));
  }, [tripToForensicMap]);

  // Sorted array of valid forensic indices for the current trip (for prev/next navigation)
  const tripForensicIndices = React.useMemo(() => {
    if (!tripForensicIndexSet) return null;
    return Array.from(tripForensicIndexSet).sort((a, b) => a - b);
  }, [tripForensicIndexSet]);

  // Navigate to prev/next forensic point within the current trip
  const navigateForensicPoint = useCallback((direction) => {
    if (!tripForensicIndices || selectedHistoryPoint === null) return;
    const currentPos = tripForensicIndices.indexOf(selectedHistoryPoint);
    if (currentPos < 0) {
      selectHistoryPointManual(tripForensicIndices[0]);
      return;
    }
    const nextPos = currentPos + direction;
    if (nextPos >= 0 && nextPos < tripForensicIndices.length) {
      selectHistoryPointManual(tripForensicIndices[nextPos]);
    }
  }, [tripForensicIndices, selectedHistoryPoint, selectHistoryPointManual]);

  // Build chart data from forensic points for Graphical Data tab — filtered to current trip
  const graphChartData = React.useMemo(() => {
    if (!forensicData?.points || !forensicData?.graph_fields) return [];
    return forensicData.points
      .map((p, idx) => {
        if (tripForensicIndexSet && !tripForensicIndexSet.has(idx)) return null;
        const row = { idx, ts: new Date(p.timestamp).getTime() };
        for (const gf of forensicData.graph_fields) {
          if (gf.source === 'gps') {
            if (gf.key === 'ignition') row[gf.key] = p.ignition ? 1 : 0;
            else row[gf.key] = p[gf.key] ?? null;
          } else if (gf.source === 'io_mapping' && gf.io_id) {
            const rawVal = p.raw_io?.[gf.io_id];
            const mapped = Object.values(p.mapped_values || {}).find(mv => mv.io_id === gf.io_id);
            if (gf.type === 'binary') {
              row[gf.key] = rawVal != null ? (Number(rawVal) ? 1 : 0) : null;
            } else if (mapped && mapped.type !== 'state') {
              row[gf.key] = Number(mapped.value);
            } else {
              row[gf.key] = rawVal != null ? Number(rawVal) : null;
            }
          }
        }
        return row;
      })
      .filter(Boolean);
  }, [forensicData, tripForensicIndexSet]);

  // Lookup: find graphChartData entry by forensic point idx
  const graphChartDataByIdx = React.useMemo(() => {
    const map = {};
    graphChartData.forEach(row => { map[row.idx] = row; });
    return map;
  }, [graphChartData]);

  // ── Forensic graph zoom + downsampling (perf for 100k+ point ranges) ──
  const [graphViewDomain, setGraphViewDomain] = useState(null);
  const graphBrushDebounceRef = useRef(null);
  useEffect(() => { setGraphViewDomain(null); }, [forensicData]);

  const windowedChartData = React.useMemo(() => {
    if (!graphViewDomain) return graphChartData;
    return graphChartData.filter(r => r.ts >= graphViewDomain[0] && r.ts <= graphViewDomain[1]);
  }, [graphChartData, graphViewDomain]);

  // Bucket min/max downsampling: caps rendered SVG points while preserving spikes per enabled series
  const displayChartData = React.useMemo(() => {
    const rows = windowedChartData;
    const MAX_RAW = 10000;
    if (rows.length <= MAX_RAW) return rows;
    const keys = graphEnabledFields;
    const buckets = 1500;
    const bucketSize = rows.length / buckets;
    const picked = new Set([0, rows.length - 1]);
    for (let b = 0; b < buckets; b++) {
      const s = Math.floor(b * bucketSize);
      const e = Math.min(rows.length, Math.floor((b + 1) * bucketSize));
      if (s >= e) continue;
      picked.add(s);
      for (const k of keys) {
        let minI = -1, maxI = -1, minV = Infinity, maxV = -Infinity;
        for (let i = s; i < e; i++) {
          const v = rows[i][k];
          if (v == null) continue;
          if (v < minV) { minV = v; minI = i; }
          if (v > maxV) { maxV = v; maxI = i; }
        }
        if (minI >= 0) picked.add(minI);
        if (maxI >= 0) picked.add(maxI);
      }
    }
    return [...picked].sort((a, b) => a - b).map(i => rows[i]);
  }, [windowedChartData, graphEnabledFields]);

  const graphIsDownsampled = displayChartData.length < windowedChartData.length;

  const displayChartDataRef = useRef([]);
  useEffect(() => { displayChartDataRef.current = displayChartData; }, [displayChartData]);

  const handleGraphBrushChange = useCallback((range) => {
    if (!range || range.startIndex == null || range.endIndex == null) return;
    if (graphBrushDebounceRef.current) clearTimeout(graphBrushDebounceRef.current);
    const { startIndex, endIndex } = range;
    graphBrushDebounceRef.current = setTimeout(() => {
      const data = displayChartDataRef.current;
      if (!data.length || endIndex <= startIndex) return;
      if (startIndex === 0 && endIndex >= data.length - 1) return;
      const startTs = data[Math.max(0, startIndex)]?.ts;
      const endTs = data[Math.min(data.length - 1, endIndex)]?.ts;
      if (startTs == null || endTs == null || endTs <= startTs) return;
      setGraphViewDomain([startTs, endTs]);
    }, 500);
  }, []);

  // Get effective color for a graph field (custom override or default)
  const getGraphColor = useCallback((gf) => graphCustomColors[gf.key] || gf.color, [graphCustomColors]);

  // Current playback point from the selected trip
  const currentPlaybackPoint = React.useMemo(() => {
    if (!selectedTrip?.points?.length || playbackPosition < 0 || playbackPosition >= selectedTrip.points.length) return null;
    return selectedTrip.points[playbackPosition];
  }, [selectedTrip, playbackPosition]);

  // Get value_colors from IO mapping for a mapped value (used for text coloring)
  // value_colors stores Tailwind color NAMES (red, green, orange) — translate to hex
  // Keys can be: mapped label ("YES"), raw value ("0"/"1"), or old labels
  const COLOR_NAME_TO_HEX = {
    'red': '#EF4444', 'green': '#22C55E', 'blue': '#3B82F6', 'orange': '#F97316',
    'purple': '#A855F7', 'teal': '#14B8A6', 'amber': '#F59E0B', 'rose': '#F43F5E', 'yellow': '#FACC15',
  };
  const getMappedValueColor = useCallback((ioId, displayValue, rawValue) => {
    if (!forensicData?.io_mappings || displayValue == null) return null;
    const mapping = forensicData.io_mappings.find(m => m.io_id === ioId);
    if (!mapping?.value_colors) return null;
    const vc = mapping.value_colors;
    if (typeof vc !== 'object') return null;
    // Try multiple key formats: exact label → raw value → case-insensitive label
    const colorName = vc[String(displayValue)]
      || (rawValue != null ? vc[String(rawValue)] : null)
      || vc[String(displayValue).toUpperCase()]
      || vc[String(displayValue).toLowerCase()]
      || vc['_default']
      || null;
    if (!colorName) return null;
    return COLOR_NAME_TO_HEX[colorName] || colorName;
  // COLOR_NAME_TO_HEX is a static, content-invariant color-name→hex map (defined just
  // above); recreated each render but not a meaningful reactive dependency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forensicData]);

  // Sorted and paginated message data for Message Data tab — filtered to current trip
  const sortedMessagePoints = React.useMemo(() => {
    if (!forensicData?.points) return [];
    let pts = [...forensicData.points];
    if (tripForensicIndexSet) {
      pts = pts.filter(p => tripForensicIndexSet.has(p.idx));
    }
    pts.sort((a, b) => {
      const ta = new Date(a.timestamp).getTime();
      const tb = new Date(b.timestamp).getTime();
      return messageSortAsc ? ta - tb : tb - ta;
    });
    return pts;
  }, [forensicData, messageSortAsc, tripForensicIndexSet]);

  const messagePageCount = Math.ceil(sortedMessagePoints.length / MESSAGE_PAGE_SIZE);
  const pagedMessagePoints = sortedMessagePoints.slice(messagePage * MESSAGE_PAGE_SIZE, (messagePage + 1) * MESSAGE_PAGE_SIZE);

  // Reset page when sort changes or new data loads
  React.useEffect(() => { setMessagePage(0); }, [messageSortAsc, forensicData]);

  // Auto-scroll to selected row when point is selected from OUTSIDE the message table
  // (map click, graph click, prev/next buttons). Does NOT fight manual pagination.
  const messageAutoScrollRef = useRef(null);
  const messageTopScrollRef = useRef(null);
  const messageBodyScrollRef = useRef(null);
  const messageTableContentRef = useRef(null);
  const messageScrollSyncRef = useRef(false);
  const [messageTableWidth, setMessageTableWidth] = useState(1180);

  const syncMessageHorizontalScroll = useCallback((source) => {
    const src = source === 'top' ? messageTopScrollRef.current : messageBodyScrollRef.current;
    const dst = source === 'top' ? messageBodyScrollRef.current : messageTopScrollRef.current;
    if (!src || !dst || messageScrollSyncRef.current) return;
    messageScrollSyncRef.current = true;
    dst.scrollLeft = src.scrollLeft;
    requestAnimationFrame(() => { messageScrollSyncRef.current = false; });
  }, []);

  useEffect(() => {
    if (forensicActiveTab !== 'messages') return undefined;
    const node = messageTableContentRef.current;
    if (!node) return undefined;
    const updateWidth = () => {
      setMessageTableWidth(Math.max(node.scrollWidth, node.offsetWidth, 1));
    };
    updateWidth();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateWidth) : null;
    observer?.observe(node);
    window.addEventListener('resize', updateWidth);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [forensicActiveTab, pagedMessagePoints.length, messageViewMode, forensicData?.io_mappings]);

  React.useEffect(() => {
    if (selectedHistoryPoint === null || forensicActiveTab !== 'messages' || !sortedMessagePoints.length) return;
    // Only auto-scroll on manual point selection (click/nav) — not slider drag or playback
    if (!manualPointSelectRef.current) return;
    manualPointSelectRef.current = false;
    // Only auto-navigate if the point index actually changed (not just a re-render)
    if (messageAutoScrollRef.current === selectedHistoryPoint) return;
    messageAutoScrollRef.current = selectedHistoryPoint;
    const sortedIdx = sortedMessagePoints.findIndex(p => p.idx === selectedHistoryPoint);
    if (sortedIdx < 0) return;
    const targetPage = Math.floor(sortedIdx / MESSAGE_PAGE_SIZE);
    setMessagePage(targetPage);
    // No scrolling: the message table has no internal vertical scroller, so any
    // scroll-to-row would scroll the page down to the Forensic panel (user opted out).
  }, [selectedHistoryPoint, forensicActiveTab, sortedMessagePoints]);

  const selectedHistoryDevice = React.useMemo(() => {
    if (!historyDevice) return null;
    return devices.find(dev => dev.id === historyDevice) || null;
  }, [devices, historyDevice]);

  const historyUserTimezone = React.useMemo(() => resolveTimezone(), []);

  const selectedDeviceResolvedTimezone = React.useMemo(() => {
    if (!selectedHistoryDevice) return null;
    if (selectedHistoryDevice.effective_timezone) return selectedHistoryDevice.effective_timezone;
    if (selectedHistoryDevice.timezone_override_enabled && selectedHistoryDevice.timezone_override === 'auto' && selectedHistoryDevice.timezone_auto) {
      return selectedHistoryDevice.timezone_auto;
    }
    if (selectedHistoryDevice.timezone_override_enabled && selectedHistoryDevice.timezone_override && selectedHistoryDevice.timezone_override !== 'auto') {
      return selectedHistoryDevice.timezone_override;
    }
    return null;
  }, [selectedHistoryDevice]);

  const activeHistoryTimezone = React.useMemo(() => {
    if (historyTimezoneMode === 'device') {
      return selectedDeviceResolvedTimezone || historyUserTimezone;
    }
    return historyUserTimezone;
  }, [historyTimezoneMode, selectedDeviceResolvedTimezone, historyUserTimezone]);

  const historyTimezoneLabel = React.useMemo(() => getTimezoneLabel(activeHistoryTimezone), [activeHistoryTimezone]);

  const historyTimezoneSourceLabel = React.useMemo(() => {
    if (historyTimezoneMode === 'user') return 'User Timezone';
    if (selectedHistoryDevice?.timezone_override_enabled && selectedHistoryDevice?.timezone_override === 'auto' && selectedDeviceResolvedTimezone) {
      return 'Device Timezone (Auto)';
    }
    if (selectedDeviceResolvedTimezone) return 'Device Timezone';
    return 'User Timezone Fallback';
  }, [historyTimezoneMode, selectedHistoryDevice, selectedDeviceResolvedTimezone]);

  const historyTimezoneNotice = React.useMemo(() => {
    if (historyTimezoneMode !== 'device') return '';
    if (!historyDevice) return 'Select a device to use device timezone. User timezone is being used for now.';
    if (!selectedDeviceResolvedTimezone) {
      return selectedHistoryDevice?.timezone_override_enabled && selectedHistoryDevice?.timezone_override === 'auto'
        ? 'Device timezone is set to Auto, but no timezone is currently resolved. Using user timezone.'
        : 'This device has no resolved timezone override. Using user timezone.';
    }
    return '';
  }, [historyTimezoneMode, historyDevice, selectedDeviceResolvedTimezone, selectedHistoryDevice]);

  const historyTimezoneLabelClass = React.useMemo(() => {
    if (historyTimezoneSourceLabel.startsWith('Device Timezone')) {
      return 'text-sky-600 dark:text-sky-400 font-semibold';
    }
    return 'text-slate-500 dark:text-slate-400';
  }, [historyTimezoneSourceLabel]);

  const applyDatePreset = useCallback((presetId) => {
    const range = buildHistoryPresetRange(presetId, activeHistoryTimezone);
    if (range) setHistoryDateRange(range);
  }, [activeHistoryTimezone]);

  useEffect(() => {
    if (historyDatePreset !== 'custom') applyDatePreset(historyDatePreset);
  }, [historyDatePreset, activeHistoryTimezone, applyDatePreset]);

  useEffect(() => {
    const prevTimezone = prevHistoryTimezoneRef.current;
    if (prevTimezone !== activeHistoryTimezone && historyDatePreset === 'custom') {
      setHistoryDateRange((currentRange) => {
        const startParts = getZonedParts(currentRange.start, { timezone: prevTimezone });
        const endParts = getZonedParts(currentRange.end, { timezone: prevTimezone });
        return {
          start: zonedDateTimeToUtc(startParts, activeHistoryTimezone),
          end: zonedDateTimeToUtc(endParts, activeHistoryTimezone),
        };
      });
    }
    prevHistoryTimezoneRef.current = activeHistoryTimezone;
  }, [activeHistoryTimezone, historyDatePreset]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reportsRes, devicesRes, liveDevicesRes] = await Promise.all([
        api.get('/reports').catch(() => ({ data: [] })),
        api.get('/devices').catch(() => ({ data: [] })),
        api.get('/gps/live').catch(() => ({ data: [] })),
      ]);
      setReports(reportsRes.data);
      const devicesById = new globalThis.Map();
      for (const device of (devicesRes.data || [])) {
        devicesById.set(device.id, { ...device });
      }
      for (const liveDevice of (liveDevicesRes.data || [])) {
        const existing = devicesById.get(liveDevice.id) || {};
        devicesById.set(liveDevice.id, {
          ...liveDevice,
          ...existing,
          effective_timezone: liveDevice.effective_timezone || existing.effective_timezone || null,
          timezone_auto: liveDevice.timezone_auto || existing.timezone_auto || null,
          last_server_timestamp: liveDevice.last_server_timestamp || existing.last_server_timestamp || null,
          last_seen: liveDevice.last_seen || existing.last_seen || null,
        });
      }
      const mergedDevices = Array.from(devicesById.values()).map((device) => {
        const liveDevice = liveDevicesRes.data?.find((item) => item.id === device.id);
        return {
          ...device,
          timezone_auto: liveDevice?.timezone_auto || device.timezone_auto || null,
          effective_timezone: liveDevice?.effective_timezone || null,
          last_server_timestamp: liveDevice?.last_server_timestamp || device.last_server_timestamp || null,
          last_seen: liveDevice?.last_seen || device.last_seen || null,
        };
      });
      setDevices(mergedDevices);

      // Load alert rules/stats/conditions (list rows come from fetchAlerts effect)
      const [rulesRes, statsRes, condsRes] = await Promise.all([
        api.get('/alerts/rules').catch(() => ({ data: [] })),
        api.get('/alerts/stats').catch(() => ({ data: { active: 0, critical_active: 0, high_active: 0, total: 0 } })),
        api.get('/alerts/available-conditions').catch(() => ({ data: { low_battery: true, disconnect: true, maintenance: false, maintenance_spend: true } })),
      ]);
      setAlertRules(rulesRes.data);
      setAlertStats(statsRes.data);
      setAvailableConditions(condsRes.data || { low_battery: true, disconnect: true, maintenance: false, maintenance_spend: true, document_expiry: true });
      // Hydrate the Low Battery settings form from its saved config (if any).
      const lowBatt = (rulesRes.data || []).find(r => r.alert_type === 'low_battery');
      if (lowBatt) {
        const c = lowBatt.conditions || {};
        setBatteryConfig({
          is_active: !!lowBatt.is_active,
          id: lowBatt.id,
          severity: lowBatt.severity || 'medium',
          threshold_percent: c.threshold_percent ?? 20,
          hysteresis_percent: c.hysteresis_percent ?? 5,
          notify_bell: c.notify_bell !== false,
          notify_push: c.notify_push !== false,
          notifications: { ...DEFAULT_ALERT_NOTIFICATIONS, ...(lowBatt.notifications || {}) },
          actions: { ...DEFAULT_ALERT_ACTIONS, ...(lowBatt.actions || {}) },
        });
      }
      const disc = (rulesRes.data || []).find(r => r.alert_type === 'disconnect');
      if (disc) {
        const c = disc.conditions || {};
        setDisconnectConfig({
          is_active: !!disc.is_active,
          id: disc.id,
          severity: disc.severity || 'medium',
          offline_minutes: c.offline_minutes ?? 0,
          escalate_after_hours: c.escalate_after_hours ?? 0,
          escalate_severity: c.escalate_severity || 'critical',
          notify_bell: c.notify_bell !== false,
          notify_push: c.notify_push !== false,
          notifications: { ...DEFAULT_ALERT_NOTIFICATIONS, ...(disc.notifications || {}) },
          actions: { ...DEFAULT_ALERT_ACTIONS, ...(disc.actions || {}) },
        });
      }
      const spend = (rulesRes.data || []).find(r => r.alert_type === 'maintenance_spend');
      if (spend) {
        const c = spend.conditions || {};
        setSpendConfig({
          is_active: !!spend.is_active,
          id: spend.id,
          severity: spend.severity || 'high',
          threshold: c.threshold ?? '',
          overrides: Object.entries(c.overrides || {}).map(([device_id, amount]) => ({ device_id, amount })),
          notify_bell: c.notify_bell !== false,
          notify_push: c.notify_push !== false,
          notifications: { ...DEFAULT_ALERT_NOTIFICATIONS, ...(spend.notifications || {}) },
          actions: { ...DEFAULT_ALERT_ACTIONS, ...(spend.actions || {}) },
        });
      }

      const docExp = (rulesRes.data || []).find(r => r.alert_type === 'document_expiry');
      if (docExp) {
        const c = docExp.conditions || {};
        setDocExpiryConfig({
          is_active: !!docExp.is_active,
          id: docExp.id,
          severity: docExp.severity || 'medium',
          warn_days: c.warn_days ?? 30,
          doc_types: (c.doc_types && c.doc_types.length) ? c.doc_types : ['licence', 'id', 'certificate', 'other'],
          include_license_expiry: c.include_license_expiry !== false,
          include_person_documents: c.include_person_documents !== false,
          include_vehicle_documents: c.include_vehicle_documents !== false,
          notify_bell: c.notify_bell !== false,
          notify_push: c.notify_push !== false,
          notifications: { ...DEFAULT_ALERT_NOTIFICATIONS, ...(docExp.notifications || {}) },
          actions: { ...DEFAULT_ALERT_ACTIONS, ...(docExp.actions || {}) },
        });
      }

      const evViewed = (rulesRes.data || []).find(r => r.alert_type === 'evidence_viewed');
      if (evViewed) {
        const c = evViewed.conditions || {};
        setEvidenceViewedConfig({
          is_active: !!evViewed.is_active,
          id: evViewed.id,
          severity: evViewed.severity || 'low',
          alert_on_download: c.alert_on_download !== false,
          notifications: { ...DEFAULT_ALERT_NOTIFICATIONS, ...(evViewed.notifications || {}) },
          actions: { ...DEFAULT_ALERT_ACTIONS, ...(evViewed.actions || {}) },
        });
      }

      // Load event stats only (events loaded by fetchEvents)
      const eventStatsRes = await api.get('/event-occurrences/stats').catch(() => ({ data: null }));
      setEventStats(eventStatsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // Shared filter params for Alerts list / Select All / Export.
  const buildAlertFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (alertFilter === 'open') params.set('exclude_resolved', 'true');
    else if (alertFilter !== 'all') params.set('status', alertFilter);
    if (alertSeverityFilter !== 'all') params.set('severity', alertSeverityFilter);
    if (alertTypeFilter !== 'all') params.set('alert_type', alertTypeFilter);
    if (alertSearch) params.set('search', alertSearch);
    if (alertDateRange.start) params.set('date_from', alertDateRange.start.toISOString());
    if (alertDateRange.end) params.set('date_to', alertDateRange.end.toISOString());
    return params;
  }, [alertFilter, alertSeverityFilter, alertTypeFilter, alertSearch, alertDateRange]);

  // Server-side paginated fetch of the alerts list.
  const fetchAlerts = useCallback(async (page = alertsPage) => {
    setAlertsLoading(true);
    try {
      const params = buildAlertFilterParams();
      params.set('paginated', 'true');
      params.set('skip', String(page * alertsPageSize));
      params.set('limit', String(alertsPageSize));
      const res = await api.get(`/alerts?${params}`);
      setAlerts(res.data?.items || []);
      setAlertsTotal(res.data?.total || 0);
    } catch {
      setAlerts([]);
      setAlertsTotal(0);
    } finally {
      setAlertsLoading(false);
      setAlertsInitialLoadDone(true);
    }
  }, [api, alertsPage, alertsPageSize, buildAlertFilterParams]);

  // Refetch alerts when filters/page/pageSize change.
  useEffect(() => {
    fetchAlerts(alertsPage);
  }, [alertsPage, alertsPageSize, alertFilter, alertSeverityFilter, alertTypeFilter, alertSearch, alertDateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selection is only meaningful against the current filter set.
  useEffect(() => { setSelectedAlertIds([]); }, [alertFilter, alertSeverityFilter, alertTypeFilter, alertSearch, alertDateRange]);

  // Lightweight refresh of just the alert data (no full-page spinner / no flash).
  // Used after toggling a config, saving settings, or acknowledging/resolving.
  const refreshAlerts = useCallback(async () => {
    try {
      const [rulesRes, statsRes] = await Promise.all([
        api.get('/alerts/rules').catch(() => ({ data: [] })),
        api.get('/alerts/stats').catch(() => ({ data: { active: 0, critical_active: 0, high_active: 0, total: 0 } })),
      ]);
      setAlertRules(rulesRes.data);
      setAlertStats(statsRes.data);
      fetchAlerts(alertsPage);
      // Broadcast the new active count so the sidebar nav badge updates instantly.
      try {
        window.dispatchEvent(new CustomEvent('ensue:alerts-changed', { detail: { active: statsRes.data?.active || 0 } }));
      } catch { /* ignore */ }
    } catch (error) {
      console.error('Failed to refresh alerts:', error);
    }
  }, [api, fetchAlerts, alertsPage]);

  // Load the shared notification-template library for the Alert config dropdowns.
  useEffect(() => { fetchNotifTemplates(); }, [fetchNotifTemplates]);

  // Live-update the Alerts sub-tab badge + stat cards + list when a new alert
  // fires over the WebSocket (matches the top-nav badge behaviour in Layout).
  useEffect(() => {
    if (!onDeviceUpdate) return undefined;
    const unsub = onDeviceUpdate((_deviceId, data) => {
      if (data?.type === 'alert') refreshAlerts();
    });
    return () => unsub?.();
  }, [onDeviceUpdate, refreshAlerts]);

  // Fetch events with pagination and filters
  const fetchEvents = useCallback(async (page = eventsPage) => {
    setEventsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('skip', String(page * EVENTS_PAGE_SIZE));
      params.set('limit', String(EVENTS_PAGE_SIZE));
      if (eventFilter !== 'all') params.set('event_type', eventFilter);
      if (eventStatusFilter !== 'all') params.set('status', eventStatusFilter);
      if (eventSeverityFilter !== 'all') params.set('severity', eventSeverityFilter);
      if (eventSearch) params.set('search', eventSearch);
      if (eventDateRange.start) params.set('start_date', eventDateRange.start.toISOString());
      if (eventDateRange.end) params.set('end_date', eventDateRange.end.toISOString());
      const res = await api.get(`/event-occurrences?${params}`);
      setEvents(res.data?.items || []);
      setEventsTotal(res.data?.total || 0);
    } catch {
      setEvents([]);
      setEventsTotal(0);
    } finally {
      setEventsLoading(false);
      setEventsInitialLoadDone(true);
    }
  }, [api, eventsPage, eventFilter, eventStatusFilter, eventSeverityFilter, eventSearch, eventDateRange, EVENTS_PAGE_SIZE]);

  // Fetch events when filters/page/pageSize change
  useEffect(() => {
    fetchEvents(eventsPage);
  }, [eventsPage, eventsPageSize, eventFilter, eventStatusFilter, eventSeverityFilter, eventSearch, eventDateRange]); // eslint-disable-line react-hooks/exhaustive-deps


  // Shared filter params for Event Log Select All / Export (mirrors fetchEvents).
  const buildEventFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (eventFilter !== 'all') params.set('event_type', eventFilter);
    if (eventStatusFilter !== 'all') params.set('status', eventStatusFilter);
    if (eventSeverityFilter !== 'all') params.set('severity', eventSeverityFilter);
    if (eventSearch) params.set('search', eventSearch);
    if (eventDateRange.start) params.set('start_date', eventDateRange.start.toISOString());
    if (eventDateRange.end) params.set('end_date', eventDateRange.end.toISOString());
    return params;
  }, [eventFilter, eventStatusFilter, eventSeverityFilter, eventSearch, eventDateRange]);

  // Selection is only meaningful against the current filter set.
  useEffect(() => { setSelectedEventIds([]); }, [eventFilter, eventStatusFilter, eventSeverityFilter, eventSearch, eventDateRange]);

  const handleExportEvents = useCallback(async () => {
    if (!selectedEventIds.length) return;
    try {
      setEventExporting(true);
      let rows = events.filter(ev => selectedEventIds.includes(ev.id));
      if (rows.length < selectedEventIds.length) {
        // Selection spans other pages — fetch the full filtered set once.
        const params = buildEventFilterParams();
        params.set('skip', '0');
        params.set('limit', String(Math.max(eventsTotal, 1)));
        const res = await api.get(`/event-occurrences?${params}`);
        rows = (res.data?.items || []).filter(ev => selectedEventIds.includes(ev.id));
      }
      const headers = ['Time', 'Timezone', 'Rule', 'Event Type', 'Severity', 'Status', 'Device', 'Speed (km/h)', 'Limit (km/h)', 'Road', 'Message', 'Duration (s)', 'Latitude', 'Longitude'];
      const csvRows = rows.map(ev => {
        const tz = ev.device_timezone || resolveTimezone();
        return [
          ev.triggered_at ? formatDT(ev.triggered_at, { timezone: tz, seconds: true }) : '',
          getTimezoneLabel(tz) || tz,
          ev.event_rule_name || '', ev.event_type || '', ev.severity || '', ev.status || '',
          formatRecordDeviceLabel(ev, devicesById),
          ev.speed_kmh ?? '', ev.threshold_kmh ?? '', ev.road_name || '', ev.message || '',
          ev.duration_secs ?? '', ev.latitude ?? '', ev.longitude ?? '',
        ];
      });
      const csv = [headers, ...csvRows].map(r => r.map(v => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `event_log_${getFileTimestamp()}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported ${csvRows.length} event${csvRows.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to export events');
    } finally {
      setEventExporting(false);
    }
  }, [selectedEventIds, events, eventsTotal, buildEventFilterParams, api, devicesById]);

  // Fetch trips when device, date range, or split mode changes
  const fetchTrips = useCallback(async () => {
    if (!historyDevice) { setTrips([]); setSelectedTrip(null); setTimelineEvents([]); setDetectedStops([]); setDetectedIdles([]); setHistoryEvents([]); return; }
    setHistoryLoading(true);
    setTrips([]);
    setSelectedTrip(null);
    setTimelineEvents([]);
    setDetectedStops([]);
    setDetectedIdles([]);
    setHistoryEvents([]);
    setSelectedTimelineIdx(null);
    setHighlightedSegment(null);
    setHighlightedStopIdx(null);
    setHighlightedMarker(null);
    setSelectedHistoryPoint(null);
    setForensicData(null);
    setSnappedRoutePath(null);
    try {
      const params = new URLSearchParams({
        start_time: historyDateRange.start.toISOString(),
        end_time: historyDateRange.end.toISOString(),
        split_mode: splitMode,
        stop_threshold_secs: String(stopThresholdSecs),
        idle_threshold_secs: String(idleThresholdSecs),
      });
      const res = await api.get(`/gps/history-trips/${historyDevice}?${params}`);
      const data = res.data;
      setTrips(data.trips || []);
      setTimelineEvents(data.timeline_events || []);
      setDetectedStops(data.stops || []);
      setDetectedIdles(data.idles || []);
      setHistoryEvents(data.events || []);
    } catch (err) {
      console.error('Failed to fetch trips:', err);
      toast.error('Failed to load trip history');
      setTrips([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [api, historyDevice, historyDateRange, splitMode, stopThresholdSecs, idleThresholdSecs]);

  // Clear all route/event/trip overlays from the History map (keeps the selected device + date filters)
  const clearHistoryMap = useCallback(() => {
    setTrips([]);
    setSelectedTrip(null);
    setTimelineEvents([]);
    setDetectedStops([]);
    setDetectedIdles([]);
    setHistoryEvents([]);
    setSelectedTimelineIdx(null);
    setHighlightedSegment(null);
    setHighlightedStopIdx(null);
    setHighlightedMarker(null);
    setHighlightedEventIdx(null);
    setSelectedHistoryPoint(null);
    setForensicData(null);
    setSnappedRoutePath(null);
    toast.success('Map cleared');
  }, []);

  const handleOpenInvestigationInHistory = useCallback((result) => {
    const point = result?.closest_point || {};
    const timestamp = point.timestamp || point.server_timestamp;
    const deviceId = result?.device_id;
    const center = timestamp ? new Date(timestamp) : null;
    if (!deviceId || !center || Number.isNaN(center.getTime())) {
      toast.error('This investigation result is missing the device or timestamp needed for History');
      return;
    }

    const start = new Date(center.getTime() - 5 * 60 * 1000);
    const end = new Date(center.getTime() + 5 * 60 * 1000);
    setHistoryDevice(deviceId);
    setHistoryDatePreset('custom');
    setHistoryDateRange({ start, end });
    setSplitMode('none');
    setTrips([]);
    setSelectedTrip(null);
    setTimelineEvents([]);
    setDetectedStops([]);
    setDetectedIdles([]);
    setHistoryEvents([]);
    setSelectedTimelineIdx(null);
    setHighlightedSegment(null);
    setHighlightedStopIdx(null);
    setHighlightedMarker(null);
    setSelectedHistoryPoint(null);
    setForensicData(null);
    setMobileHistoryView('map');
    setActiveTab('history');
    navigate('/analytics?tab=history', { replace: false });
    setPendingHistoryLaunch({
      deviceId,
      startMs: start.getTime(),
      endMs: end.getTime(),
      deviceName: formatRecordDeviceLabel(result, devicesById) || 'vehicle',
    });
    toast.info(`Opening ${formatRecordDeviceLabel(result, devicesById) || 'vehicle'} in History ±5 minutes`);
  }, [navigate, devicesById]);

  useEffect(() => {
    if (!pendingHistoryLaunch) return;
    const sameRange =
      historyDateRange.start?.getTime?.() === pendingHistoryLaunch.startMs &&
      historyDateRange.end?.getTime?.() === pendingHistoryLaunch.endMs;
    if (activeTab !== 'history' || historyDevice !== pendingHistoryLaunch.deviceId || !sameRange) return;
    setPendingHistoryLaunch(null);
    fetchTrips();
  }, [activeTab, fetchTrips, historyDateRange, historyDevice, pendingHistoryLaunch]);

  // Remember the user's last-used History range so Travel History deep links reuse it
  useEffect(() => {
    if (!historyDateRange?.start || !historyDateRange?.end) return;
    try {
      localStorage.setItem('lt_history_last_range', JSON.stringify({
        preset: historyDatePreset,
        startIso: historyDateRange.start.toISOString(),
        endIso: historyDateRange.end.toISOString(),
      }));
    } catch { /* storage unavailable */ }
  }, [historyDatePreset, historyDateRange]);

  // Deep link: /analytics?tab=history&device=<id> (Travel History icons on Fleet/dashboards).
  // Applies the device + Today range via the pendingHistoryLaunch mechanism, then strips the
  // device param so refreshes/back-navigation don't relaunch.
  useEffect(() => {
    if (!hasHistory) return;
    const params = new URLSearchParams(location.search);
    const deviceId = params.get('device');
    if (!deviceId || params.get('tab') !== 'history') return;
    // Reuse the last-used range (Quick Date Jump); presets roll forward, custom stays fixed.
    let preset = 'today';
    let range = null;
    try {
      const saved = JSON.parse(localStorage.getItem('lt_history_last_range') || 'null');
      if (saved?.preset && saved.preset !== 'custom') {
        preset = saved.preset;
        range = buildHistoryPresetRange(saved.preset, resolveTimezone());
      } else if (saved?.preset === 'custom' && saved.startIso && saved.endIso) {
        preset = 'custom';
        range = { start: new Date(saved.startIso), end: new Date(saved.endIso) };
      }
    } catch { /* ignore corrupt storage */ }
    if (!range?.start || !range?.end || isNaN(range.start.getTime()) || isNaN(range.end.getTime())) {
      preset = 'today';
      range = buildHistoryPresetRange('today', resolveTimezone());
    }
    setHistoryDevice(deviceId);
    setHistoryDatePreset(preset);
    setHistoryDateRange(range);
    setSplitMode('none');
    setTrips([]);
    setSelectedTrip(null);
    setTimelineEvents([]);
    setDetectedStops([]);
    setDetectedIdles([]);
    setHistoryEvents([]);
    setSelectedTimelineIdx(null);
    setHighlightedSegment(null);
    setHighlightedStopIdx(null);
    setHighlightedMarker(null);
    setSelectedHistoryPoint(null);
    setForensicData(null);
    setMobileHistoryView('map');
    setActiveTab('history');
    setPendingHistoryLaunch({ deviceId, startMs: range.start.getTime(), endMs: range.end.getTime(), deviceName: 'device' });
    navigate('/analytics?tab=history', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, hasHistory]);

  // Fetch forensic detail data (runs in parallel after trips search, only if forensic panel is enabled)
  const fetchHistoryDetail = useCallback(async () => {
    if (!historyDevice || !hasForensicPanel) return;
    setForensicLoading(true);
    try {
      const params = new URLSearchParams({
        start_time: historyDateRange.start.toISOString(),
        end_time: historyDateRange.end.toISOString(),
      });
      const res = await api.get(`/gps/history-detail/${historyDevice}?${params}`);
      setForensicData(res.data);
    } catch (err) {
      console.error('Failed to fetch forensic detail:', err);
    } finally {
      setForensicLoading(false);
    }
  }, [api, historyDevice, historyDateRange, hasForensicPanel]);

  // Trigger forensic detail fetch whenever trips are loaded
  const prevTripsLenRef = useRef(0);
  useEffect(() => {
    const currentLen = trips.length;
    if (currentLen > 0 && prevTripsLenRef.current === 0 && hasForensicPanel) {
      fetchHistoryDetail();
    }
    prevTripsLenRef.current = currentLen;
  }, [trips, hasForensicPanel, fetchHistoryDetail]);

  // fetchTrips is triggered manually by the Search button (not auto-fetched)

  // Snap-to-road effect: snap route when toggle is enabled and trip is selected
  // Chunks points into batches of MAX_SNAP_POINTS to respect OSRM --max-matching-size limits
  useEffect(() => {
    if (!snapToRoadEnabled || !selectedTrip?.points || selectedTrip.points.length < 2) {
      setSnappedRoutePath(null);
      return;
    }
    let cancelled = false;
    setSnapLoading(true);
    const allPts = selectedTrip.points.map(p => ({ lat: p.lat, lng: p.lng, ts: p.timestamp || p.server_timestamp }));
    const MAX_SNAP_POINTS = 180; // Stay under OSRM default (200) with margin

    const snapChunked = async () => {
      if (allPts.length <= MAX_SNAP_POINTS) {
        return await snapToRoad(allPts, api);
      }
      // Split into overlapping chunks, stitch results
      const chunks = [];
      for (let i = 0; i < allPts.length; i += MAX_SNAP_POINTS - 5) {
        const end = Math.min(i + MAX_SNAP_POINTS, allPts.length);
        chunks.push(allPts.slice(i, end));
        if (end >= allPts.length) break;
      }
      let combined = [];
      for (const chunk of chunks) {
        if (cancelled) return null;
        const snapped = await snapToRoad(chunk, api);
        if (!snapped) continue;
        // Skip first few points of subsequent chunks to avoid duplicates at overlap
        const skip = combined.length > 0 ? 3 : 0;
        combined = combined.concat(snapped.slice(skip));
      }
      return combined.length >= 2 ? combined : null;
    };

    snapChunked().then((snapped) => {
      if (!cancelled) {
        setSnappedRoutePath(snapped);
        setSnapLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setSnappedRoutePath(null);
        setSnapLoading(false);
        toast.error('Snap to Road failed — showing raw route');
      }
    });
    return () => { cancelled = true; };
  }, [snapToRoadEnabled, selectedTrip, api]);

  // === PLAYBACK ANIMATION ENGINE ===
  useEffect(() => {
    if (!isPlaying || !selectedTrip?.points?.length) {
      clearInterval(playbackIntervalRef.current);
      return;
    }
    const totalPoints = selectedTrip.points.length;
    const baseInterval = 200; // ms per point at 1x
    const msPerPoint = baseInterval / playbackSpeed;
    const tickInterval = Math.max(16, msPerPoint);
    const pointsPerTick = msPerPoint < 16 ? Math.round(16 / msPerPoint) : 1;

    playbackIntervalRef.current = setInterval(() => {
      setPlaybackPosition(prev => {
        const next = Math.min(prev + pointsPerTick, totalPoints - 1);
        if (next >= totalPoints - 1) {
          setIsPlaying(false);
          return totalPoints - 1;
        }
        return next;
      });
    }, tickInterval);

    return () => clearInterval(playbackIntervalRef.current);
  }, [isPlaying, playbackSpeed, selectedTrip]);

  // Sync playback position → selectedHistoryPoint + map follow
  useEffect(() => {
    if (playbackPosition == null || !selectedTrip?.points?.length) return;
    const pt = selectedTrip.points[playbackPosition];
    if (!pt) return;

    // Sync forensic panel
    if (tripToForensicMap) {
      const forensicIdx = tripToForensicMap[playbackPosition];
      if (forensicIdx != null && forensicIdx !== selectedHistoryPoint) {
        setSelectedHistoryPoint(forensicIdx);
      }
    }

    // Map follow: pan when vehicle exits viewport (only during active playback)
    if (isPlaying) {
      if (historyMapType === 'osm' && historyLeafletMapRef.current) {
        const map = historyLeafletMapRef.current;
        const bounds = map.getBounds();
        if (!bounds.contains([pt.lat, pt.lng])) {
          map.panTo([pt.lat, pt.lng], { animate: true, duration: 0.3 });
        }
      } else if (historyMapRef.current) {
        const map = historyMapRef.current;
        const bounds = map.getBounds?.();
        if (bounds && !bounds.contains({ lat: pt.lat, lng: pt.lng })) {
          map.panTo({ lat: pt.lat, lng: pt.lng });
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackPosition]);

  // Auto-select first data point when Data Points toggle is turned ON
  useEffect(() => {
    if (showDataPoints && forensicData?.points?.length > 0 && selectedHistoryPoint === null) {
      setSelectedHistoryPoint(0);
    }
  }, [showDataPoints, forensicData, selectedHistoryPoint]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Map layers for History tab
  const getHistoryMapLayers = () => {
    const layers = {
      osm: { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', name: 'OSM' },
    };
    if (hasGoogleMaps) {
      layers.roadmap = { url: 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', name: 'Google' };
      if (hasSatelliteView) {
        layers.satellite = { url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', name: 'Satellite' };
        layers.hybrid = { url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', name: 'Hybrid' };
      }
      if (hasTerrainView) {
        layers.terrain = { url: 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}', name: 'Terrain' };
      }
    }
    return layers;
  };

  const historyMapLayers = getHistoryMapLayers();
  const trafficLayerUrl = 'https://mt1.google.com/vt?lyrs=traffic&styles=feature:all|element:labels|visibility:off&x={x}&y={y}&z={z}';

  // Fullscreen handler for History map
  const handleHistoryToggleFullscreen = () => {
    const mapContainer = document.querySelector('[data-testid="history-map-container"]');
    if (!mapContainer) return;

    const isCurrentlyFullscreen = document.fullscreenElement !== null;

    if (!isCurrentlyFullscreen) {
      if (mapContainer.requestFullscreen) {
        mapContainer.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen && document.fullscreenElement) {
        document.exitFullscreen();
      }
    }
  };

  // Listen for fullscreen changes for History map
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = document.fullscreenElement !== null;
      setHistoryIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // History map zoom handlers
  const handleHistoryZoomIn = () => {
    if (historyMapType === 'osm' && historyLeafletMapRef.current) {
      historyLeafletMapRef.current.zoomIn();
    } else if (historyMapRef.current) {
      historyMapRef.current.setZoom(historyMapRef.current.getZoom() + 1);
    }
  };

  const handleHistoryZoomOut = () => {
    if (historyMapType === 'osm' && historyLeafletMapRef.current) {
      historyLeafletMapRef.current.zoomOut();
    } else if (historyMapRef.current) {
      historyMapRef.current.setZoom(historyMapRef.current.getZoom() - 1);
    }
  };

  const handleHistoryMapLoad = useCallback((map) => {
    historyMapRef.current = map;
    flushPendingMapFitRef.current?.();
  }, []);



  // Keep React state in sync with the user's pan/zoom so typing in the search box
  // (which re-renders) doesn't snap the map back to the default center.
  const handleHistoryGoogleIdle = useCallback(() => {
    const map = historyMapRef.current;
    if (!map) return;
    if (historyGoogleAnimatingRef.current) return;

    const c = map.getCenter();
    const z = map.getZoom();

    if (c) {
      const next = { lat: c.lat(), lng: c.lng() };
      setHistoryMapCenter(prev => (
        prev && Math.abs(prev.lat - next.lat) < 1e-10 && Math.abs(prev.lng - next.lng) < 1e-10 ? prev : next
      ));
    }
    if (typeof z === 'number') {
      setHistoryMapZoom(prev => (prev === z ? prev : z));
    }
  }, [setHistoryMapCenter, setHistoryMapZoom]);

  const handleHistoryAddressSearch = async (e) => {
    e.preventDefault();
    const q = historyAddressSearch.trim();
    if (!q) return;

    try {
      if (historyMapType === 'osm') {
        // Use Nominatim for OSM
        const response = await fetch(`${NOMINATIM_BASE}/search?format=json&q=${encodeURIComponent(q)}&limit=1`);
        const data = await response.json();

        if (data && data.length > 0) {
          const { lat, lon } = data[0];
          const latNum = parseFloat(lat);
          const lngNum = parseFloat(lon);

          if (historyLeafletMapRef.current) {
            historyLeafletMapRef.current.flyTo([latNum, lngNum], 16);
          }
          setHistorySearchMarker({ lat: latNum, lng: lngNum });
          toast.success('Location found');
        } else {
          toast.error('Location not found');
        }
        return;
      }

      // Google Maps geocoding
      if (!window.google?.maps?.Geocoder) {
        toast.error('Google Maps not ready');
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: q }, (results, status) => {
        if (status !== 'OK' || !results?.[0]) {
          toast.error('Location not found');
          return;
        }

        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        const target = { lat, lng };

        setHistorySearchMarker(target);

        // IMPORTANT:
        // If we don't update state, any re-render (e.g., typing) can snap the map back to the old "center" prop.
        setHistoryMapCenter(target);
        setHistoryMapZoom(16);

        const map = historyMapRef.current;
        if (!map) {
          toast.error('Map not ready');
          return;
        }

        historyGoogleAnimatingRef.current = true;
        map.panTo(target);
        map.setZoom(16);

        // Release the "animating" lock after the animation window.
        // (Idle will then keep state synced to the map's real position.)
        const distReleaseMs = 1800;
        window.setTimeout(() => {
          historyGoogleAnimatingRef.current = false;
        }, distReleaseMs);

        toast.success('Location found');
      });
    } catch (error) {
      console.error('Geocoding error:', error);
      toast.error('Search failed');
    }
  };

  // Format seconds to human-readable duration
  const formatDuration = (secs) => {
    if (!secs || secs <= 0) return '0m';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // Precise duration including seconds
  const formatDurationExact = (secs) => {
    if (!secs || secs <= 0) return '0s';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.floor(secs % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  // Fit map to trip bounds when a trip is selected
  const pendingMapFitRef = useRef(null);
  const mobileHistoryViewRef = useRef(mobileHistoryView);
  useEffect(() => { mobileHistoryViewRef.current = mobileHistoryView; }, [mobileHistoryView]);

  const fitMapToTrip = useCallback((trip) => {
    if (!trip?.points?.length) return;
    // Mobile: the map pane is display:none while in List view — fitBounds on a
    // zero-size container zooms out to the whole world. Defer until pane is visible.
    if (typeof window !== 'undefined' && window.innerWidth < 1024 && mobileHistoryViewRef.current === 'list') {
      pendingMapFitRef.current = trip;
      return;
    }
    // Map instance not mounted yet (e.g. deep-link launch right after navigation) —
    // defer the fit until the map's ready callback flushes it.
    if (historyMapType === 'osm' ? !historyLeafletMapRef.current : !historyMapRef.current) {
      pendingMapFitRef.current = trip;
      return;
    }
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const p of trip.points) {
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    }
    const pad = 0.002; // Small padding

    if (historyMapType === 'osm' && historyLeafletMapRef.current) {
      historyLeafletMapRef.current.fitBounds([[minLat - pad, minLng - pad], [maxLat + pad, maxLng + pad]]);
    } else if (historyMapRef.current) {
      const bounds = new window.google.maps.LatLngBounds(
        { lat: minLat - pad, lng: minLng - pad },
        { lat: maxLat + pad, lng: maxLng + pad }
      );
      historyMapRef.current.fitBounds(bounds);
    }
  }, [historyMapType]);

  // Flush a deferred fit once the map instance becomes available (deep-link race fix).
  const flushPendingMapFit = useCallback(() => {
    setTimeout(() => {
      if (!pendingMapFitRef.current) return;
      const trip = pendingMapFitRef.current;
      pendingMapFitRef.current = null;
      if (historyMapType === 'osm' && historyLeafletMapRef.current?.invalidateSize) {
        historyLeafletMapRef.current.invalidateSize();
      }
      fitMapToTrip(trip);
    }, 250);
  }, [historyMapType, fitMapToTrip]);
  const flushPendingMapFitRef = useRef(flushPendingMapFit);
  useEffect(() => { flushPendingMapFitRef.current = flushPendingMapFit; }, [flushPendingMapFit]);
  useEffect(() => {
    if (mobileHistoryView !== 'map') return;
    const t = setTimeout(() => {
      if (historyMapType === 'osm' && historyLeafletMapRef.current?._mapPane) {
        historyLeafletMapRef.current.invalidateSize();
      }
      if (pendingMapFitRef.current) {
        const trip = pendingMapFitRef.current;
        pendingMapFitRef.current = null;
        fitMapToTrip(trip);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [mobileHistoryView, historyMapType, fitMapToTrip]);

  // Auto-select first trip and fit map when trips are loaded
  useEffect(() => {
    if (historyLoading || trips.length === 0) return;
    // Always select the first trip
    setSelectedTrip(trips[0]);
    fitMapToTrip(trips[0]);
    // Mobile: jump straight to the Map view once search results arrive
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setMobileHistoryView('map');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trips, historyLoading]);

  const handleSelectTrip = useCallback((trip) => {
    setSelectedTrip(trip);
    setPlaybackPosition(0);
    setIsPlaying(false);
    setHighlightedSegment(null);
    setSelectedTimelineIdx(null);
    setHighlightedStopIdx(null);
    setHighlightedMarker(null);
    fitMapToTrip(trip);
  }, [fitMapToTrip]);

  // Handle timeline event click (Whole Period mode)
  const handleTimelineClick = useCallback((evt, idx) => {
    setSelectedTimelineIdx(idx);
    if (evt.type === 'start' || evt.type === 'end') {
      const lat = evt.lat;
      const lng = evt.lng;
      if (historyMapType === 'osm' && historyLeafletMapRef.current) {
        historyLeafletMapRef.current.panTo([lat, lng]);
      } else if (historyMapRef.current) {
        historyMapRef.current.panTo({ lat, lng });
      }
      setHighlightedSegment(null);
      setHighlightedStopIdx(null);
      setHighlightedMarker(evt.type);
      setHighlightedEventIdx(null);
      setMapBubble({ type: evt.type, lat, lng, data: { time: evt.timestamp } });
    } else if (evt.type === 'stop') {
      // Pan only (no rezoom) + highlight the P marker
      if (historyMapType === 'osm' && historyLeafletMapRef.current) {
        historyLeafletMapRef.current.panTo([evt.lat, evt.lng]);
      } else if (historyMapRef.current) {
        historyMapRef.current.panTo({ lat: evt.lat, lng: evt.lng });
      }
      const stopIdx = visibleNonMovingSegments.findIndex(s =>
        s.state === evt.state && s.start_time === evt.start_time && s.end_time === evt.end_time
      );
      setHighlightedStopIdx(stopIdx >= 0 ? stopIdx : null);
      setHighlightedSegment(null);
      setHighlightedMarker(null);
      setHighlightedEventIdx(null);
      setMapBubble({ type: 'stop', lat: evt.lat, lng: evt.lng, data: stopIdx >= 0 ? visibleNonMovingSegments[stopIdx] : evt });
    } else if (evt.type === 'drive') {
      // Fit map to the drive segment and highlight the polyline
      const trip = trips[0];
      if (trip?.points && evt.point_start_idx != null && evt.point_end_idx != null) {
        const segPts = trip.points.slice(evt.point_start_idx, evt.point_end_idx + 1);
        if (segPts.length >= 2) {
          fitMapToTrip({ points: segPts });
          setHighlightedSegment({ start: evt.point_start_idx, end: evt.point_end_idx });
        }
      }
      setHighlightedStopIdx(null);
      setHighlightedMarker(null);
      setHighlightedEventIdx(null);
      setMapBubble(null);
    } else if (evt.type === 'event' && evt.event) {
      // Pan to event location + highlight event marker
      if (historyMapType === 'osm' && historyLeafletMapRef.current) {
        historyLeafletMapRef.current.panTo([evt.event.lat, evt.event.lng]);
      } else if (historyMapRef.current) {
        historyMapRef.current.panTo({ lat: evt.event.lat, lng: evt.event.lng });
      }
      // Find the event index in the events array
      const eventsArr = selectedTrip?.events || historyEvents || [];
      const evIdx = eventsArr.findIndex(e => e.id === evt.event.id);
      setHighlightedEventIdx(evIdx >= 0 ? evIdx : null);
      setHighlightedSegment(null);
      setHighlightedStopIdx(null);
      setHighlightedMarker(null);
      setMapBubble({ type: 'event', lat: evt.event.lat, lng: evt.event.lng, data: evt.event });
    }
  }, [historyMapType, trips, fitMapToTrip, visibleNonMovingSegments, historyEvents, selectedTrip?.events]);

  // Handle clicking a P marker on the map → highlight + show bubble + select in list
  // ── Marker info bubble (map popups for both engines) ──
  useEffect(() => { setMapBubble(null); }, [trips, selectedTrip]);

  // Reverse-geocode the bubble location via the backend endpoint ONLY (self-hosted
  // Nominatim — never Google, to keep geocoding costs down). Cached per coordinate.
  const bubbleAddrCacheRef = useRef({});
  const [bubbleAddress, setBubbleAddress] = useState(null); // { key, text|null(loading) }
  useEffect(() => {
    if (!mapBubble) { setBubbleAddress(null); return; }
    const key = `${Number(mapBubble.lat).toFixed(5)},${Number(mapBubble.lng).toFixed(5)}`;
    const cached = bubbleAddrCacheRef.current[key];
    if (cached) { setBubbleAddress({ key, text: cached }); return; }
    let alive = true;
    setBubbleAddress({ key, text: null });
    api.get(`/geocode/reverse?lat=${mapBubble.lat}&lng=${mapBubble.lng}`)
      .then((r) => {
        const text = r?.data?.formatted_address;
        const clean = text && text !== 'Address not found' ? text : '';
        if (clean) bubbleAddrCacheRef.current[key] = clean;
        if (alive) setBubbleAddress({ key, text: clean });
      })
      .catch(() => { if (alive) setBubbleAddress({ key, text: '' }); });
    return () => { alive = false; };
  }, [mapBubble, api]);

  const renderMapBubbleTitle = (b) => {
    if (!b) return null;
    if (b.type === 'stop') {
      const isIdle = b.data.state === 'idle';
      return <p className={`text-sm font-bold leading-none ${isIdle ? 'text-amber-600' : 'text-red-600'}`} data-testid="map-bubble-title">{isIdle ? 'Engine Idle' : 'Stopped'}</p>;
    }
    if (b.type === 'event') {
      const ev = b.data;
      const sevColor = ev.severity === 'critical' ? '#dc2626' : ev.severity === 'warning' ? '#f59e0b' : '#64748b';
      return (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="map-bubble-title">
          <p className="text-sm font-bold leading-none text-[#7F00FF]">{ev.event_rule_name || 'Event'}</p>
          {ev.severity && (
            <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border leading-none" style={{ color: sevColor, borderColor: sevColor }}>{ev.severity}</span>
          )}
        </div>
      );
    }
    if (b.type === 'start' || b.type === 'end') {
      const isStart = b.type === 'start';
      return <p className={`text-sm font-bold leading-none ${isStart ? 'text-green-600' : 'text-red-600'}`} data-testid="map-bubble-title">{isStart ? 'Trip Start' : 'Trip End'}</p>;
    }
    if (b.type === 'point') {
      return <p className="text-sm font-bold leading-none text-blue-600" data-testid="map-bubble-title">Data Point</p>;
    }
    return null;
  };

  const renderMapBubbleBody = (b) => {
    if (!b) return null;
    const fmt = (t) => (t ? formatDT(t, { timezone: activeHistoryTimezone, seconds: true }) : '—');
    const addrKey = `${Number(b.lat).toFixed(5)},${Number(b.lng).toFixed(5)}`;
    const addrRow = bubbleAddress?.key === addrKey ? (
      bubbleAddress.text === null
        ? <p className="text-xs font-medium text-slate-500 italic">Looking up address…</p>
        : bubbleAddress.text
          ? <p className="text-xs font-semibold text-green-800 leading-snug" data-testid="map-bubble-address">{bubbleAddress.text}</p>
          : null
    ) : null;
    const coordsLink = (
      <a href={`https://www.google.com/maps?q=${b.lat},${b.lng}`} target="_blank" rel="noopener noreferrer"
        className="text-xs font-semibold text-blue-600 underline underline-offset-2" data-testid="map-bubble-coords-link">
        {Number(b.lat).toFixed(5)}, {Number(b.lng).toFixed(5)}
      </a>
    );
    const locationBlock = (<>{addrRow}{coordsLink}</>);
    if (b.type === 'stop') {
      const isIdle = b.data.state === 'idle';
      return (
        <div className="space-y-1 min-w-[170px]" data-testid={`map-bubble-${isIdle ? 'idle' : 'stopped'}`}>
          <p className="text-xs font-semibold text-slate-800 leading-snug">{fmt(b.data.start_time)}<br />→ {fmt(b.data.end_time)}</p>
          <p className="text-xs font-semibold text-slate-800"><span className="font-bold">Duration:</span> {formatDurationExact(b.data.duration_secs)}</p>
          {locationBlock}
        </div>
      );
    }
    if (b.type === 'event') {
      const ev = b.data;
      return (
        <div className="space-y-1 min-w-[170px]" data-testid="map-bubble-event">
          {ev.message && <p className="text-xs font-semibold text-slate-800 leading-snug" data-testid="map-bubble-event-message">{ev.message}</p>}
          {ev.speed_kmh != null && (
            <p className="text-xs font-semibold text-slate-800">
              <span className="font-bold">Speed:</span> {formatSpeed(ev.speed_kmh)}
              {ev.threshold_kmh != null ? ` (Limit: ${formatSpeed(ev.threshold_kmh)})` : ''}
              {ev.road_name ? ` on ${ev.road_name}` : ''}
            </p>
          )}
          <p className="text-xs font-semibold text-slate-800">{fmt(ev.triggered_at)}</p>
          {locationBlock}
        </div>
      );
    }
    if (b.type === 'start' || b.type === 'end') {
      return (
        <div className="space-y-1 min-w-[170px]" data-testid={`map-bubble-${b.type}`}>
          <p className="text-xs font-semibold text-slate-800">{fmt(b.data.time)}</p>
          {locationBlock}
        </div>
      );
    }
    if (b.type === 'point') {
      const pt = b.data;
      return (
        <div className="space-y-1 min-w-[170px]" data-testid="map-bubble-point">
          <p className="text-xs font-semibold text-slate-800">{fmt(pt.timestamp)}</p>
          {pt.speed != null && <p className="text-xs font-semibold text-slate-800"><span className="font-bold">Speed:</span> {formatSpeed(pt.speed)}</p>}
          {locationBlock}
        </div>
      );
    }
    return null;
  };

  const handleMapStopClick = useCallback((stopIdx) => {
    setHighlightedStopIdx(stopIdx);
    setHighlightedSegment(null);
    setHighlightedMarker(null);
    setHighlightedEventIdx(null);

    const segment = visibleNonMovingSegments[stopIdx];
    if (!segment) return;
    setMapBubble({ type: 'stop', lat: segment.lat, lng: segment.lng, data: segment });

    if (splitMode === 'none') {
      if (segment.state === 'stopped' && timelineEvents.length > 0) {
        const tlIdx = timelineEvents.findIndex(e =>
          e.type === 'stop' && e.state === segment.state && e.start_time === segment.start_time && e.end_time === segment.end_time
        );
        if (tlIdx >= 0) {
          setSelectedTimelineIdx(tlIdx);
        }
      } else if (segment.state === 'idle' && timelineEvents.length > 0) {
        const driveIdx = timelineEvents.findIndex(e =>
          e.type === 'drive' && Array.isArray(e.children) && e.children.some(c =>
            c.kind === 'idle' && c.start_time === segment.start_time && c.end_time === segment.end_time
          )
        );
        if (driveIdx >= 0) {
          setSelectedTimelineIdx(driveIdx);
          const drive = timelineEvents[driveIdx];
          if (drive.point_start_idx != null && drive.point_end_idx != null) {
            setHighlightedSegment({ start: drive.point_start_idx, end: drive.point_end_idx });
          }
        }
      }
    }
  }, [visibleNonMovingSegments, timelineEvents, splitMode]);

  // Handle clicking an event "E" marker on the map → highlight + select parent trip/drive card
  const handleMapEventClick = useCallback((ev) => {
    setHighlightedStopIdx(null);
    setHighlightedMarker(null);
    if (ev.lat != null && ev.lng != null) setMapBubble({ type: 'event', lat: ev.lat, lng: ev.lng, data: ev });
    if (splitMode === 'ignition') {
      const ownerTrip = trips.find(t => (t.events || []).some(e => e.id === ev.id)) || selectedTrip;
      if (ownerTrip) {
        const evIdx = (ownerTrip.events || []).findIndex(e => e.id === ev.id);
        setSelectedTrip(ownerTrip);
        setHighlightedEventIdx(evIdx >= 0 ? evIdx : null);
      }
    } else {
      const eventsArr = selectedTrip?.events || historyEvents || [];
      const evIdx = eventsArr.findIndex(e => e.id === ev.id);
      setHighlightedEventIdx(evIdx >= 0 ? evIdx : null);
      const driveIdx = timelineEvents.findIndex(e =>
        e.type === 'drive' && Array.isArray(e.children) && e.children.some(c => c.kind === 'event' && c.id === ev.id)
      );
      if (driveIdx >= 0) {
        setSelectedTimelineIdx(driveIdx);
        const drive = timelineEvents[driveIdx];
        if (drive.point_start_idx != null && drive.point_end_idx != null) {
          setHighlightedSegment({ start: drive.point_start_idx, end: drive.point_end_idx });
        }
      } else {
        setHighlightedSegment(null);
        const tlIdx = timelineEvents.findIndex(e => e.type === 'event' && e.event?.id === ev.id);
        if (tlIdx >= 0) { setSelectedTimelineIdx(tlIdx); }
      }
    }
  }, [splitMode, trips, selectedTrip, historyEvents, timelineEvents]);

  // Handle clicking Start/End marker on the map → select in timeline
  const handleMapStartEndClick = useCallback((type) => {
    setHighlightedMarker(type);
    setHighlightedStopIdx(null);
    setHighlightedSegment(null);
    setHighlightedEventIdx(null);
    if (selectedTrip?.points?.length) {
      const pt = type === 'start' ? selectedTrip.points[0] : selectedTrip.points[selectedTrip.points.length - 1];
      const time = type === 'start' ? selectedTrip.start_time : selectedTrip.end_time;
      setMapBubble({ type, lat: pt.lat, lng: pt.lng, data: { time } });
    }
    if (timelineEvents.length > 0) {
      const tlIdx = timelineEvents.findIndex(e => e.type === type);
      if (tlIdx >= 0) {
        setSelectedTimelineIdx(tlIdx);
      }
    }
  }, [timelineEvents, selectedTrip]);
  // Handle clicking the route polyline on the map → find nearest drive segment in timeline
  const handleMapRouteClick = useCallback((latLng) => {
    // Timeline segment highlighting (Whole Period mode only — needs timelineEvents)
    if (timelineEvents.length && trips[0]?.points) {
      const pts = trips[0].points;
      // Find nearest point on route
      let minDist = Infinity;
      let nearestIdx = 0;
      for (let i = 0; i < pts.length; i++) {
        const d = (pts[i].lat - latLng.lat) ** 2 + (pts[i].lng - latLng.lng) ** 2;
        if (d < minDist) { minDist = d; nearestIdx = i; }
      }
      // Find which drive segment contains this point index
      const driveIdx = timelineEvents.findIndex(e =>
        e.type === 'drive' && e.point_start_idx <= nearestIdx && e.point_end_idx >= nearestIdx
      );
      if (driveIdx >= 0) {
        const evt = timelineEvents[driveIdx];
        setSelectedTimelineIdx(driveIdx);
        setHighlightedSegment({ start: evt.point_start_idx, end: evt.point_end_idx });
        setHighlightedStopIdx(null);
        setHighlightedMarker(null);
      }
    }
    // Select nearest forensic data point — restricted to current trip's points
    if (forensicData?.points?.length) {
      let fMinDist = Infinity;
      let fNearestIdx = 0;
      const allowedSet = tripForensicIndexSet;
      for (let i = 0; i < forensicData.points.length; i++) {
        if (allowedSet && !allowedSet.has(i)) continue; // skip points outside current trip
        const fp = forensicData.points[i];
        const d = (fp.lat - latLng.lat) ** 2 + (fp.lng - latLng.lng) ** 2;
        if (d < fMinDist) { fMinDist = d; fNearestIdx = i; }
      }
      selectHistoryPointManual(fNearestIdx);
    }
  }, [timelineEvents, trips, forensicData, selectHistoryPointManual, tripForensicIndexSet]);

  // === EXPORT HELPERS ===
  const downloadFile = useCallback((content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // Reverse geocode coordinates → clean address using Nominatim
  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const resp = await fetch(`${NOMINATIM_BASE}/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
      const data = await resp.json();
      const a = data.address;
      if (a) {
        const parts = [];
        const street = [a.house_number, a.road].filter(Boolean).join(' ');
        if (street) parts.push(street);
        const suburb = a.suburb || a.village || a.town || a.hamlet;
        if (suburb) parts.push(suburb);
        if (a.state) parts.push(a.state);
        if (a.postcode) parts.push(a.postcode);
        if (a.country) parts.push(a.country);
        return parts.join(', ') || data.display_name || '';
      }
      return data.display_name || '';
    } catch { return ''; }
  }, []);

  // CSV-safe value: quote and escape internal quotes, force text for long numbers
  const csvVal = (v) => {
    const s = String(v ?? '');
    // Force text for numbers longer than 15 digits (prevents Excel scientific notation for ICCID etc)
    if (/^\d{16,}$/.test(s)) return `="${s}"`;
    return `"${s.replace(/"/g, '""')}"`;
  };

  // Google Maps link helper
  const gMapsLink = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

  // Shared HTML export wrapper with logo header (logoSrc = base64 dataURL, embedded so exports have no external image dependency)
  const buildExportHTML = (title, subtitle, bodyHTML, exportTimestamp, logoSrc) => `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${title}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:system-ui,-apple-system,sans-serif;background:#fff;color:#1e293b}
.header{height:60px;background:#fff;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;padding:0 16px;gap:16px}
.header img{height:36px}.header-info{font-size:13px;color:#475569}.header-info b{color:#1e293b}
.content{padding:20px}table{width:100%;border-collapse:collapse;font-size:11px;margin-top:12px}
th{background:#f1f5f9;color:#475569;font-weight:600;text-align:left;padding:6px 8px;border:1px solid #e2e8f0;white-space:nowrap}
td{padding:5px 8px;border:1px solid #e2e8f0;vertical-align:top}tr:nth-child(even){background:#f8fafc}
a{color:#2563eb;text-decoration:none}a:hover{text-decoration:underline}
.meta{margin-bottom:12px;font-size:12px;color:#64748b}.meta b{color:#1e293b}
.footer{margin-top:20px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:10px;color:#475569;text-align:center}
@media print{.header{position:fixed;top:0;left:0;right:0;border-bottom:2px solid #2563eb}.content{margin-top:70px}@page{margin:10mm 8mm}}</style></head><body>
<div class="header">${logoSrc ? `<img src="${logoSrc}" alt="EnsueLIVETRAK"/>` : ''}<div class="header-info"><b>${title}</b>${subtitle ? ' &mdash; ' + subtitle : ''}</div></div>
<div class="content">${bodyHTML}<div class="footer">Generated by EnsueLIVETRAK &mdash; ${exportTimestamp}</div></div></body></html>`;

  // Download PDF from backend (used for Trips/Trip Points/Messages)
  const downloadBackendPDF = useCallback(async (endpoint, filename) => {
    try {
      toast.info('Generating PDF...');
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('PDF export failed');
    }
  }, [api]);

  // === UNIFIED EXPORT DISPATCHER ===
  const handleExport = useCallback(async (type, fmt) => {
    const deviceLabel = selectedDeviceName || historyDevice;
    const tripNum = selectedTrip?.trip_index != null ? selectedTrip.trip_index + 1 : 1;
    const ts = format(new Date(), 'yyyyMMdd_HHmm');
    const dateFrom = historyDateRange?.start ? format(historyDateRange.start, 'yyyyMMdd') : '';
    const dateTo = historyDateRange?.end ? format(historyDateRange.end, 'yyyyMMdd') : '';
    const dateRange = dateFrom && dateTo ? `${dateFrom}-${dateTo}` : ts;
    const fmtDT = (t) => formatDT(t, { timezone: activeHistoryTimezone, seconds: true });
    const exportTs = fmtDT(new Date().toISOString());
    // Load brand logo once per export as embedded base64 dataURL — exported HTML has zero external image dependency
    const exportLogoSrc = await getExportLogoDataUrl();
    const posLink = (lat, lng) => `<a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank">${lat}, ${lng}</a>`;
    // CSV-safe unit conversion (plain numbers, no thousands separators) for the user's configured distance/speed unit
    const distCsv = (km) => { const v = convertDistance(km); return v == null ? '' : v.toFixed(1); };
    const spdCsv = (kmh) => { const v = convertSpeed(kmh); return v == null ? '' : v.toFixed(0); };
    const dUnit = distanceUnitLabel();
    const sUnit = speedUnitLabel();

    if (type === 'trips') {
      if (!trips.length) return;
      toast.info('Exporting trips with addresses...');
      // Reverse geocode start/end of each trip
      const addresses = await Promise.all(trips.map(async (t) => {
        const pts = t.points || [];
        const sa = pts.length > 0 ? await reverseGeocode(pts[0].lat, pts[0].lng) : '';
        const ea = pts.length > 1 ? await reverseGeocode(pts[pts.length - 1].lat, pts[pts.length - 1].lng) : '';
        return { sa, ea };
      }));
      const headers = ['Trip', 'Start Time', 'End Time', 'Start Address', 'End Address', 'Start Position', 'End Position', `Distance (${dUnit})`, 'Duration', `Avg Speed (${sUnit})`, `Max Speed (${sUnit})`, 'Points', 'Idles', 'Events'];
      const rowData = trips.map((t, i) => { const pts = t.points || []; return [i+1, fmtDT(t.start_time), fmtDT(t.end_time), addresses[i].sa, addresses[i].ea, pts.length>0?`${pts[0].lat}, ${pts[0].lng}`:'', pts.length>1?`${pts[pts.length-1].lat}, ${pts[pts.length-1].lng}`:'', distCsv(t.distance_km), formatDurationExact(t.duration_secs), spdCsv(t.avg_speed), spdCsv(t.max_speed), pts.length, t.idles?.length||0, t.event_count||0]; });
      if (fmt === 'csv') { downloadFile([headers.map(csvVal).join(','), ...rowData.map(r=>r.map(csvVal).join(','))].join('\n'), `trips_${splitMode}_${deviceLabel}_${dateRange}.csv`, 'text/csv'); toast.success('Trips exported'); }
      else { const tbl=`<table><thead><tr>${headers.map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${rowData.map(r=>`<tr>${r.map((v,ci)=>`<td>${(ci===5||ci===6)&&v?posLink(...String(v).split(', ').map(Number)):v}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
        const html=buildExportHTML(`Trips — ${deviceLabel}`,`${splitMode==='ignition'?'Ignition ON':'Whole Period'} • ${trips.length} trips`,`<div class="meta"><b>Device:</b> ${deviceLabel} • <b>Period:</b> ${fmtDT(historyDateRange.start.toISOString())} to ${fmtDT(historyDateRange.end.toISOString())}</div>${tbl}`,exportTs,exportLogoSrc);
        if(fmt==='pdf'){const params=new URLSearchParams({device_id:historyDevice,start_time:historyDateRange.start.toISOString(),end_time:historyDateRange.end.toISOString(),split_mode:splitMode,stop_threshold_secs:String(stopThresholdSecs),idle_threshold_secs:String(idleThresholdSecs),timezone:activeHistoryTimezone});downloadBackendPDF(`/gps/export/trips?${params}`,`trips_${splitMode}_${deviceLabel}_${dateRange}.pdf`);}else{downloadFile(html,`trips_${splitMode}_${deviceLabel}_${dateRange}.html`,'text/html');toast.success('Trips exported as HTML');}}
      return;
    }
    if (type === 'trip-detail') {
      const isWhole = splitMode === 'none';
      if (isWhole ? !timelineEvents.length : !selectedTrip?.children?.length && !selectedTrip) { toast.error('No timeline data'); return; }
      const evDurTxt = (ev) => ev.duration_secs != null && !(ev.metadata?.instant_event || (ev.duration_secs === 0 && ev.status === 'cleared')) ? formatDurationExact(ev.duration_secs) : '';
      const evDesc = (ev) => `${ev.event_rule_name || 'Event'}${ev.speed_kmh != null ? ` — ${formatSpeed(ev.speed_kmh)}` : ''}`;
      const posTxt = (lat, lng) => lat != null && lng != null ? `${Math.round(lat * 1e6) / 1e6}, ${Math.round(lng * 1e6) / 1e6}` : '';
      const rows = [];
      const childRows = (children) => (children || []).forEach(c => {
        if (c.kind === 'idle') rows.push([`↳ ${c.state === 'idle' ? 'Idle' : 'Stopped'}`, `Idled for ${formatDurationExact(c.duration_secs)}`, fmtDT(c.start_time), fmtDT(c.end_time), formatDurationExact(c.duration_secs), posTxt(c.lat, c.lng)]);
        else rows.push(['↳ Event', evDesc(c), fmtDT(c.triggered_at), c.cleared_at ? fmtDT(c.cleared_at) : '', evDurTxt(c), posTxt(c.lat, c.lng)]);
      });
      if (isWhole) {
        timelineEvents.forEach(te => {
          if (te.type === 'start') rows.push(['Start', 'Journey start', fmtDT(te.timestamp), '', '', posTxt(te.lat, te.lng)]);
          else if (te.type === 'drive') { rows.push(['Drive', `${formatDistance(te.distance_km)} | Avg ${formatSpeed(te.avg_speed)} | Max ${formatSpeed(te.max_speed)}`, fmtDT(te.start_time), fmtDT(te.end_time), formatDurationExact(te.duration_secs), posTxt(te.start_lat, te.start_lng)]); childRows(te.children); }
          else if (te.type === 'stop') rows.push(['Stopped', `Parked for ${formatDurationExact(te.duration_secs)}`, fmtDT(te.start_time), fmtDT(te.end_time), formatDurationExact(te.duration_secs), posTxt(te.lat, te.lng)]);
          else if (te.type === 'event' && te.event) rows.push(['Event', evDesc(te.event), fmtDT(te.event.triggered_at), te.event.cleared_at ? fmtDT(te.event.cleared_at) : '', evDurTxt(te.event), posTxt(te.event.lat, te.event.lng)]);
          else if (te.type === 'end') rows.push(['End', 'Journey end', fmtDT(te.timestamp), '', '', posTxt(te.lat, te.lng)]);
        });
      } else {
        const t = selectedTrip;
        rows.push(['Start', `Trip ${tripNum} start — Ignition ON`, fmtDT(t.start_time), '', '', posTxt(t.start_lat, t.start_lng)]);
        childRows(t.children);
        rows.push(['End', `Trip ${tripNum} end — ${formatDistance(t.distance_km)} | Avg ${formatSpeed(t.avg_speed)} | Max ${formatSpeed(t.max_speed)}`, fmtDT(t.end_time), '', '', posTxt(t.end_lat, t.end_lng)]);
      }
      const numbered = rows.map((r, i) => [i + 1, ...r]);
      const headers = ['#', 'Type', 'Description', 'Start Time', 'End Time', 'Duration', 'Position'];
      const title = isWhole ? `Trip Detail — ${deviceLabel}` : `Trip ${tripNum} Detail — ${deviceLabel}`;
      const subtitle = isWhole ? 'Whole Period timeline' : 'Ignition ON trip timeline';
      const baseFn = isWhole ? `trip_detail_whole_${deviceLabel}_${dateRange}` : `trip_detail_trip${tripNum}_${deviceLabel}_${dateRange}`;
      if (fmt === 'csv') { downloadFile([headers.map(csvVal).join(','), ...numbered.map(r => r.map(csvVal).join(','))].join('\n'), `${baseFn}.csv`, 'text/csv'); toast.success('Trip detail exported'); return; }
      if (fmt === 'pdf') { const params = new URLSearchParams({ device_id: historyDevice, start_time: historyDateRange.start.toISOString(), end_time: historyDateRange.end.toISOString(), trip_index: String(selectedTrip?.trip_index ?? 0), split_mode: splitMode, stop_threshold_secs: String(stopThresholdSecs), idle_threshold_secs: String(idleThresholdSecs), timezone: activeHistoryTimezone }); downloadBackendPDF(`/gps/export/trip-detail?${params}`, `${baseFn}.pdf`); return; }
      const tbl = `<table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>${numbered.map(r => `<tr>${r.map((v, ci) => `<td>${ci === 6 && v ? posLink(...String(v).split(', ').map(Number)) : v}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
      const html = buildExportHTML(title, subtitle, `<div class="meta"><b>Device:</b> ${deviceLabel} • <b>Period:</b> ${fmtDT(historyDateRange.start.toISOString())} to ${fmtDT(historyDateRange.end.toISOString())} • <b>${rows.length}</b> timeline entries</div>${tbl}`, exportTs, exportLogoSrc);
      downloadFile(html, `${baseFn}.html`, 'text/html'); toast.success('Trip detail exported as HTML');
      return;
    }
    if (type === 'trip-points') {
      if (!selectedTrip?.points?.length) return; const pts=selectedTrip.points;
      const idleTime = formatDurationExact(selectedTrip.idle_secs || 0);
      const stoppedTime = formatDurationExact(selectedTrip.non_moving_secs || 0);
      const hasFixData = pts.some(p => p.gps_fix);
      const hasIgnMapping = (selectedHistoryDevice?.io_mappings || []).some(m => (m.io_type || '').toLowerCase() === 'ignition');
      const satsVal = (p) => p.satellites ?? (p.gps_fix ? 'Not reported' : '');
      const fixCols = (p) => {
        const f = p.gps_fix;
        if (!f) return ['', '', '', ''];
        const st = !f.fix_valid ? 'Invalid' : `Valid${f.fix_3d ? ' 3D' : ''}${f.old_fix ? ' (old)' : ''}`;
        return [st, f.position_accuracy_m ?? '', f.pdop ?? '', f.gps_timestamp ? fmtDT(f.gps_timestamp) : ''];
      };
      toast.info('Exporting with addresses...');
      const sa=await reverseGeocode(pts[0].lat,pts[0].lng); const ea=pts.length>1?await reverseGeocode(pts[pts.length-1].lat,pts[pts.length-1].lng):'';
      if (fmt==='csv') {
        const h=['Point','Timestamp','Position',`Speed (${sUnit})`,'Heading','Altitude (m)','Satellites',...(hasFixData?['GPS Fix','Fix Accuracy (m)','PDOP','Fix Time']:[]),...(hasIgnMapping?['Ignition']:[])]; const info=[[csvVal('Device'),csvVal(deviceLabel)].join(',')+'\n'+[csvVal('Trip Number'),csvVal(tripNum)].join(',')+'\n'+[csvVal('Start Address'),csvVal(sa)].join(',')+'\n'+[csvVal('End Address'),csvVal(ea)].join(',')+'\n'+[csvVal('Distance'),csvVal(formatDistance(selectedTrip.distance_km))].join(',')+'\n'+[csvVal('Duration'),csvVal(formatDurationExact(selectedTrip.duration_secs))].join(',')+'\n'+[csvVal('Total Time Idled'),csvVal(idleTime)].join(',')+'\n'+[csvVal('Total Time Stopped'),csvVal(stoppedTime)].join(',')+'\n'+[csvVal('Avg Speed'),csvVal(formatSpeed(selectedTrip.avg_speed))].join(',')+'\n'+[csvVal('Max Speed'),csvVal(formatSpeed(selectedTrip.max_speed))].join(',')];
        const rows=pts.map((p,i)=>[csvVal(i+1),csvVal(fmtDT(p.timestamp)),csvVal(`${p.lat}, ${p.lng}`),csvVal(spdCsv(p.speed)),csvVal(p.heading),csvVal(p.altitude),csvVal(satsVal(p)),...(hasFixData?fixCols(p).map(csvVal):[]),...(hasIgnMapping?[csvVal(p.ignition?'ON':'OFF')]:[])].join(','));
        downloadFile([...info,'',h.map(csvVal).join(','),...rows].join('\n'),`trip${tripNum}_${deviceLabel}_${dateRange}.csv`,'text/csv'); toast.success('Trip points exported');
      } else { const h=['Point','Timestamp','Position','Speed','Heading','Alt','Sats',...(hasFixData?['GPS Fix','Fix Time']:[]),...(hasIgnMapping?['Ignition']:[])];
        const fixCell=(p)=>{const f=p.gps_fix;if(!f)return '<td></td><td></td>';const st=!f.fix_valid?'<span style="color:#dc2626">Invalid</span>':`Valid${f.fix_3d?' · 3D':''}${f.old_fix?' <span style="color:#d97706">(old)</span>':''}`;const extra=[f.position_accuracy_m!=null?`±${f.position_accuracy_m}m`:'',f.pdop!=null?`PDOP ${f.pdop}`:''].filter(Boolean).join(' · ');return `<td style="white-space:nowrap">${st}${extra?` <span style="opacity:.7">${extra}</span>`:''}</td><td style="white-space:nowrap">${f.gps_timestamp?fmtDT(f.gps_timestamp):''}</td>`;};
        const tbl=`<table><thead><tr>${h.map(x=>`<th>${x}</th>`).join('')}</tr></thead><tbody>${pts.map((p,i)=>`<tr><td>${i+1}</td><td>${fmtDT(p.timestamp)}</td><td>${posLink(p.lat,p.lng)}</td><td>${formatSpeed(p.speed)}</td><td>${p.heading}</td><td>${p.altitude} m</td><td>${satsVal(p)}</td>${hasFixData?fixCell(p):''}${hasIgnMapping?`<td>${p.ignition?'ON':'OFF'}</td>`:''}</tr>`).join('')}</tbody></table>`;
        const meta=`<div class="meta"><b>Device:</b> ${deviceLabel} • <b>Trip ${tripNum}</b> • ${formatDistance(selectedTrip.distance_km)} • ${formatDurationExact(selectedTrip.duration_secs)}<br><b>Start:</b> ${sa || 'N/A'}${ea ? ` • <b>End:</b> ${ea}` : ''}<br><b>Total Time Idled:</b> ${idleTime} • <b>Total Time Stopped:</b> ${stoppedTime} • <b>Avg:</b> ${formatSpeed(selectedTrip.avg_speed)} • <b>Max:</b> ${formatSpeed(selectedTrip.max_speed)}</div>`;
        const html=buildExportHTML(`Trip ${tripNum} — ${deviceLabel}`,`${pts.length} points`,meta+tbl,exportTs,exportLogoSrc);
        if(fmt==='pdf'){const params=new URLSearchParams({device_id:historyDevice,start_time:historyDateRange.start.toISOString(),end_time:historyDateRange.end.toISOString(),trip_index:String(selectedTrip.trip_index||0),split_mode:splitMode,stop_threshold_secs:String(stopThresholdSecs),idle_threshold_secs:String(idleThresholdSecs),timezone:activeHistoryTimezone});downloadBackendPDF(`/gps/export/trip-points?${params}`,`trip${tripNum}_${deviceLabel}_${dateRange}.pdf`);}else{downloadFile(html,`trip${tripNum}_${deviceLabel}_${dateRange}.html`,'text/html');toast.success('Trip points exported as HTML');}}
      return;
    }
    if (type === 'messages') {
      if (!sortedMessagePoints.length) return; const mappings=(forensicData?.io_mappings||[]).filter(m=>m.show_telemetry!==false); const ioH=mappings.map(m=>m.display_name||m.io_type);
      const getIO=(pt)=>mappings.map(m=>{const mv=Object.values(pt.mapped_values||{}).find(v=>v.io_id===m.io_id);return mv?`${mv.value}${mv.units?' '+mv.units:''}`:(pt.raw_io?.[m.io_id]??'');});
      const msgHasFix=sortedMessagePoints.some(pt=>pt.gps_fix);
      const msgFixCols=(pt)=>{const f=pt.gps_fix;if(!f)return ['','','',''];const st=!f.fix_valid?'Invalid':`Valid${f.fix_3d?' 3D':''}${f.old_fix?' (old)':''}`;return [st,f.position_accuracy_m??'',f.pdop??'',f.gps_timestamp?fmtDT(f.gps_timestamp):''];};
      if(fmt==='csv'){const h=['Point','Position Time','Server Time','Position','Speed','Heading',...(msgHasFix?['GPS Fix','Fix Accuracy (m)','PDOP','Fix Time']:[]),...ioH]; const info=[[csvVal('Device'),csvVal(deviceLabel)].join(','),...(tripNum?[[csvVal('Trip Number'),csvVal(tripNum)].join(',')]:[]),''];
        const rows=sortedMessagePoints.map(pt=>[csvVal(pt.idx+1),csvVal(fmtDT(pt.timestamp)),csvVal(fmtDT(pt.server_timestamp)),csvVal(`${pt.lat}, ${pt.lng}`),csvVal(pt.speed),csvVal(pt.heading),...(msgHasFix?msgFixCols(pt).map(csvVal):[]),...getIO(pt).map(csvVal)].join(','));
        const fn=tripNum?`messages_trip${tripNum}_${deviceLabel}`:`messages_${deviceLabel}`; downloadFile([...info,h.map(csvVal).join(','),...rows].join('\n'),`${fn}_${dateRange}.csv`,'text/csv'); toast.success('Messages exported');
      } else { const h=['Pt','Position Time','Server Time','Position','Spd','Hdg',...(msgHasFix?['GPS Fix','Fix Time']:[]),...ioH];
        const msgFixCell=(pt)=>{const f=pt.gps_fix;if(!f)return '<td></td><td></td>';const st=!f.fix_valid?'<span style="color:#dc2626">Invalid</span>':`Valid${f.fix_3d?' · 3D':''}${f.old_fix?' <span style="color:#d97706">(old)</span>':''}`;const extra=[f.position_accuracy_m!=null?`±${f.position_accuracy_m}m`:'',f.pdop!=null?`PDOP ${f.pdop}`:''].filter(Boolean).join(' · ');return `<td style="white-space:nowrap">${st}${extra?` <span style="opacity:.7">${extra}</span>`:''}</td><td style="white-space:nowrap">${f.gps_timestamp?fmtDT(f.gps_timestamp):''}</td>`;};
        const tbl=`<table><thead><tr>${h.map(x=>`<th>${x}</th>`).join('')}</tr></thead><tbody>${sortedMessagePoints.map(pt=>`<tr><td>${pt.idx+1}</td><td style="white-space:nowrap">${fmtDT(pt.timestamp)}</td><td style="white-space:nowrap">${fmtDT(pt.server_timestamp)}</td><td>${posLink(pt.lat,pt.lng)}</td><td>${pt.speed}</td><td>${pt.heading}</td>${msgHasFix?msgFixCell(pt):''}${getIO(pt).map(v=>`<td>${v}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
        const meta=`<div class="meta"><b>Device:</b> ${deviceLabel}${tripNum?` • <b>Trip ${tripNum}</b>`:''} • ${sortedMessagePoints.length} messages</div>`;
        const html=buildExportHTML(`Messages — ${deviceLabel}`,`${sortedMessagePoints.length} rows`,meta+tbl,exportTs,exportLogoSrc);
        if(fmt==='pdf'){const params=new URLSearchParams({device_id:historyDevice,start_time:historyDateRange.start.toISOString(),end_time:historyDateRange.end.toISOString(),trip_index:String(selectedTrip?.trip_index??-1),split_mode:splitMode,stop_threshold_secs:String(stopThresholdSecs),idle_threshold_secs:String(idleThresholdSecs),timezone:activeHistoryTimezone});const fn=tripNum?`messages_trip${tripNum}_${deviceLabel}`:`messages_${deviceLabel}`;downloadBackendPDF(`/gps/export/messages?${params}`,`${fn}_${dateRange}.pdf`);}else{const fn=tripNum?`messages_trip${tripNum}_${deviceLabel}`:`messages_${deviceLabel}`;downloadFile(html,`${fn}_${dateRange}.html`,'text/html');toast.success('Messages exported as HTML');}}
      return;
    }
    if (type === 'map') {
      if (!selectedTrip?.points?.length){toast.error('No trip data');return;} const pts=selectedTrip.points;
      const vendor = await getExportVendorAssets({ leafletCss: true, leafletJs: true });
      let bMinLat=Infinity,bMaxLat=-Infinity,bMinLng=Infinity,bMaxLng=-Infinity;for(const p of pts){if(p.lat<bMinLat)bMinLat=p.lat;if(p.lat>bMaxLat)bMaxLat=p.lat;if(p.lng<bMinLng)bMinLng=p.lng;if(p.lng>bMaxLng)bMaxLng=p.lng;}const bounds=[[bMinLat,bMinLng],[bMaxLat,bMaxLng]];
      const cJSON=JSON.stringify(pts.map(p=>[p.lat,p.lng]));const sT=fmtDT(pts[0].timestamp);const eT=fmtDT(pts[pts.length-1].timestamp);
      // Build stops/idles data for the export
      const stopsData = visibleNonMovingSegments.map(s => ({
        lat: s.lat, lng: s.lng, state: s.state,
        start: fmtDT(s.start_time), end: fmtDT(s.end_time),
        duration: formatDurationExact(s.duration_secs),
      }));
      const stopsJSON = JSON.stringify(stopsData);
      const eventsData = (selectedTrip?.events || historyEvents || []).filter(ev => ev.lat && ev.lng).map(ev => ({
        lat: ev.lat, lng: ev.lng, name: ev.event_rule_name, severity: ev.severity,
        speed: ev.speed_kmh != null ? Math.round(convertSpeed(ev.speed_kmh)) : null, limit: ev.threshold_kmh != null ? Math.round(convertSpeed(ev.threshold_kmh)) : null, time: fmtDT(ev.triggered_at),
      }));
      const eventsJSON = JSON.stringify(eventsData);
      // Defensive escape: any literal </script> inside vendor JS would prematurely close the <script> tag when the browser parses the export
      const safeLeafletJs = (vendor.leafletJs || '').replace(/<\/script>/gi, '<\\/script>');
      const mapHTML=`<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>Map - Trip ${tripNum} - ${deviceLabel}</title><style>${vendor.leafletCss || ''}</style><script>${safeLeafletJs}<\/script><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:system-ui,-apple-system,sans-serif}#map{width:100%;height:calc(100vh - 90px)}.header{height:60px;background:#fff;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;padding:0 16px;gap:16px}.header img{height:36px}.header-info{display:flex;gap:24px;font-size:13px;color:#475569}.header-info b{color:#1e293b}.footer{height:30px;background:#fff;border-top:1px solid #e2e8f0;display:flex;align-items:center;justify-content:center;font-size:10px;color:#475569}.controls{position:absolute;top:80px;right:12px;z-index:1000;background:#fff;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,0.15);padding:8px 12px;font-size:12px}.controls label{display:flex;align-items:center;gap:6px;cursor:pointer;margin:4px 0;user-select:none}.controls input{width:16px;height:16px;cursor:pointer}@media print{#map{height:700px;page-break-inside:avoid}.controls{display:none}}</style></head><body><div class="header">${exportLogoSrc ? `<img src="${exportLogoSrc}" alt="EnsueLIVETRAK"/>` : ''}<div class="header-info"><span><b>Trip ${tripNum}</b> — ${deviceLabel}</span><span>${sT} → ${eT}</span><span>${formatDistance(selectedTrip.distance_km)} • ${formatDurationExact(selectedTrip.duration_secs)} • Avg ${formatSpeed(selectedTrip.avg_speed)}</span></div></div><div id="map"></div><div class="controls"><label><input type="checkbox" id="toggleRoute" checked onchange="toggleLayer('route')"><span style="color:#22c55e;font-weight:600">Route</span></label><label><input type="checkbox" id="togglePoints" checked onchange="toggleLayer('points')"><span style="color:#f59e0b;font-weight:600">Data Points</span></label><label><input type="checkbox" id="toggleStops" checked onchange="toggleLayer('stops')"><span style="color:#2563eb;font-weight:600">Stopped</span></label><label><input type="checkbox" id="toggleIdles" checked onchange="toggleLayer('idles')"><span style="color:#fbbf24;font-weight:600">Idle</span></label><label><input type="checkbox" id="toggleEvents" checked onchange="toggleLayer('events')"><span style="color:#7F00FF;font-weight:600">Events</span></label></div><div class="footer">Generated by EnsueLIVETRAK — ${exportTs}</div><script>
const coords=${cJSON};const stops=${stopsJSON};const events=${eventsJSON};const map=L.map('map',{preferCanvas:true,zoomAnimation:false,fadeAnimation:false,markerZoomAnimation:false});
const base=L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',{attribution:'Tiles © Esri — Esri, HERE, OpenStreetMap contributors',maxZoom:19}).addTo(map);
base.on('load',()=>{window.__exportReady=true;});setTimeout(()=>{window.__exportReady=true;},8000);
map.fitBounds(${JSON.stringify(bounds)},{padding:[40,40]});
const layers={route:L.layerGroup(),points:L.layerGroup(),stops:L.layerGroup(),idles:L.layerGroup(),events:L.layerGroup()};
L.polyline(coords,{color:'#22c55e',weight:4,opacity:0.9}).addTo(layers.route);
const startIcon=L.divIcon({className:'',html:'<div style="width:28px;height:28px"><svg width="28" height="28" viewBox="0 0 512 512"><path fill="#16a34a" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.218c0,16.291,6.982,30.254,20.945,39.563L257.224,512l188.509-125.673c11.637-9.31,20.945-23.272,20.945-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M356.491,195.132l-157.37-108.53c-1.666-1.145-3.826-1.264-5.605-0.336c-1.785,0.939-2.903,2.783-2.903,4.802v217.061c0,2.018,1.118,3.868,2.903,4.809c0.792,0.412,1.66,0.618,2.523,0.618c1.08,0,2.155-0.326,3.083-0.961l157.37-108.53c1.471-1.015,2.344-2.681,2.344-4.466S357.956,196.147,356.491,195.132z"/></svg></div>',iconSize:[28,28],iconAnchor:[14,28]});
const endIcon=L.divIcon({className:'',html:'<div style="width:28px;height:28px"><svg width="28" height="28" viewBox="0 0 512 512"><path fill="#dc2626" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.218c0,16.291,6.982,30.254,20.945,39.563L257.224,512l188.509-125.673c11.637-9.31,20.945-23.272,20.945-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M334.743,122.647H179.705c-2.518,0-4.561,2.038-4.561,4.56v155.039c0,2.521,2.043,4.561,4.561,4.561h155.038c2.519,0,4.561-2.039,4.561-4.561V127.207C339.304,124.685,337.262,122.647,334.743,122.647z"/></svg></div>',iconSize:[28,28],iconAnchor:[14,28]});
L.marker([${pts[0].lat},${pts[0].lng}],{icon:startIcon}).addTo(map).bindPopup('<b>Start</b><br>${sT}');
L.marker([${pts[pts.length-1].lat},${pts[pts.length-1].lng}],{icon:endIcon}).addTo(map).bindPopup('<b>End</b><br>${eT}');
coords.forEach((c,i)=>{L.circleMarker(c,{radius:4,fillColor:'#f59e0b',fillOpacity:0.9,color:'#fff',weight:1}).addTo(layers.points).bindPopup('<b>Point '+(i+1)+'</b><br>'+c[0].toFixed(6)+', '+c[1].toFixed(6)+'<br><a href="https://www.google.com/maps?q='+c[0]+','+c[1]+'" target="_blank">Open in Google Maps</a>');});
const stopSvg='<svg width="24" height="24" viewBox="0 0 512 512"><path fill="#2563eb" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.509-125.673c11.637-9.31,20.944-23.272,20.944-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M189.555,314.22V116.897h63.935c24.228,0,40.02,0.989,47.378,2.961c11.307,2.961,20.772,9.401,28.4,19.315c7.626,9.916,11.441,22.726,11.441,38.428c0,12.114-2.2,22.299-6.596,30.554c-4.397,8.257-9.983,14.739-16.757,19.45c-6.776,4.711-13.662,7.83-20.661,9.354c-9.513,1.884-23.286,2.827-41.322,2.827h-25.978v74.433H189.555z"/></svg>';
const idleSvg='<svg width="24" height="24" viewBox="0 0 512 512"><path fill="#fbbf24" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.509-125.673c11.637-9.31,20.944-23.272,20.944-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M236,116.897h40v197.323h-40z"/></svg>';
stops.forEach(s=>{const svg=s.state==='idle'?idleSvg:stopSvg;const icon=L.divIcon({className:'',html:'<div style="width:24px;height:24px">'+svg+'</div>',iconSize:[24,24],iconAnchor:[12,24]});const layer=s.state==='idle'?layers.idles:layers.stops;L.marker([s.lat,s.lng],{icon}).addTo(layer).bindPopup('<b>'+(s.state==='idle'?'Engine Idle':'Stopped')+'</b><br>'+s.start+' — '+s.end+'<br>Duration: '+s.duration);});
const eventSvg='<svg width="24" height="24" viewBox="0 0 512 512"><path fill="#7F00FF" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.51-125.673c11.637-9.31,20.943-23.272,20.943-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M189.555,314.22V116.897h146.309v33.381H229.396v43.744h99.064v33.246h-99.064v53.705h110.236v33.246H189.555z"/></svg>';
events.forEach(e=>{const icon=L.divIcon({className:'',html:'<div style="width:24px;height:24px">'+eventSvg+'</div>',iconSize:[24,24],iconAnchor:[12,24]});L.marker([e.lat,e.lng],{icon}).addTo(layers.events).bindPopup('<b style="color:#7F00FF">'+e.name+'</b><br>'+(e.speed?e.speed+' ${sUnit}'+(e.limit?' (Limit: '+e.limit+' ${sUnit})':'')+'<br>':'')+'<span style="color:'+(e.severity==='critical'?'#dc2626':'#f59e0b')+'">'+e.severity+'</span><br>'+e.time);});
Object.values(layers).forEach(l=>l.addTo(map));
function toggleLayer(name){const cb=document.getElementById('toggle'+name.charAt(0).toUpperCase()+name.slice(1));if(cb.checked)map.addLayer(layers[name]);else map.removeLayer(layers[name]);}
<\/script></body></html>`;
      if(fmt==='pdf'){await exportImagePdf({html:mapHTML,width:1400,height:900,captureElement:'#map',title:`Trip ${tripNum} Map`,subtitle:deviceLabel,metaLines:[`${sT} to ${eT}`,`${formatDistance(selectedTrip.distance_km)} • ${formatDurationExact(selectedTrip.duration_secs)} • Avg ${formatSpeed(selectedTrip.avg_speed)}`],filename:`map_trip${tripNum}_${deviceLabel}_${dateRange}.pdf`,generatedAt:exportTs,extraWaitMs:1500});}else{downloadFile(mapHTML,`map_trip${tripNum}_${deviceLabel}_${dateRange}.html`,'text/html');toast.success('Map exported as HTML');}
      return;
    }
    if (type === 'graph') {
      if (!graphChartData.length) return; const fields=graphEnabledFields.map(k=>forensicData?.graph_fields?.find(f=>f.key===k)).filter(Boolean);
      const vendor = await getExportVendorAssets({ chartJs: true });
      const safeChartJs = (vendor.chartJs || '').replace(/<\/script>/gi, '<\\/script>');
      const dJSON=JSON.stringify(graphChartData.map(d=>{const row={time:fmtDT(new Date(d.ts).toISOString())};fields.forEach(f=>{row[f.label]=d[f.key];});return row;}));
      const sJSON=JSON.stringify(fields.map(f=>({key:f.label,color:getGraphColor(f),type:f.type})));
      const legend=fields.map(f=>`<span><span class="dot" style="background:${getGraphColor(f)}"></span>${f.label}${f.type==='binary'?' (ON/OFF)':f.units?' ('+f.units+')':''}</span>`).join('');
      const graphHTML=`<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>Graph - ${deviceLabel}</title><script>${safeChartJs}<\/script><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:system-ui,-apple-system,sans-serif;background:#f8fafc}.header{height:60px;background:#fff;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;padding:0 16px;gap:16px}.header img{height:36px}.header-info{font-size:13px;color:#475569}.header-info b{color:#1e293b}.content{padding:20px}canvas{max-height:500px}.legend{display:flex;gap:16px;flex-wrap:wrap;margin:12px 0}.legend span{display:flex;align-items:center;gap:4px;font-size:13px}.dot{width:10px;height:10px;border-radius:2px}.footer{margin-top:20px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:10px;color:#475569;text-align:center}@media print{canvas{max-height:400px;page-break-inside:avoid}}</style></head><body><div class="header">${exportLogoSrc ? `<img src="${exportLogoSrc}" alt="EnsueLIVETRAK"/>` : ''}<div class="header-info"><b>${deviceLabel}</b> — ${graphChartData.length} data points</div></div><div class="content"><div class="legend">${legend}</div><canvas id="chart"></canvas><div class="footer">Generated by EnsueLIVETRAK — ${exportTs}</div></div><script>const data=${dJSON};const series=${sJSON};const ctx=document.getElementById('chart').getContext('2d');const binaryScales={};const hasBinary=series.some(s=>s.type==='binary');const hasNumeric=series.some(s=>s.type!=='binary');if(hasBinary){binaryScales.binary={type:'linear',position:'right',min:-0.1,max:1.4,ticks:{stepSize:1,callback:v=>v===1?'ON':v===0?'OFF':''},grid:{drawOnChartArea:false}}}new Chart(ctx,{type:'line',data:{labels:data.map(d=>d.time),datasets:series.map(s=>({label:s.key,data:data.map(d=>d[s.key]),borderColor:s.color,backgroundColor:s.color+'20',tension:s.type==='binary'?0:0.3,stepped:s.type==='binary'?'after':false,pointRadius:0,borderWidth:1.5,yAxisID:s.type==='binary'?'binary':'y'}))},options:{responsive:true,animation:false,interaction:{intersect:false,mode:'index'},scales:{x:{ticks:{maxTicksLimit:20,font:{size:9}}},y:{display:hasNumeric,position:'left'},...binaryScales}}});requestAnimationFrame(()=>{window.__exportReady=true;});<\/script></body></html>`;
      if(fmt==='pdf'){const pdfHTML=graphHTML.replace('</head>','<style>canvas{max-height:none!important}</style></head>');await exportImagePdf({html:pdfHTML,width:1500,height:800,captureCanvas:'#chart',title:'History Graph',subtitle:deviceLabel,metaLines:[`${graphChartData.length} data points`],legendItems:fields.map(f=>({label:f.label+(f.type==='binary'?' (ON/OFF)':f.units?' ('+f.units+')':''),color:getGraphColor(f)})),filename:`graph_${deviceLabel}_${dateRange}.pdf`,generatedAt:exportTs,extraWaitMs:600});}else{downloadFile(graphHTML,`graph_${deviceLabel}_${dateRange}.html`,'text/html');toast.success('Graph exported as HTML');}
      return;
    }
  }, [selectedTrip, trips, sortedMessagePoints, graphChartData, graphEnabledFields, forensicData, selectedDeviceName, historyDevice, activeHistoryTimezone, splitMode, historyDateRange, stopThresholdSecs, idleThresholdSecs, downloadFile, downloadBackendPDF, reverseGeocode, getGraphColor, historyEvents, visibleNonMovingSegments, timelineEvents, selectedHistoryDevice?.io_mappings]);

  // === TRIP → ROUTE GEOFENCE EXPORT ===
  const [routeExportDialog, setRouteExportDialog] = useState(null);
  const [routeExportForm, setRouteExportForm] = useState({ name: '', color: '#F59E0B', deviation: 100, group: 'none' });
  const [routeExportGroups, setRouteExportGroups] = useState([]);

  const openRouteExportDialog = useCallback((mode) => {
    const pts = selectedTrip?.points || [];
    if (pts.length < 2) { toast.error('No trip data — run a search first'); return; }
    const deviceLabel = selectedDeviceName || historyDevice;
    const tripNum = selectedTrip?.trip_index != null ? selectedTrip.trip_index + 1 : 1;
    const dateFrom = historyDateRange?.start ? format(historyDateRange.start, 'yyyyMMdd') : '';
    const dateTo = historyDateRange?.end ? format(historyDateRange.end, 'yyyyMMdd') : '';
    const dateRange = dateFrom && dateTo ? `${dateFrom}-${dateTo}` : format(new Date(), 'yyyyMMdd_HHmm');
    const name = splitMode === 'ignition' ? `${deviceLabel} — Trip ${tripNum} — ${dateRange}` : `${deviceLabel} — ${dateRange}`;
    setRouteExportForm({ name, color: '#F59E0B', deviation: 100, group: 'none' });
    setRouteExportDialog({ mode });
    api.get('/geofence-groups?group_type=route')
      .then(res => setRouteExportGroups(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRouteExportGroups([]));
  }, [selectedTrip, selectedDeviceName, historyDevice, historyDateRange, splitMode, api]);

  const confirmRouteExport = useCallback(async () => {
    if (!routeExportDialog) return;
    const pts = selectedTrip?.points || [];
    if (pts.length < 2) { toast.error('No trip data'); setRouteExportDialog(null); return; }
    const name = (routeExportForm.name || '').trim() || 'Exported Route';
    const deviation = Math.max(1, Number(routeExportForm.deviation) || 100);
    const color = routeExportForm.color || '#F59E0B';
    const path = pts.map(p => [p.lat, p.lng]);
    const selectedGroup = routeExportForm.group !== 'none' ? routeExportGroups.find(g => g.id === routeExportForm.group) : null;
    if (routeExportDialog.mode === 'direct') {
      try {
        await api.post('/geo-routes', {
          route_name: name,
          route_group: selectedGroup ? selectedGroup.id : null,
          route_color: color,
          show_name_on_map: true,
          visible_on_map: true,
          path_coordinates: path,
          deviation_distance: deviation,
        });
        toast.success(`Route "${name}" saved — view it in Geofencing → Routes`);
      } catch (err) {
        console.error('Failed to save route:', err);
        toast.error('Failed to save route geofence');
      }
    } else {
      const feature = {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: path.map(([la, ln]) => [ln, la]) },
        properties: {
          route_name: name,
          route_group: selectedGroup ? selectedGroup.name : '',
          route_color: color,
          deviation_distance: deviation,
          show_name_on_map: true,
          visible_on_map: true,
        },
      };
      const safe = name.replace(/[^\w-]+/g, '_');
      downloadFile(JSON.stringify({ type: 'FeatureCollection', features: [feature] }, null, 2), `route_${safe}.geojson`, 'application/geo+json');
      toast.success('Route GeoJSON exported — import it in Geofencing → Routes');
    }
    setRouteExportDialog(null);
  }, [routeExportDialog, routeExportForm, routeExportGroups, selectedTrip, api, downloadFile]);

  // === INVESTIGATIONS HANDLERS ===
  const handleInvGeocode = useCallback(async () => {
    if (!invAddress.trim()) return;
    setInvGeocoding(true);
    try {
      const resp = await fetch(`${NOMINATIM_BASE}/search?format=json&q=${encodeURIComponent(invAddress)}&limit=1`);
      const data = await resp.json();
      if (data.length > 0) {
        setInvLat(parseFloat(data[0].lat));
        setInvLng(parseFloat(data[0].lon));
        toast.success(`Location found: ${data[0].display_name?.substring(0, 60)}...`);
      } else {
        toast.error('Address not found. Try a more specific address.');
      }
    } catch (err) {
      toast.error('Geocoding failed');
    } finally {
      setInvGeocoding(false);
    }
  }, [invAddress]);

  const handleInvSearch = useCallback(async () => {
    if (invLat == null || invLng == null || !invDateRange?.start || !invDateRange?.end) {
      toast.error('Please set location and time range');
      return;
    }
    setInvLoading(true);
    setInvResults(null);
    try {
      const res = await api.post('/gps/proximity-search', {
        lat: invLat, lng: invLng,
        radius_m: invRadius,
        start_time: invDateRange.start.toISOString(),
        end_time: invDateRange.end.toISOString(),
      });
      setInvResults(res.data);
      if (res.data.results?.length === 0) {
        toast.info('No vehicles found within the specified radius and time range');
      } else {
        toast.success(`Found ${res.data.results.length} vehicle(s) nearby`);
      }
    } catch (err) {
      toast.error('Proximity search failed');
      console.error(err);
    } finally {
      setInvLoading(false);
    }
  }, [api, invLat, invLng, invRadius, invDateRange]);

  // Clear the entire Investigations page back to defaults (address, location, results, radius, date range)
  const clearInvestigation = useCallback(() => {
    setInvAddress('');
    setInvLat(null);
    setInvLng(null);
    setInvResults(null);
    setInvRadius(500);
    setInvDatePreset('today');
    setInvDateRange(buildHistoryPresetRange('today', invTimezone));
    toast.success('Investigation cleared');
  }, [invTimezone]);

  // Report handlers
  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      await api.post('/reports/generate', {
        report_type: selectedReportType,
        start_date: reportDateRange.start.toISOString(),
        end_date: reportDateRange.end.toISOString(),
        format: reportFormat,
      });
      toast.success('Report generated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  // Alert handlers
  const handleAcknowledge = async (alertId) => {
    setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, status: 'acknowledged' } : a));
    try {
      await api.put(`/alerts/${alertId}/acknowledge`);
      toast.success('Alert acknowledged');
      refreshAlerts();
    } catch (error) {
      toast.success('Alert acknowledged');
    }
  };

  const handleResolve = async (alertId) => {
    setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, status: 'resolved' } : a));
    try {
      await api.put(`/alerts/${alertId}/resolve`);
      toast.success('Alert resolved');
      refreshAlerts();
    } catch (error) {
      toast.success('Alert resolved');
    }
  };

  const buildConfigPayload = (type, cfg) => {
    const notifications = { ...DEFAULT_ALERT_NOTIFICATIONS, ...(cfg.notifications || {}) };
    const actions = { ...DEFAULT_ALERT_ACTIONS, ...(cfg.actions || {}) };
    if (type === 'low_battery') {
      return {
        name: 'Low Beacon Battery',
        is_active: cfg.is_active,
        severity: cfg.severity,
        conditions: {
          threshold_percent: Number(cfg.threshold_percent) || 20,
          hysteresis_percent: Number(cfg.hysteresis_percent) || 5,
          notify_bell: cfg.notify_bell,
          notify_push: cfg.notify_push,
        },
        notifications,
        actions,
      };
    }
    if (type === 'maintenance_spend') {
      const overrides = {};
      (cfg.overrides || []).forEach((o) => { if (o.device_id && Number(o.amount) > 0) overrides[o.device_id] = Number(o.amount); });
      return {
        name: 'High Maintenance Spend',
        is_active: cfg.is_active,
        severity: cfg.severity,
        conditions: {
          threshold: Number(cfg.threshold) || 0,
          overrides,
          notify_bell: cfg.notify_bell,
          notify_push: cfg.notify_push,
        },
        notifications,
        actions,
      };
    }
    if (type === 'document_expiry') {
      return {
        name: 'Document Expiry',
        is_active: cfg.is_active,
        severity: cfg.severity,
        conditions: {
          warn_days: Math.max(0, Number(cfg.warn_days) || 0),
          doc_types: cfg.doc_types || [],
          include_license_expiry: cfg.include_license_expiry !== false,
          include_person_documents: cfg.include_person_documents !== false,
          include_vehicle_documents: cfg.include_vehicle_documents !== false,
          notify_bell: cfg.notify_bell,
          notify_push: cfg.notify_push,
        },
        notifications,
        actions,
      };
    }
    if (type === 'evidence_viewed') {
      return {
        name: 'Shared Evidence Viewed',
        is_active: cfg.is_active,
        severity: cfg.severity,
        conditions: {
          alert_on_download: cfg.alert_on_download !== false,
        },
        notifications,
        actions,
      };
    }
    return {
      name: 'Device Disconnected',
      is_active: cfg.is_active,
      severity: cfg.severity,
      conditions: {
        offline_minutes: Number(cfg.offline_minutes) || 0,
        escalate_after_hours: Number(cfg.escalate_after_hours) || 0,
        escalate_severity: cfg.escalate_severity || 'critical',
        notify_bell: cfg.notify_bell,
        notify_push: cfg.notify_push,
      },
      notifications,
      actions,
    };
  };

  // Instant enable/disable straight from the settings list (no need to open Configure).
  const toggleAlertActive = async (type, next) => {
    const base = type === 'low_battery' ? batteryConfig : type === 'maintenance_spend' ? spendConfig : type === 'document_expiry' ? docExpiryConfig : type === 'evidence_viewed' ? evidenceViewedConfig : disconnectConfig;
    const setter = type === 'low_battery' ? setBatteryConfig : type === 'maintenance_spend' ? setSpendConfig : type === 'document_expiry' ? setDocExpiryConfig : type === 'evidence_viewed' ? setEvidenceViewedConfig : setDisconnectConfig;
    const cfg = { ...base, is_active: next };
    setter(cfg);
    try {
      await api.put(`/alerts/config/${type}`, buildConfigPayload(type, cfg));
      toast.success(next ? 'Alert enabled' : 'Alert disabled');
      refreshAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update alert');
      setter(prev => ({ ...prev, is_active: !next }));
    }
  };

  // Save from a per-type Configure modal.
  const saveAlertConfig = async (type) => {
    setSavingAlertSettings(true);
    const cfg = type === 'low_battery' ? batteryConfig : type === 'maintenance_spend' ? spendConfig : type === 'document_expiry' ? docExpiryConfig : type === 'evidence_viewed' ? evidenceViewedConfig : disconnectConfig;
    try {
      await api.put(`/alerts/config/${type}`, buildConfigPayload(type, cfg));
      toast.success('Alert settings saved');
      setConfigureType(null);
      refreshAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save alert settings');
    } finally {
      setSavingAlertSettings(false);
    }
  };

  // On-demand evaluation of the saved Document Expiry config.
  const runDocExpiryCheck = async () => {
    try {
      const res = await api.post('/alerts/document-expiry/run-check');
      toast.success(`${res.data?.message || 'Check complete'} — ${res.data?.raised ?? 0} alert(s) raised`);
      refreshAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Document check failed');
    }
  };

  // On-demand evaluation of the saved High Maintenance Spend config.
  const runSpendCheck = async () => {
    try {
      const res = await api.post('/alerts/maintenance-spend/run-check');
      toast.success(`${res.data?.message || 'Check complete'} — ${res.data?.raised ?? 0} alert(s) raised`);
      refreshAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Spend check failed');
    }
  };

  // Events are now fetched server-side with filters — no client-side filtering needed

  // Export selected alerts (or all filtered) to CSV — mirrors Event Log export.
  const handleExportAlerts = useCallback(async () => {
    if (!selectedAlertIds.length) return;
    try {
      setAlertExporting(true);
      let rows = alerts.filter(a => selectedAlertIds.includes(a.id));
      if (rows.length < selectedAlertIds.length) {
        const params = buildAlertFilterParams();
        params.set('paginated', 'true');
        params.set('skip', '0');
        params.set('limit', String(Math.max(alertsTotal, 1)));
        const res = await api.get(`/alerts?${params}`);
        rows = (res.data?.items || []).filter(a => selectedAlertIds.includes(a.id));
      }
      const tz = resolveTimezone();
      const headers = ['Time', 'Timezone', 'Title', 'Type', 'Severity', 'Status', 'Device', 'Message', 'Acknowledged At', 'Acknowledged By', 'Resolved At', 'Resolved By'];
      const csvRows = rows.map(a => [
        a.created_at ? formatDT(a.created_at, { timezone: tz, seconds: true }) : '',
        getTimezoneLabel(tz) || tz,
        a.title || '', a.alert_type || '', a.severity || '', a.status || '',
        formatRecordDeviceLabel(a, devicesById),
        a.message || '',
        a.acknowledged_at ? formatDT(a.acknowledged_at, { timezone: tz, seconds: true }) : '',
        a.acknowledged_by_name || '',
        a.resolved_at ? formatDT(a.resolved_at, { timezone: tz, seconds: true }) : '',
        a.resolved_at ? (a.auto_resolved ? 'Auto' : (a.resolved_by_name || '')) : '',
      ]);
      const csv = [headers, ...csvRows].map(r => r.map(v => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const el = document.createElement('a');
      el.href = URL.createObjectURL(blob);
      el.download = `alerts_${getFileTimestamp()}.csv`;
      document.body.appendChild(el);
      el.click();
      el.remove();
      URL.revokeObjectURL(el.href);
      toast.success(`Exported ${csvRows.length} alert${csvRows.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to export alerts');
    } finally {
      setAlertExporting(false);
    }
  }, [api, alerts, selectedAlertIds, alertsTotal, buildAlertFilterParams, devicesById]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Page Header */}
      <div className="hidden lg:flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#2563eb]/10">
          <BarChart3 className="w-6 h-6 text-[#2563eb]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
          <p className="text-sm text-muted-foreground">Trip history, events, alerts, reports, and investigations</p>
        </div>
      </div>

      {/* Sub-navigation Tabs - Same style as Media page, filtered by user features */}
      <MobileSectionMenu
        items={[
          hasHistory && { id: 'history', label: 'History', icon: History },
          hasEvents && { id: 'events', label: 'Events', icon: Zap },
          hasAlerts && { id: 'alerts', label: 'Alerts', icon: Bell, badge: alertStats?.active > 0 ? <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0">{alertStats.active}</Badge> : null },
          hasReports && { id: 'reports', label: 'Reports', icon: FileText },
          hasInvestigations && { id: 'investigations', label: 'Investigations', icon: Search },
        ].filter(Boolean)}
        activeId={activeTab}
        onChange={handleAnalyticsTabChange}
        testIdPrefix="analytics-tabs"
      />
      <div className="hidden sm:flex gap-2 flex-wrap">
        {hasHistory && (
          <Button
            onClick={() => handleAnalyticsTabChange('history')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'history' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-history"
          >
            <History className="w-4 h-4" />
            History
          </Button>
        )}
        {hasEvents && (
          <Button
            onClick={() => handleAnalyticsTabChange('events')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'events' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-events"
          >
            <Zap className="w-4 h-4" />
            Events
          </Button>
        )}
        {hasAlerts && (
          <Button
            onClick={() => handleAnalyticsTabChange('alerts')}
            className={`gap-2 relative min-w-[170px] justify-center ${activeTab === 'alerts' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-alerts"
          >
            <Bell className="w-4 h-4" />
            Alerts
            {alertStats?.active > 0 && (
              <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0">{alertStats.active}</Badge>
            )}
          </Button>
        )}
        {hasReports && (
          <Button
            onClick={() => handleAnalyticsTabChange('reports')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'reports' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-reports"
          >
            <FileText className="w-4 h-4" />
            Reports
          </Button>
        )}
        {hasInvestigations && (
          <Button
            onClick={() => handleAnalyticsTabChange('investigations')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'investigations' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-investigations"
          >
            <Search className="w-4 h-4" />
            Investigations
          </Button>
        )}
      </div>

      {activeTab === 'history' && hasHistory && (
        <div className="grid grid-cols-2 gap-2 lg:hidden" data-testid="analytics-history-mobile-view-toggle">
          <Button
            type="button"
            variant={mobileHistoryView === 'list' ? 'default' : 'outline'}
            className={`h-10 gap-2 ${mobileHistoryView === 'list' ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white' : 'bg-white dark:bg-slate-800'}`}
            onClick={() => setMobileHistoryView('list')}
            aria-pressed={mobileHistoryView === 'list'}
            data-testid="analytics-history-mobile-list-toggle"
          >
            <History className="w-4 h-4" /> List
          </Button>
          <Button
            type="button"
            variant={mobileHistoryView === 'map' ? 'default' : 'outline'}
            className={`h-10 gap-2 ${mobileHistoryView === 'map' ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white' : 'bg-white dark:bg-slate-800'}`}
            onClick={() => setMobileHistoryView('map')}
            aria-pressed={mobileHistoryView === 'map'}
            data-testid="analytics-history-mobile-map-toggle"
          >
            <Map className="w-4 h-4" /> Map
          </Button>
        </div>
      )}

      {activeTab === 'investigations' && hasInvestigations && (
        <div className="grid grid-cols-2 gap-2 lg:hidden" data-testid="analytics-investigations-mobile-view-toggle">
          <Button
            type="button"
            variant={mobileInvestigationView === 'search' ? 'default' : 'outline'}
            className={`h-10 gap-2 ${mobileInvestigationView === 'search' ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white' : 'bg-white dark:bg-slate-800'}`}
            onClick={() => setMobileInvestigationView('search')}
            aria-pressed={mobileInvestigationView === 'search'}
            data-testid="analytics-investigations-mobile-search-toggle"
          >
            <Search className="w-4 h-4" /> Search
          </Button>
          <Button
            type="button"
            variant={mobileInvestigationView === 'results' ? 'default' : 'outline'}
            className={`h-10 gap-2 ${mobileInvestigationView === 'results' ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white' : 'bg-white dark:bg-slate-800'}`}
            onClick={() => setMobileInvestigationView('results')}
            aria-pressed={mobileInvestigationView === 'results'}
            data-testid="analytics-investigations-mobile-results-toggle"
          >
            <Car className="w-4 h-4" /> Results
          </Button>
        </div>
      )}

      {/* No Access Message - SECURITY FIX */}
      {!activeTab && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Shield className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
            <p className="text-lg font-medium text-muted-foreground">No Access</p>
            <p className="text-sm text-muted-foreground mt-2">You don&apos;t have permission to access any sections of Analytics.</p>
            <p className="text-xs text-muted-foreground mt-4">Contact your administrator to request access.</p>
          </CardContent>
        </Card>
      )}

      {/* ==================== REPORTS TAB ==================== */}
      {activeTab === 'reports' && hasReports && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">Reports</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">Generate, customize, and export analytical reports.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Sidebar - Report Types */}
            <div className="lg:col-span-1 space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Report Templates</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {reportCategories.map((category) => (
                    <div key={category.category}>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">{category.category}</p>
                      <div className="space-y-1">
                        {category.reports.map((report) => {
                          const Icon = report.icon;
                          return (
                            <button
                              key={report.value}
                              onClick={() => setSelectedReportType(report.value)}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                selectedReportType === report.value
                                  ? 'bg-[#2563eb] text-white'
                                  : 'hover:bg-muted text-foreground'
                              }`}
                            >
                              <Icon className="w-4 h-4" />
                              {report.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Right Content - Configuration */}
            <div className="lg:col-span-3 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Report Configuration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start dark:bg-[#172031]">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {format(reportDateRange.start, 'PPP')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={reportDateRange.start}
                            onSelect={(date) => date && setReportDateRange(prev => ({ ...prev, start: date }))}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start dark:bg-[#172031]">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {format(reportDateRange.end, 'PPP')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={reportDateRange.end}
                            onSelect={(date) => date && setReportDateRange(prev => ({ ...prev, end: date }))}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Export Format</Label>
                      <Select value={reportFormat} onValueChange={setReportFormat}>
                        <SelectTrigger className="dark:bg-[#172031]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pdf">PDF Document</SelectItem>
                          <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                          <SelectItem value="csv">CSV File</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Devices</Label>
                      <Select>
                        <SelectTrigger className="dark:bg-[#172031]">
                          <SelectValue placeholder="All Devices" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Devices</SelectItem>
                          {devices.map(d => (
                            <SelectItem key={d.id} value={d.id}>{deviceDisplayName(d)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button 
                    className="w-full bg-[#2563eb] hover:bg-[#1d4ed8]"
                    onClick={handleGenerateReport}
                    disabled={generating}
                  >
                    {generating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2" />
                    )}
                    Generate Report
                  </Button>
                </CardContent>
              </Card>

              {/* Generated Reports */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Generated Reports</CardTitle>
                </CardHeader>
                <CardContent>
                  {reports.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No reports generated yet</p>
                      <p className="text-sm">Select a report type and generate your first report</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reports.map((report) => (
                        <div key={report.id} className="flex items-center justify-between p-4 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#2563eb]/10 flex items-center justify-center">
                              <FileText className="w-5 h-5 text-[#2563eb]" />
                            </div>
                            <div>
                              <h4 className="font-medium capitalize">{report.report_type?.replace('_', ' ')} Report</h4>
                              <p className="text-sm text-muted-foreground">
                                Generated {format(new Date(report.created_at), 'PPp')}
                              </p>
                            </div>
                          </div>
                          <Button variant="outline" size="sm">
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* ==================== EVENTS TAB ==================== */}
      {activeTab === 'events' && hasEvents && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">Events</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">Chronological view of all detected event occurrences and their configuration rules.</p>
            </div>
          </div>

          {/* Event Log / Event Rules toggle — moved to its own row, left-aligned,
              equal-width buttons (matches other section-nav rows in the app). */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={eventsView === 'occurrences' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setEventsView('occurrences')}
              className={`gap-2 min-w-[160px] justify-center ${eventsView === 'occurrences' ? 'bg-[#2563eb] hover:bg-[#1d4ed8]' : ''}`}
              data-testid="events-view-occurrences"
            >
              <Zap className="w-4 h-4 mr-1" />
              Event Log
            </Button>
            <Button
              variant={eventsView === 'rules' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setEventsView('rules')}
              className={`gap-2 min-w-[160px] justify-center ${eventsView === 'rules' ? 'bg-[#2563eb] hover:bg-[#1d4ed8]' : ''}`}
              data-testid="events-view-rules"
            >
              <Settings className="w-4 h-4 mr-1" />
              Event Rules
            </Button>
          </div>

          {eventsView === 'rules' ? (
            <EventRulesManager devices={devices} />
          ) : (
            <>
              {/* Event Log subtitle — mirrors the Event Rules title bar. */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-foreground" />
                  <h3 className="text-lg font-semibold">Event Log</h3>
                </div>
              </div>
              {/* Event stats — clickable cards drive the Status / Severity / Date dropdowns (like Maintenance). */}
              {eventStats && (() => {
                const activeOn = eventStatusFilter === 'active' && eventSeverityFilter === 'all';
                const criticalOn = eventStatusFilter === 'active' && eventSeverityFilter === 'critical';
                const todayOn = eventDatePreset === 'today';
                const applyCardFilter = (on, status, severity) => {
                  setEventStatusFilter(on ? 'all' : status);
                  setEventSeverityFilter(on ? 'all' : severity);
                  setEventsPage(0);
                };
                const toggleToday = () => {
                  if (todayOn) { setEventDatePreset('all'); setEventDateRange({ start: null, end: null }); }
                  else { const now = new Date(); const s = new Date(now); s.setHours(0, 0, 0, 0); setEventDatePreset('today'); setEventDateRange({ start: s, end: now }); }
                  setEventsPage(0);
                };
                const resetAll = () => {
                  setEventStatusFilter('all'); setEventSeverityFilter('all');
                  setEventDatePreset('all'); setEventDateRange({ start: null, end: null });
                  setEventsPage(0);
                };
                return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="events-summary">
                  <button type="button" className="text-left w-full h-full" onClick={() => applyCardFilter(activeOn, 'active', 'all')} data-testid="events-stat-active">
                    <Card className={`h-full transition-shadow hover:shadow-md ${activeOn ? 'ring-2 ring-[#2563eb]' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Active</p>
                            <p className="text-2xl font-bold" data-testid="events-summary-active">{eventStats.active}</p>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                            <Zap className="w-5 h-5 text-red-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                  <button type="button" className="text-left w-full h-full" onClick={() => applyCardFilter(criticalOn, 'active', 'critical')} data-testid="events-stat-critical">
                    <Card className={`h-full transition-shadow hover:shadow-md ${criticalOn ? 'ring-2 ring-[#2563eb]' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Critical</p>
                            <p className="text-2xl font-bold" data-testid="events-summary-critical">{eventStats.critical_active}</p>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                  <button type="button" className="text-left w-full h-full" onClick={toggleToday} data-testid="events-stat-today">
                    <Card className={`h-full transition-shadow hover:shadow-md ${todayOn ? 'ring-2 ring-[#2563eb]' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Today</p>
                            <p className="text-2xl font-bold" data-testid="events-summary-today">{eventStats.today}</p>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                  <button type="button" className="text-left w-full h-full" onClick={resetAll} data-testid="events-stat-total">
                    <Card className="h-full transition-shadow hover:shadow-md">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Total</p>
                            <p className="text-2xl font-bold" data-testid="events-summary-total">{eventStats.total}</p>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <Zap className="w-5 h-5 text-slate-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                </div>
                );
              })()}

              {/* Filters — search input spans the whole width, matching the
                  Shared Evidence layout. Includes an [x] to clear the query. */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search device, rule, road..."
                    value={eventSearch}
                    onChange={(e) => { setEventSearch(e.target.value); setEventsPage(0); }}
                    className="pl-9 pr-9 h-8 text-base md:text-sm"
                    data-testid="event-search-input"
                  />
                  {eventSearch && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0"
                      onClick={() => { setEventSearch(''); setEventsPage(0); }}
                      data-testid="event-search-clear"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <Select value={eventFilter} onValueChange={(v) => { setEventFilter(v); setEventsPage(0); }}>
                  <SelectTrigger className="w-44 h-8" data-testid="event-type-filter">
                    <SelectValue placeholder="Event Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventLogFilterOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        data-testid={`event-type-filter-option-${option.value}`}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={eventStatusFilter} onValueChange={(v) => { setEventStatusFilter(v); setEventsPage(0); }}>
                  <SelectTrigger className="w-36 h-8" data-testid="event-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="cleared">Cleared</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={eventSeverityFilter} onValueChange={(v) => { setEventSeverityFilter(v); setEventsPage(0); }}>
                  <SelectTrigger className="w-36 h-8" data-testid="event-severity-filter">
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severity</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={eventDatePreset} onValueChange={(v) => {
                  setEventDatePreset(v);
                  setEventsPage(0);
                  const now = new Date();
                  if (v === 'all') setEventDateRange({ start: null, end: null });
                  else if (v === '1hour') setEventDateRange({ start: new Date(now.getTime() - 3600000), end: now });
                  else if (v === '2hours') setEventDateRange({ start: new Date(now.getTime() - 7200000), end: now });
                  else if (v === '3hours') setEventDateRange({ start: new Date(now.getTime() - 10800000), end: now });
                  else if (v === 'today') { const s = new Date(now); s.setHours(0,0,0,0); setEventDateRange({ start: s, end: now }); }
                  else if (v === 'yesterday') { const s = subDays(now, 1); s.setHours(0,0,0,0); const e = new Date(s); e.setHours(23,59,59,999); setEventDateRange({ start: s, end: e }); }
                  else if (v === '7days') setEventDateRange({ start: subDays(now, 7), end: now });
                  else if (v === '30days') setEventDateRange({ start: subDays(now, 30), end: now });
                }}>
                  <SelectTrigger className="w-36 h-8" data-testid="event-date-filter">
                    <SelectValue placeholder="Date Range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="1hour">Last 1 Hour</SelectItem>
                    <SelectItem value="2hours">Last 2 Hours</SelectItem>
                    <SelectItem value="3hours">Last 3 Hours</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="7days">Last 7 Days</SelectItem>
                    <SelectItem value="30days">Last 30 Days</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
                {eventDatePreset === 'custom' && (
                  <div className="flex items-center gap-2">
                    <Popover open={eventStartOpen} onOpenChange={setEventStartOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="h-8 text-xs font-normal justify-between min-w-[180px]">
                          {eventDateRange.start ? formatDT(eventDateRange.start, { timezone: resolveTimezone(), seconds: false }) : 'Start date'}
                          <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={eventDateRange.start}
                          onSelect={(date) => {
                            if (!date) return;
                            const prev = eventDateRange.start;
                            date.setHours(prev?.getHours() || 0, prev?.getMinutes() || 0, 0, 0);
                            setEventDateRange(r => ({ ...r, start: date }));
                            setEventsPage(0);
                          }}
                        />
                        <div className="border-t p-3 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                          <Input
                            type="time"
                            value={eventDateRange.start ? `${String(eventDateRange.start.getHours()).padStart(2,'0')}:${String(eventDateRange.start.getMinutes()).padStart(2,'0')}` : '00:00'}
                            onChange={(e) => { const [h,m] = e.target.value.split(':').map(Number); setEventDateRange(r => { const d = new Date(r.start || new Date()); d.setHours(h||0,m||0,0,0); return { ...r, start: d }; }); setEventsPage(0); }}
                            className="h-8 text-sm flex-1"
                          />
                          <Button size="sm" className="h-8 text-xs" onClick={() => setEventStartOpen(false)}>Done</Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <span className="text-xs text-muted-foreground">to</span>
                    <Popover open={eventEndOpen} onOpenChange={setEventEndOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="h-8 text-xs font-normal justify-between min-w-[180px]">
                          {eventDateRange.end ? formatDT(eventDateRange.end, { timezone: resolveTimezone(), seconds: false }) : 'End date'}
                          <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={eventDateRange.end}
                          onSelect={(date) => {
                            if (!date) return;
                            const prev = eventDateRange.end;
                            date.setHours(prev?.getHours() || 23, prev?.getMinutes() || 59, 59, 999);
                            setEventDateRange(r => ({ ...r, end: date }));
                            setEventsPage(0);
                          }}
                        />
                        <div className="border-t p-3 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                          <Input
                            type="time"
                            value={eventDateRange.end ? `${String(eventDateRange.end.getHours()).padStart(2,'0')}:${String(eventDateRange.end.getMinutes()).padStart(2,'0')}` : '23:59'}
                            onChange={(e) => { const [h,m] = e.target.value.split(':').map(Number); setEventDateRange(r => { const d = new Date(r.end || new Date()); d.setHours(h||0,m||0,59,999); return { ...r, end: d }; }); setEventsPage(0); }}
                            className="h-8 text-sm flex-1"
                          />
                          <Button size="sm" className="h-8 text-xs" onClick={() => setEventEndOpen(false)}>Done</Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportEvents}
                  disabled={selectedEventIds.length === 0 || eventExporting}
                  data-testid="event-export-btn"
                >
                  {eventExporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                  {selectedEventIds.length > 0 ? `Export (${selectedEventIds.length})` : 'Export'}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => { setEventFilter('all'); setEventStatusFilter('all'); setEventSeverityFilter('all'); setEventSearch(''); setEventDatePreset('all'); setEventDateRange({ start: null, end: null }); setEventsPage(0); setSelectedEventIds([]); fetchEvents(0); }}
                  data-testid="event-refresh-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
              </div>

              {/* Selection bar — mirrors Shared Evidence. Select All grabs every
                  filtered event ID across all pages via ids_only. */}
              <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="events-selection-bar">
                  <span data-testid="event-selection-count">{selectedEventIds.length} selected</span>
{" "}
                  {selectedEventIds.length > 0 && (<>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
                    disabled={eventSelectAllLoading}
                    data-testid="event-select-all-toggle"
                    onClick={async () => {
                      if (selectedEventIds.length >= eventsTotal) { setSelectedEventIds([]); return; }
                      try {
                        setEventSelectAllLoading(true);
                        const params = buildEventFilterParams();
                        params.set('ids_only', 'true');
                        const res = await api.get(`/event-occurrences?${params}`);
                        setSelectedEventIds(res.data?.ids || []);
                      } catch {
                        toast.error('Failed to select all rows');
                      } finally {
                        setEventSelectAllLoading(false);
                      }
                    }}
                  >
                    {eventSelectAllLoading ? 'Selecting…' : (selectedEventIds.length >= eventsTotal ? 'Deselect All' : 'Select All')}
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedEventIds([])} data-testid="event-selection-clear">Clear</Button>
                </>)}
                  </div>

              {/* Events List */}
              <Card>
                <CardContent className="p-0">
                  {!eventsInitialLoadDone ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : events.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Zap className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No events found</p>
                      <p className="text-xs mt-1">Events will appear here when event rules are triggered</p>
                    </div>
                  ) : (
                    <div>
                      {/* Page select-all header — checks only the rows on this page. */}
                      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
                        <Checkbox
                          checked={events.length > 0 && events.every(ev => selectedEventIds.includes(ev.id))}
                          onCheckedChange={(checked) => {
                            const pageIds = events.map(ev => ev.id);
                            setSelectedEventIds(prev => checked
                              ? Array.from(new Set([...prev, ...pageIds]))
                              : prev.filter(id => !pageIds.includes(id)));
                          }}
                          data-testid="event-select-page"
                        />
                        <span className="text-xs text-muted-foreground">Select page</span>
                      </div>
                      <div className="divide-y">
                        {events.map((event) => {
                          const isSpeed = event.event_type?.includes('overspeed');
                          const Icon = isSpeed ? Gauge : (alertTypeIcons[event.event_type] || Zap);
                          const severityClass = event.severity === 'critical'
                            ? 'bg-red-100 dark:bg-red-900/30'
                            : event.severity === 'warning'
                            ? 'bg-amber-100 dark:bg-amber-900/30'
                            : 'bg-slate-100 dark:bg-slate-800';
                          const iconClass = event.severity === 'critical'
                            ? 'text-red-600'
                            : event.severity === 'warning'
                            ? 'text-amber-600'
                            : 'text-slate-600';
                          const statusBadge = event.status === 'active'
                            ? 'bg-red-100 text-red-700 border-red-200'
                            : event.status === 'cleared'
                            ? 'bg-green-100 text-green-700 border-green-200'
                            : 'bg-slate-100 text-slate-700 border-slate-200';

                          return (
                            <div
                              key={event.id}
                              className={`flex items-start gap-3 px-3 py-2 hover:bg-muted/50 transition-colors cursor-pointer ${selectedEventIds.includes(event.id) ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`}
                              onClick={() => { setSelectedEvent(event); setEventDetailOpen(true); }}
                              data-testid={`event-occurrence-${event.id}`}
                            >
                              <Checkbox
                                className="mt-1.5 flex-shrink-0"
                                checked={selectedEventIds.includes(event.id)}
                                onCheckedChange={(checked) => setSelectedEventIds(prev => checked ? [...prev, event.id] : prev.filter(id => id !== event.id))}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`event-select-${event.id}`}
                              />
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${severityClass}`}>
                                <Icon className={`w-3.5 h-3.5 ${iconClass}`} />
                              </div>
                              <div className="flex-1 min-w-0 space-y-0.5">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-xs font-semibold">{event.event_rule_name}</span>
                                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${severityColors[event.severity] || ''}`}>{event.severity}</Badge>
                                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 capitalize ${statusBadge}`}>{event.status}</Badge>
                                  {event.metadata?.reattach_resolved_at && (
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`event-resolved-badge-${event.id}`}>resolved</Badge>
                                  )}
                                </div>
                                <div className="text-xs font-medium text-slate-700 dark:text-slate-200" data-testid={`event-occurrence-device-${event.id}`}>{formatRecordDeviceLabel(event, devicesById)}</div>
                                {isSpeed && (
                                  <div className="text-[11px] text-red-600 font-medium">
                                    {formatSpeed(event.speed_kmh)} (Limit: {formatSpeed(event.threshold_kmh)}){event.road_name ? ` on ${event.road_name}` : ''}
                                  </div>
                                )}
                                {!isSpeed && event.message && (
                                  <div className="text-[11px] text-muted-foreground">
                                    {event.message}
                                  </div>
                                )}
                                <div className="text-[10px] text-muted-foreground">
                                  {formatDT(event.triggered_at, { timezone: event.device_timezone || resolveTimezone(), seconds: true })} — {getTimezoneLabel(event.device_timezone || resolveTimezone()) || (event.device_timezone || resolveTimezone())}
                                </div>
                                {event.duration_secs != null && !(event.metadata?.instant_event || (event.duration_secs === 0 && event.status === 'cleared')) && (
                                  <div className="text-[10px] text-muted-foreground">
                                    Duration: {event.duration_secs >= 60 ? `${Math.floor(event.duration_secs/60)}m ${Math.round(event.duration_secs%60)}s` : `${Math.round(event.duration_secs)}s`}
                                  </div>
                                )}
                                {(event.latitude != null && event.longitude != null && (Number(event.latitude) !== 0 || Number(event.longitude) !== 0)) ? (
                                  <div className="flex items-center gap-1 text-[10px] text-blue-500">
                                    <MapPin className="w-3 h-3 flex-shrink-0" />
                                    {Number(event.latitude).toFixed(5)}, {Number(event.longitude).toFixed(5)}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Pagination — unified shared footer (server-side) */}
                  <PaginationFooter
                    page={eventsPage}
                    setPage={setEventsPage}
                    totalItems={eventsTotal}
                    itemsPerPage={eventsPageSize}
                    setItemsPerPage={setEventsPageSize}
                    testIdPrefix="events"
                    zeroIndexed
                    disabled={eventsLoading}
                    alwaysShow
                  />
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Event Detail Modal */}
      <EventDetailModal
        event={selectedEvent}
        open={eventDetailOpen}
        onOpenChange={setEventDetailOpen}
        devicesById={devicesById}
        onViewDevice={(deviceId, lat, lng, eventId) => navigate(`/tracking?select=${deviceId}${lat != null && lng != null ? `&lat=${lat}&lng=${lng}&zoom=17` : ''}${eventId ? `&event=${eventId}` : ''}`)}
      />

      {/* ==================== ALERTS TAB ==================== */}
      {activeTab === 'alerts' && hasAlerts && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">Alerts</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">Actionable alerts requiring your attention.</p>
            </div>
          </div>

          {/* Alert Stats — clickable cards drive the Status / Severity dropdowns (like Maintenance). */}
          {alertStats && (() => {
            const activeOn = alertFilter === 'active' && alertSeverityFilter === 'all';
            const criticalOn = alertFilter === 'active' && alertSeverityFilter === 'critical';
            const highOn = alertFilter === 'active' && alertSeverityFilter === 'high';
            const applyCardFilter = (on, status, severity) => {
              setAlertFilter(on ? 'all' : status);
              setAlertSeverityFilter(on ? 'all' : severity);
              setAlertsPage(0);
            };
            const resetAll = () => { setAlertFilter('all'); setAlertSeverityFilter('all'); setAlertsPage(0); };
            return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="alerts-summary">
              <button type="button" className="text-left w-full h-full" onClick={() => applyCardFilter(activeOn, 'active', 'all')} data-testid="alerts-stat-active">
                <Card className={`h-full transition-shadow hover:shadow-md ${activeOn ? 'ring-2 ring-[#2563eb]' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Active</p>
                        <p className="text-2xl font-bold">{alertStats.active}</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-red-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>
              <button type="button" className="text-left w-full h-full" onClick={() => applyCardFilter(criticalOn, 'active', 'critical')} data-testid="alerts-stat-critical">
                <Card className={`h-full transition-shadow hover:shadow-md ${criticalOn ? 'ring-2 ring-[#2563eb]' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Critical</p>
                        <p className="text-2xl font-bold">{alertStats.critical_active}</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>
              <button type="button" className="text-left w-full h-full" onClick={() => applyCardFilter(highOn, 'active', 'high')} data-testid="alerts-stat-high">
                <Card className={`h-full transition-shadow hover:shadow-md ${highOn ? 'ring-2 ring-[#2563eb]' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">High Priority</p>
                        <p className="text-2xl font-bold">{alertStats.high_active}</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                        <AlertCircle className="w-5 h-5 text-orange-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>
              <button type="button" className="text-left w-full h-full" onClick={resetAll} data-testid="alerts-stat-total">
                <Card className="h-full transition-shadow hover:shadow-md">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total</p>
                        <p className="text-2xl font-bold">{alertStats.total}</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-slate-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>
            </div>
            );
          })()}

          {/* Filters — Event-Log-style search + type + status + date + export + refresh */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search title, device, message..."
                value={alertSearch}
                onChange={(e) => { setAlertSearch(e.target.value); setAlertsPage(0); }}
                className="pl-9 pr-9 h-8 text-base md:text-sm"
                data-testid="alert-search-input"
              />
              {alertSearch && (
                <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => { setAlertSearch(''); setAlertsPage(0); }} data-testid="alert-search-clear">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            <Select value={alertTypeFilter} onValueChange={(v) => { setAlertTypeFilter(v); setAlertsPage(0); }}>
              <SelectTrigger className="w-40 h-8" data-testid="alert-type-filter">
                <SelectValue placeholder="Alert Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="low_battery">Low Battery</SelectItem>
                <SelectItem value="disconnect">Disconnected</SelectItem>
                <SelectItem value="maintenance">Service</SelectItem>
                <SelectItem value="maintenance_spend">Maintenance Spend</SelectItem>
                <SelectItem value="document_expiry">Document Expiry</SelectItem>
                <SelectItem value="evidence_viewed">Evidence Viewed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={alertFilter} onValueChange={(v) => { setAlertFilter(v); setAlertsPage(0); }}>
              <SelectTrigger className="w-36 h-8" data-testid="alert-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            <Select value={alertSeverityFilter} onValueChange={(v) => { setAlertSeverityFilter(v); setAlertsPage(0); }}>
              <SelectTrigger className="w-36 h-8" data-testid="alert-severity-filter">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <DateRangePresetFilter
              value={{ from: alertDateRange.start || undefined, to: alertDateRange.end || undefined }}
              onChange={(r) => { setAlertDateRange({ start: r.from || null, end: r.to || null }); setAlertDatePreset(r.from || r.to ? 'custom' : 'all'); setAlertsPage(0); }}
              testIdPrefix="alert-date"
            />
            <AlertsHelpPanel triggerTestId="alerts-help-btn-header" />
            <Button variant="outline" size="sm" onClick={handleExportAlerts} disabled={selectedAlertIds.length === 0 || alertExporting} data-testid="alert-export-btn">
              {alertExporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              {selectedAlertIds.length > 0 ? `Export (${selectedAlertIds.length})` : 'Export'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setAlertFilter('all'); setAlertSeverityFilter('all'); setAlertTypeFilter('all'); setAlertSearch(''); setAlertDatePreset('all'); setAlertDateRange({ start: null, end: null }); setAlertsPage(0); setSelectedAlertIds([]); fetchAlerts(0); }} data-testid="alert-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
            <Dialog open={isAddRuleDialogOpen} onOpenChange={setIsAddRuleDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8] ml-auto" data-testid="alert-settings-button">
                  <Settings className="w-4 h-4 mr-2" />
                  Alert Settings
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="alert-settings-dialog" className="w-[calc(100%-1.5rem)] max-w-lg sm:max-w-2xl p-4 sm:p-6">
                <DialogHeader>
                  <div className="flex items-center justify-between gap-2 pr-6">
                    <DialogTitle>Alert Settings</DialogTitle>
                    <AlertsHelpPanel />
                  </div>
                </DialogHeader>
                <div className="space-y-3 py-2">
                  {availableConditions.low_battery && (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3" data-testid="alert-row-low_battery">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <BatteryLow className="w-5 h-5 text-[#2563eb] flex-none" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm">Low Beacon Battery</p>
                          <p className="text-xs text-muted-foreground truncate">Alert when a BLE beacon's battery is low</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-none">
                        <Button size="sm" variant="outline" onClick={() => setConfigureType('low_battery')} data-testid="configure-low_battery-button">
                          <Settings className="w-3.5 h-3.5 mr-1" /> Configure
                        </Button>
                        <Switch
                          checked={batteryConfig.is_active}
                          onCheckedChange={(v) => toggleAlertActive('low_battery', v)}
                          data-testid="battery-alert-enable-switch"
                        />
                      </div>
                    </div>
                  )}

                  {availableConditions.disconnect && (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3" data-testid="alert-row-disconnect">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Power className="w-5 h-5 text-[#2563eb] flex-none" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm">Device Disconnected</p>
                          <p className="text-xs text-muted-foreground truncate">Alert when a tracker goes offline</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-none">
                        <Button size="sm" variant="outline" onClick={() => setConfigureType('disconnect')} data-testid="configure-disconnect-button">
                          <Settings className="w-3.5 h-3.5 mr-1" /> Configure
                        </Button>
                        <Switch
                          checked={disconnectConfig.is_active}
                          onCheckedChange={(v) => toggleAlertActive('disconnect', v)}
                          data-testid="disconnect-alert-enable-switch"
                        />
                      </div>
                    </div>
                  )}

                  {availableConditions.maintenance_spend && hasMaintenancePage && (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3" data-testid="alert-row-maintenance_spend">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <DollarSign className="w-5 h-5 text-[#2563eb] flex-none" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm">High Maintenance Spend</p>
                          <p className="text-xs text-muted-foreground truncate">Alert when a vehicle's rolling 3-month spend passes a threshold</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-none">
                        <Button size="sm" variant="outline" onClick={() => setConfigureType('maintenance_spend')} data-testid="configure-maintenance_spend-button">
                          <Settings className="w-3.5 h-3.5 mr-1" /> Configure
                        </Button>
                        <Switch
                          checked={spendConfig.is_active}
                          onCheckedChange={(v) => toggleAlertActive('maintenance_spend', v)}
                          data-testid="spend-alert-enable-switch"
                        />
                      </div>
                    </div>
                  )}

                  {availableConditions.document_expiry && (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3" data-testid="alert-row-document_expiry">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <FileClock className="w-5 h-5 text-[#2563eb] flex-none" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm">Document Expiry</p>
                          <p className="text-xs text-muted-foreground truncate">Alert before a person's licence or document expires</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-none">
                        <Button size="sm" variant="outline" onClick={() => setConfigureType('document_expiry')} data-testid="configure-document_expiry-button">
                          <Settings className="w-3.5 h-3.5 mr-1" /> Configure
                        </Button>
                        <Switch
                          checked={docExpiryConfig.is_active}
                          onCheckedChange={(v) => toggleAlertActive('document_expiry', v)}
                          data-testid="document-expiry-alert-enable-switch"
                        />
                      </div>
                    </div>
                  )}

                  {availableConditions.evidence_viewed && (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3" data-testid="alert-row-evidence_viewed">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Eye className="w-5 h-5 text-[#2563eb] flex-none" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm">Shared Evidence Viewed</p>
                          <p className="text-xs text-muted-foreground truncate">Alert when a shared evidence link is first opened</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-none">
                        <Button size="sm" variant="outline" onClick={() => setConfigureType('evidence_viewed')} data-testid="configure-evidence_viewed-button">
                          <Settings className="w-3.5 h-3.5 mr-1" /> Configure
                        </Button>
                        <Switch
                          checked={evidenceViewedConfig.is_active}
                          onCheckedChange={(v) => toggleAlertActive('evidence_viewed', v)}
                          data-testid="evidence-viewed-alert-enable-switch"
                        />
                      </div>
                    </div>
                  )}

                  {!availableConditions.low_battery && !availableConditions.disconnect && (
                    <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="no-conditions-note">
                      No condition alerts are available for your account yet.
                    </div>
                  )}

                  {hasMaintenancePage && (
                    <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="service-alerts-note">
                      <div className="flex items-start gap-2">
                        <Wrench className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">Service / Maintenance alerts</p>
                          <p className="mt-0.5">These are configured per service schedule in <span className="font-medium">Fleet › Maintenance</span>. Each reminder stage there sets its own severity, notifications and actions — service alerts still appear in this Alerts list.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="signin-security-alerts-note">
                    <div className="flex items-start gap-2">
                      <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-foreground">Sign-in Security alerts</p>
                        <p className="mt-0.5">Suspicious login alerts (new IP, new device, new country, or outside your home-region geofence) are configured in <span className="font-medium">Settings › Devices & Sign-in Security</span> — that's where you set your home region, radius, trusted-device exception and Travel Mode. They arrive as instant bell/email/push notifications and don't appear in this Alerts list.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Per-type: Low Beacon Battery */}
            <Dialog open={configureType === 'low_battery'} onOpenChange={(o) => !o && setConfigureType(null)}>
              <DialogContent data-testid="battery-config-dialog" className="w-[calc(100%-1.5rem)] max-w-lg sm:max-w-2xl h-[85vh] sm:h-[80vh] p-4 sm:p-6 overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><BatteryLow className="w-4 h-4 text-[#2563eb]" /> Low Beacon Battery</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="settings" className="flex-1 flex flex-col overflow-hidden">
                  <TabsList className={`grid ${hasEventActions ? 'grid-cols-3' : 'grid-cols-2'} w-full`}>
                    <TabsTrigger value="settings" data-testid="battery-tab-settings">Settings</TabsTrigger>
                    <TabsTrigger value="notifications" data-testid="battery-tab-notifications">Notifications</TabsTrigger>
                    {hasEventActions && <TabsTrigger value="actions" data-testid="battery-tab-actions">Actions</TabsTrigger>}
                  </TabsList>
                  <div className="flex-1 overflow-y-auto mt-3 pr-1">
                    <TabsContent value="settings" className="space-y-4 mt-0">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">Threshold (%)<AlertFieldTip topic="battery_threshold" /></Label>
                          <Input type="number" min={1} max={100}
                            value={batteryConfig.threshold_percent}
                            onChange={(e) => setBatteryConfig(prev => ({ ...prev, threshold_percent: e.target.value }))}
                            data-testid="battery-alert-threshold-input" />
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">Re-arm margin (%)<AlertFieldTip topic="battery_rearm" /></Label>
                          <Input type="number" min={0} max={50}
                            value={batteryConfig.hysteresis_percent}
                            onChange={(e) => setBatteryConfig(prev => ({ ...prev, hysteresis_percent: e.target.value }))}
                            data-testid="battery-alert-hysteresis-input" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">Severity<AlertFieldTip topic="alert_severity" /></Label>
                        <Select value={batteryConfig.severity} onValueChange={(v) => setBatteryConfig(prev => ({ ...prev, severity: v }))}>
                          <SelectTrigger data-testid="battery-alert-severity-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TabsContent>
                    <TabsContent value="notifications" className="mt-0" data-testid="battery-notifications-tab">
                      <NotificationsConfig
                        notifications={batteryConfig.notifications}
                        onChange={(k, v) => setBatteryConfig(prev => ({ ...prev, notifications: { ...prev.notifications, [k]: v } }))}
                        hasPushNotif={hasPushNotif}
                        hasEmailNotif={hasEmailNotif}
                        hasSmsNotif={hasSmsNotif}
                        pushDisabledReason={pushDisabledReason}
                        emailDisabledReason={emailDisabledReason}
                        smsDisabledReason={smsDisabledReason}
                        notifTemplates={notifTemplates}
                        onManageTemplate={openAlertTemplateManager}
                        supportsTransitionCardColorTiming={false}
                      />
                    </TabsContent>
                    {hasEventActions && (
                    <TabsContent value="actions" className="mt-0 space-y-3" data-testid="battery-actions-tab">
                      <WebhookActionConfig
                        actions={batteryConfig.actions}
                        onChange={(k, v) => setBatteryConfig(prev => ({ ...prev, actions: { ...prev.actions, [k]: v } }))}
                        api={api}
                        testPath="/event-rules/test-webhook"
                        webhookRuleId={batteryConfig.id}
                      />
                      <p className="text-[11px] text-muted-foreground px-1">Device commands (e.g. send a command when a service is due) will be available with the upcoming Service module.</p>
                    </TabsContent>
                    )}
                  </div>
                </Tabs>
                <DialogFooter>
                  <Button size="sm" variant="outline" onClick={() => setConfigureType(null)} data-testid="battery-config-cancel">Cancel</Button>
                  <Button size="sm" onClick={() => saveAlertConfig('low_battery')} disabled={savingAlertSettings} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="battery-config-save">
                    {savingAlertSettings ? 'Saving…' : 'Save Settings'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Per-type: Document Expiry */}
            <Dialog open={configureType === 'document_expiry'} onOpenChange={(o) => !o && setConfigureType(null)}>
              <DialogContent data-testid="document-expiry-config-dialog" className="w-[calc(100%-1.5rem)] max-w-lg sm:max-w-2xl h-[85vh] sm:h-[80vh] p-4 sm:p-6 overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><FileClock className="w-4 h-4 text-[#2563eb]" /> Document Expiry</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="settings" className="flex-1 flex flex-col overflow-hidden">
                  <TabsList className={`grid ${hasEventActions ? 'grid-cols-3' : 'grid-cols-2'} w-full`}>
                    <TabsTrigger value="settings" data-testid="document-expiry-tab-settings">Settings</TabsTrigger>
                    <TabsTrigger value="notifications" data-testid="document-expiry-tab-notifications">Notifications</TabsTrigger>
                    {hasEventActions && <TabsTrigger value="actions" data-testid="document-expiry-tab-actions">Actions</TabsTrigger>}
                  </TabsList>
                  <div className="flex-1 overflow-y-auto mt-3 pr-1">
                    <TabsContent value="settings" className="space-y-4 mt-0">
                      <div className="space-y-2">
                        <Label>Warn this many days before expiry</Label>
                        <Input type="number" min={0} max={365}
                          value={docExpiryConfig.warn_days}
                          onChange={(e) => setDocExpiryConfig(prev => ({ ...prev, warn_days: e.target.value }))}
                          data-testid="document-expiry-warn-days-input" />
                        <p className="text-[11px] text-muted-foreground">Checks the documents attached to your Drivers, Staff and Passengers. One alert per document — it escalates to critical once the date lapses, and auto-resolves when the document is renewed or removed.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Document types to watch</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {[['licence', 'Licence'], ['id', 'ID'], ['certificate', 'Certificate'], ['other', 'Other']].map(([val, label]) => (
                            <label key={val} className="flex items-center gap-2 rounded-md border p-2 text-sm cursor-pointer">
                              <Checkbox
                                checked={(docExpiryConfig.doc_types || []).includes(val)}
                                onCheckedChange={(v) => setDocExpiryConfig(prev => ({
                                  ...prev,
                                  doc_types: v ? [...(prev.doc_types || []), val] : (prev.doc_types || []).filter(t => t !== val),
                                }))}
                                data-testid={`document-expiry-type-${val}`}
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <div>
                          <p className="text-sm font-medium">Driver "Licence expiry" field</p>
                          <p className="text-[11px] text-muted-foreground">Also watch the dedicated licence expiry date on Driver records</p>
                        </div>
                        <Switch
                          checked={docExpiryConfig.include_license_expiry}
                          onCheckedChange={(v) => setDocExpiryConfig(prev => ({ ...prev, include_license_expiry: v }))}
                          data-testid="document-expiry-license-field-switch"
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <div>
                          <p className="text-sm font-medium">People documents</p>
                          <p className="text-[11px] text-muted-foreground">Watch uploaded documents on drivers / staff / passengers (People &gt; Edit &gt; Documents)</p>
                        </div>
                        <Switch
                          checked={docExpiryConfig.include_person_documents !== false}
                          onCheckedChange={(v) => setDocExpiryConfig(prev => ({ ...prev, include_person_documents: v }))}
                          data-testid="document-expiry-person-docs-switch"
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <div>
                          <p className="text-sm font-medium">Vehicle documents</p>
                          <p className="text-[11px] text-muted-foreground">Also watch registration / insurance / inspection documents on your vehicles (Fleet &gt; Edit &gt; Documents)</p>
                        </div>
                        <Switch
                          checked={docExpiryConfig.include_vehicle_documents !== false}
                          onCheckedChange={(v) => setDocExpiryConfig(prev => ({ ...prev, include_vehicle_documents: v }))}
                          data-testid="document-expiry-vehicle-docs-switch"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">Severity<AlertFieldTip topic="alert_severity" /></Label>
                        <Select value={docExpiryConfig.severity} onValueChange={(v) => setDocExpiryConfig(prev => ({ ...prev, severity: v }))}>
                          <SelectTrigger data-testid="document-expiry-severity-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button size="sm" variant="outline" onClick={runDocExpiryCheck} data-testid="document-expiry-run-check-button">
                        Run check now
                      </Button>
                    </TabsContent>
                    <TabsContent value="notifications" className="mt-0" data-testid="document-expiry-notifications-tab">
                      <NotificationsConfig
                        notifications={docExpiryConfig.notifications}
                        onChange={(k, v) => setDocExpiryConfig(prev => ({ ...prev, notifications: { ...prev.notifications, [k]: v } }))}
                        hasPushNotif={hasPushNotif}
                        hasEmailNotif={hasEmailNotif}
                        hasSmsNotif={hasSmsNotif}
                        pushDisabledReason={pushDisabledReason}
                        emailDisabledReason={emailDisabledReason}
                        smsDisabledReason={smsDisabledReason}
                        notifTemplates={notifTemplates}
                        onManageTemplate={openAlertTemplateManager}
                        supportsTransitionCardColorTiming={false}
                        showDeviceCardColor={false}
                      />
                    </TabsContent>
                    {hasEventActions && (
                    <TabsContent value="actions" className="mt-0 space-y-3" data-testid="document-expiry-actions-tab">
                      <WebhookActionConfig
                        actions={docExpiryConfig.actions}
                        onChange={(k, v) => setDocExpiryConfig(prev => ({ ...prev, actions: { ...prev.actions, [k]: v } }))}
                        api={api}
                        testPath="/event-rules/test-webhook"
                        webhookRuleId={docExpiryConfig.id}
                      />
                    </TabsContent>
                    )}
                  </div>
                </Tabs>
                <DialogFooter>
                  <Button size="sm" variant="outline" onClick={() => setConfigureType(null)} data-testid="document-expiry-config-cancel">Cancel</Button>
                  <Button size="sm" onClick={() => saveAlertConfig('document_expiry')} disabled={savingAlertSettings} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="document-expiry-config-save">
                    {savingAlertSettings ? 'Saving…' : 'Save Settings'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Per-type: Shared Evidence Viewed */}
            <Dialog open={configureType === 'evidence_viewed'} onOpenChange={(o) => !o && setConfigureType(null)}>
              <DialogContent data-testid="evidence-viewed-config-dialog" className="w-[calc(100%-1.5rem)] max-w-lg sm:max-w-2xl h-[85vh] sm:h-[80vh] p-4 sm:p-6 overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4 text-[#2563eb]" /> Shared Evidence Viewed</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="settings" className="flex-1 flex flex-col overflow-hidden">
                  <TabsList className={`grid ${hasEventActions ? 'grid-cols-3' : 'grid-cols-2'} w-full`}>
                    <TabsTrigger value="settings" data-testid="evidence-viewed-tab-settings">Settings</TabsTrigger>
                    <TabsTrigger value="notifications" data-testid="evidence-viewed-tab-notifications">Notifications</TabsTrigger>
                    {hasEventActions && <TabsTrigger value="actions" data-testid="evidence-viewed-tab-actions">Actions</TabsTrigger>}
                  </TabsList>
                  <div className="flex-1 overflow-y-auto mt-3 pr-1">
                    <TabsContent value="settings" className="space-y-4 mt-0">
                      <p className="text-[11px] text-muted-foreground">Fires once per share link, the moment the recipient first opens it. The full view-by-view trail stays in Media &gt; Shared Evidence.</p>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <div>
                          <p className="text-sm font-medium">Also alert on first download</p>
                          <p className="text-[11px] text-muted-foreground">A second alert if the recipient downloads the original clip (links with downloads enabled)</p>
                        </div>
                        <Switch
                          checked={evidenceViewedConfig.alert_on_download !== false}
                          onCheckedChange={(v) => setEvidenceViewedConfig(prev => ({ ...prev, alert_on_download: v }))}
                          data-testid="evidence-viewed-download-switch"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">Severity<AlertFieldTip topic="alert_severity" /></Label>
                        <Select value={evidenceViewedConfig.severity} onValueChange={(v) => setEvidenceViewedConfig(prev => ({ ...prev, severity: v }))}>
                          <SelectTrigger data-testid="evidence-viewed-severity-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TabsContent>
                    <TabsContent value="notifications" className="mt-0" data-testid="evidence-viewed-notifications-tab">
                      <NotificationsConfig
                        notifications={evidenceViewedConfig.notifications}
                        onChange={(k, v) => setEvidenceViewedConfig(prev => ({ ...prev, notifications: { ...prev.notifications, [k]: v } }))}
                        hasPushNotif={hasPushNotif}
                        hasEmailNotif={hasEmailNotif}
                        hasSmsNotif={hasSmsNotif}
                        pushDisabledReason={pushDisabledReason}
                        emailDisabledReason={emailDisabledReason}
                        smsDisabledReason={smsDisabledReason}
                        notifTemplates={notifTemplates}
                        onManageTemplate={openAlertTemplateManager}
                        supportsTransitionCardColorTiming={false}
                        showDeviceCardColor={false}
                      />
                    </TabsContent>
                    {hasEventActions && (
                    <TabsContent value="actions" className="mt-0 space-y-3" data-testid="evidence-viewed-actions-tab">
                      <WebhookActionConfig
                        actions={evidenceViewedConfig.actions}
                        onChange={(k, v) => setEvidenceViewedConfig(prev => ({ ...prev, actions: { ...prev.actions, [k]: v } }))}
                        api={api}
                        testPath="/event-rules/test-webhook"
                        webhookRuleId={evidenceViewedConfig.id}
                      />
                    </TabsContent>
                    )}
                  </div>
                </Tabs>
                <DialogFooter>
                  <Button size="sm" variant="outline" onClick={() => setConfigureType(null)} data-testid="evidence-viewed-config-cancel">Cancel</Button>
                  <Button size="sm" onClick={() => saveAlertConfig('evidence_viewed')} disabled={savingAlertSettings} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="evidence-viewed-config-save">
                    {savingAlertSettings ? 'Saving…' : 'Save Settings'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Per-type: Device Disconnected */}
            <Dialog open={configureType === 'disconnect'} onOpenChange={(o) => !o && setConfigureType(null)}>
              <DialogContent data-testid="disconnect-config-dialog" className="w-[calc(100%-1.5rem)] max-w-lg sm:max-w-2xl h-[85vh] sm:h-[80vh] p-4 sm:p-6 overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Power className="w-4 h-4 text-[#2563eb]" /> Device Disconnected</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="settings" className="flex-1 flex flex-col overflow-hidden">
                  <TabsList className={`grid ${hasEventActions ? 'grid-cols-3' : 'grid-cols-2'} w-full`}>
                    <TabsTrigger value="settings" data-testid="disconnect-tab-settings">Settings</TabsTrigger>
                    <TabsTrigger value="notifications" data-testid="disconnect-tab-notifications">Notifications</TabsTrigger>
                    {hasEventActions && <TabsTrigger value="actions" data-testid="disconnect-tab-actions">Actions</TabsTrigger>}
                  </TabsList>
                  <div className="flex-1 overflow-y-auto mt-3 pr-1">
                    <TabsContent value="settings" className="space-y-4 mt-0">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">Extra grace before alerting (minutes)<AlertFieldTip topic="disconnect_grace" /></Label>
                        <Input type="number" min={0} max={1440}
                          value={disconnectConfig.offline_minutes}
                          onChange={(e) => setDisconnectConfig(prev => ({ ...prev, offline_minutes: e.target.value }))}
                          data-testid="disconnect-alert-grace-input" />
                        <p className="text-[11px] text-muted-foreground">0 = alert as soon as the device is marked offline.</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">Severity<AlertFieldTip topic="alert_severity" /></Label>
                        <Select value={disconnectConfig.severity} onValueChange={(v) => setDisconnectConfig(prev => ({ ...prev, severity: v }))}>
                          <SelectTrigger data-testid="disconnect-alert-severity-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="rounded-md bg-muted/50 p-3 space-y-3">
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">Escalate if still offline after (hours)<AlertFieldTip topic="escalate_hours" /></Label>
                          <Input type="number" min={0} max={168}
                            value={disconnectConfig.escalate_after_hours}
                            onChange={(e) => setDisconnectConfig(prev => ({ ...prev, escalate_after_hours: e.target.value }))}
                            data-testid="disconnect-alert-escalate-hours-input" />
                          <p className="text-[11px] text-muted-foreground">0 = no escalation. A long outage re-notifies once at a raised severity.</p>
                        </div>
                        <div className={`space-y-2 transition-opacity ${Number(disconnectConfig.escalate_after_hours) > 0 ? 'opacity-100' : 'opacity-50'}`}>
                          <Label className="flex items-center gap-1">Escalation severity<AlertFieldTip topic="escalate_severity" /></Label>
                          <Select value={disconnectConfig.escalate_severity} onValueChange={(v) => setDisconnectConfig(prev => ({ ...prev, escalate_severity: v }))} disabled={Number(disconnectConfig.escalate_after_hours) <= 0}>
                            <SelectTrigger data-testid="disconnect-alert-escalate-severity-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="critical">Critical</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="notifications" className="mt-0" data-testid="disconnect-notifications-tab">
                      <NotificationsConfig
                        notifications={disconnectConfig.notifications}
                        onChange={(k, v) => setDisconnectConfig(prev => ({ ...prev, notifications: { ...prev.notifications, [k]: v } }))}
                        hasPushNotif={hasPushNotif}
                        hasEmailNotif={hasEmailNotif}
                        hasSmsNotif={hasSmsNotif}
                        pushDisabledReason={pushDisabledReason}
                        emailDisabledReason={emailDisabledReason}
                        smsDisabledReason={smsDisabledReason}
                        notifTemplates={notifTemplates}
                        onManageTemplate={openAlertTemplateManager}
                        supportsTransitionCardColorTiming={false}
                      />
                    </TabsContent>
                    {hasEventActions && (
                    <TabsContent value="actions" className="mt-0 space-y-3" data-testid="disconnect-actions-tab">
                      <WebhookActionConfig
                        actions={disconnectConfig.actions}
                        onChange={(k, v) => setDisconnectConfig(prev => ({ ...prev, actions: { ...prev.actions, [k]: v } }))}
                        api={api}
                        testPath="/event-rules/test-webhook"
                        webhookRuleId={disconnectConfig.id}
                      />
                      <p className="text-[11px] text-muted-foreground px-1">Device commands (e.g. send a command when a service is due) will be available with the upcoming Service module.</p>
                    </TabsContent>
                    )}
                  </div>
                </Tabs>
                <DialogFooter>
                  <Button size="sm" variant="outline" onClick={() => setConfigureType(null)} data-testid="disconnect-config-cancel">Cancel</Button>
                  <Button size="sm" onClick={() => saveAlertConfig('disconnect')} disabled={savingAlertSettings} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="disconnect-config-save">
                    {savingAlertSettings ? 'Saving…' : 'Save Settings'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Per-type: High Maintenance Spend */}
            <Dialog open={configureType === 'maintenance_spend'} onOpenChange={(o) => !o && setConfigureType(null)}>
              <DialogContent data-testid="spend-config-dialog" className="w-[calc(100%-1.5rem)] max-w-lg sm:max-w-2xl h-[85vh] sm:h-[80vh] p-4 sm:p-6 overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#2563eb]" /> High Maintenance Spend</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="settings" className="flex-1 flex flex-col overflow-hidden">
                  <TabsList className={`grid ${hasEventActions ? 'grid-cols-3' : 'grid-cols-2'} w-full`}>
                    <TabsTrigger value="settings" data-testid="spend-tab-settings">Settings</TabsTrigger>
                    <TabsTrigger value="notifications" data-testid="spend-tab-notifications">Notifications</TabsTrigger>
                    {hasEventActions && <TabsTrigger value="actions" data-testid="spend-tab-actions">Actions</TabsTrigger>}
                  </TabsList>
                  <div className="flex-1 overflow-y-auto mt-3 pr-1">
                    <TabsContent value="settings" className="space-y-4 mt-0">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">Fleet-wide threshold ($)<AlertFieldTip topic="spend_threshold" /></Label>
                          <Input type="number" min={0}
                            value={spendConfig.threshold}
                            onChange={(e) => setSpendConfig(prev => ({ ...prev, threshold: e.target.value }))}
                            data-testid="spend-alert-threshold-input" />
                          <p className="text-[11px] text-muted-foreground">Rolling window = this month + the 2 before it. 0 = rely on overrides only.</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">Severity<AlertFieldTip topic="alert_severity" /></Label>
                          <Select value={spendConfig.severity} onValueChange={(v) => setSpendConfig(prev => ({ ...prev, severity: v }))}>
                            <SelectTrigger data-testid="spend-alert-severity-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="critical">Critical</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="rounded-md bg-muted/50 p-3 space-y-3">
                        <Label className="flex items-center gap-1">Per-vehicle overrides<AlertFieldTip topic="spend_override" /></Label>
                        {(spendConfig.overrides || []).map((o, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Select value={o.device_id || ''} onValueChange={(v) => setSpendConfig(prev => {
                              const overrides = [...prev.overrides]; overrides[i] = { ...overrides[i], device_id: v }; return { ...prev, overrides };
                            })}>
                              <SelectTrigger className="flex-1" data-testid={`spend-override-device-${i}`}><SelectValue placeholder="Pick a vehicle…" /></SelectTrigger>
                              <SelectContent>
                                {devices.filter((d) => (d.device_type || '') !== 'beacon').map((d) => (
                                  <SelectItem key={d.id} value={d.id}>{d.asset_name || d.name || d.id}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input type="number" min={0} className="w-32" placeholder="$"
                              value={o.amount}
                              onChange={(e) => setSpendConfig(prev => {
                                const overrides = [...prev.overrides]; overrides[i] = { ...overrides[i], amount: e.target.value }; return { ...prev, overrides };
                              })}
                              data-testid={`spend-override-amount-${i}`} />
                            <Button size="icon" variant="ghost" className="h-8 w-8 flex-none" data-testid={`spend-override-remove-${i}`}
                              onClick={() => setSpendConfig(prev => ({ ...prev, overrides: prev.overrides.filter((_, j) => j !== i) }))}>
                              <XCircle className="w-4 h-4 text-muted-foreground" />
                            </Button>
                          </div>
                        ))}
                        <Button size="sm" variant="outline" data-testid="spend-alert-add-override"
                          onClick={() => setSpendConfig(prev => ({ ...prev, overrides: [...(prev.overrides || []), { device_id: '', amount: '' }] }))}>
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add override
                        </Button>
                        <p className="text-[11px] text-muted-foreground">An override replaces the fleet-wide threshold for that vehicle — handy for expensive assets with naturally higher spend.</p>
                      </div>
                      <div className="flex items-center justify-between rounded-md border border-dashed p-3">
                        <p className="text-[11px] text-muted-foreground pr-3">Alerts are checked hourly (once per vehicle per calendar month while it stays over). Run an immediate check against the last saved settings — a manual run re-raises vehicles whose alert this month was already resolved, so you can test freely:</p>
                        <Button size="sm" variant="outline" onClick={runSpendCheck} data-testid="spend-alert-run-check">Run check now</Button>
                      </div>
                    </TabsContent>
                    <TabsContent value="notifications" className="mt-0" data-testid="spend-notifications-tab">
                      <NotificationsConfig
                        notifications={spendConfig.notifications}
                        onChange={(k, v) => setSpendConfig(prev => ({ ...prev, notifications: { ...prev.notifications, [k]: v } }))}
                        hasPushNotif={hasPushNotif}
                        hasEmailNotif={hasEmailNotif}
                        hasSmsNotif={hasSmsNotif}
                        pushDisabledReason={pushDisabledReason}
                        emailDisabledReason={emailDisabledReason}
                        smsDisabledReason={smsDisabledReason}
                        notifTemplates={notifTemplates}
                        onManageTemplate={openAlertTemplateManager}
                        supportsTransitionCardColorTiming={false}
                      />
                    </TabsContent>
                    {hasEventActions && (
                    <TabsContent value="actions" className="mt-0 space-y-3" data-testid="spend-actions-tab">
                      <WebhookActionConfig
                        actions={spendConfig.actions}
                        onChange={(k, v) => setSpendConfig(prev => ({ ...prev, actions: { ...prev.actions, [k]: v } }))}
                        api={api}
                        testPath="/event-rules/test-webhook"
                        webhookRuleId={spendConfig.id}
                      />
                    </TabsContent>
                    )}
                  </div>
                </Tabs>
                <DialogFooter>
                  <Button size="sm" variant="outline" onClick={() => setConfigureType(null)} data-testid="spend-config-cancel">Cancel</Button>
                  <Button size="sm" onClick={() => saveAlertConfig('maintenance_spend')} disabled={savingAlertSettings} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="spend-config-save">
                    {savingAlertSettings ? 'Saving…' : 'Save Settings'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Shared email/SMS template manager (same library as Event Rules) */}
            <NotificationTemplateModal
              open={alertTemplateModalOpen}
              onClose={() => {
                setAlertTemplateModalOpen(false);
                const returnTo = alertTemplateReturnRef.current;
                alertTemplateReturnRef.current = null;
                if (returnTo) setTimeout(() => setConfigureType(returnTo), 150);
              }}
              api={api}
              initialType={alertTemplateInitialType}
              onTemplatesChanged={fetchNotifTemplates}
            />
          </div>

          {/* Selection bar — mirrors Event Log */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="alerts-selection-bar">
            <span data-testid="alert-selection-count">{selectedAlertIds.length} selected</span>
            {selectedAlertIds.length > 0 && (<>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
                disabled={alertSelectAllLoading}
                data-testid="alert-select-all-toggle"
                onClick={async () => {
                  if (selectedAlertIds.length >= alertsTotal) { setSelectedAlertIds([]); return; }
                  try {
                    setAlertSelectAllLoading(true);
                    const params = buildAlertFilterParams();
                    params.set('ids_only', 'true');
                    const res = await api.get(`/alerts?${params}`);
                    setSelectedAlertIds(res.data?.ids || []);
                  } catch {
                    toast.error('Failed to select all rows');
                  } finally {
                    setAlertSelectAllLoading(false);
                  }
                }}
              >
                {alertSelectAllLoading ? 'Selecting…' : (selectedAlertIds.length >= alertsTotal ? 'Deselect All' : 'Select All')}
              </Button>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedAlertIds([])} data-testid="alert-selection-clear">Clear</Button>
            </>)}
          </div>

          {/* Alerts List */}
          <Card>
            <CardContent className="p-0">
              {!alertsInitialLoadDone ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : alerts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No alerts found</p>
                </div>
              ) : (
                <div>
                  {/* Page select-all header */}
                  <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
                    <Checkbox
                      checked={alerts.length > 0 && alerts.every(a => selectedAlertIds.includes(a.id))}
                      onCheckedChange={(checked) => {
                        const pageIds = alerts.map(a => a.id);
                        setSelectedAlertIds(prev => checked
                          ? Array.from(new Set([...prev, ...pageIds]))
                          : prev.filter(id => !pageIds.includes(id)));
                      }}
                      data-testid="alert-select-page"
                    />
                    <span className="text-xs text-muted-foreground">Select page</span>
                  </div>
                  <div className="divide-y">
                    {alerts.map((alert) => {
                      const Icon = alertTypeIcons[alert.alert_type] || AlertCircle;
                      return (
                        <div
                          key={alert.id}
                          className={`px-3 py-2 ${
                            alert.status === 'active' ? 'bg-red-50 dark:bg-red-950/20' :
                            alert.status === 'acknowledged' ? 'bg-amber-50 dark:bg-amber-950/20' :
                            'bg-green-50 dark:bg-green-950/20'
                          }`}
                          data-testid={`alert-row-item-${alert.id}`}
                        >
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-start gap-2.5 min-w-0 flex-1">
                              <Checkbox
                                className="mt-1 flex-shrink-0"
                                checked={selectedAlertIds.includes(alert.id)}
                                onCheckedChange={(checked) => setSelectedAlertIds(prev => checked ? [...prev, alert.id] : prev.filter(id => id !== alert.id))}
                                data-testid={`alert-select-${alert.id}`}
                              />
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                alert.severity === 'critical' || alert.severity === 'high' ? 'bg-red-100' :
                                alert.severity === 'medium' ? 'bg-amber-100' : 'bg-slate-100'
                              }`}>
                                <Icon className="w-3.5 h-3.5" />
                              </div>
                              <div className="flex-1 min-w-0 space-y-0.5">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-xs font-semibold break-words">{alert.title}</span>
                                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${severityColors[alert.severity] || ''}`}>{alert.severity}</Badge>
                                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${statusColors[alert.status] || ''}`}>{alert.status}</Badge>
                                </div>
                                <p className="text-[11px] text-muted-foreground break-words">{alert.message}</p>
                                <p className="text-[10px] text-muted-foreground break-words" data-testid={`alert-device-${alert.id}`}>
                                  {formatRecordDeviceLabel(alert, devicesById)} • {formatDT(alert.created_at, { timezone: resolveTimezone(), seconds: true })}
                                  {alert.acknowledged_at && (
                                    <> • <span className="text-amber-700 dark:text-amber-400" data-testid={`alert-acknowledged-at-${alert.id}`}>Acknowledged {formatDT(alert.acknowledged_at, { timezone: resolveTimezone(), seconds: true })}{alert.acknowledged_by_name ? ` by ${alert.acknowledged_by_name}` : ''}</span></>
                                  )}
                                  {alert.resolved_at && (
                                    <> • <span className="text-green-700 dark:text-green-400" data-testid={`alert-resolved-at-${alert.id}`}>{alert.auto_resolved ? 'Auto-resolved' : 'Resolved'} {formatDT(alert.resolved_at, { timezone: resolveTimezone(), seconds: true })}{!alert.auto_resolved && alert.resolved_by_name ? ` by ${alert.resolved_by_name}` : ''}</span></>
                                  )}
                                </p>
                                {alert.alert_type === 'document_expiry' && alert.metadata?.person_id && (
                                  <button
                                    type="button"
                                    className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                                    data-testid={`alert-open-person-${alert.id}`}
                                    onClick={() => navigate(`/people?tab=${alert.metadata.person_type === 'staff' ? 'staff' : `${alert.metadata.person_type}s`}&open_person=${alert.metadata.person_type}:${alert.metadata.person_id}`)}
                                  >
                                    <UserRound className="w-3 h-3 flex-shrink-0" />
                                    Open person
                                  </button>
                                )}
                                {alert.alert_type === 'evidence_viewed' && isSubpageEnabled('media_shared_evidence') && (
                                  <button
                                    type="button"
                                    className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                                    data-testid={`alert-open-shared-evidence-${alert.id}`}
                                    onClick={() => navigate('/media?tab=shared')}
                                  >
                                    <Eye className="w-3 h-3 flex-shrink-0" />
                                    Open Shared Evidence
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2 flex-wrap sm:flex-shrink-0">
                              {alert.status === 'active' && (
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleAcknowledge(alert.id)}>
                                  Acknowledge
                                </Button>
                              )}
                              {alert.status !== 'resolved' && (
                                <Button size="sm" variant="outline" className="h-7 text-xs text-green-600" onClick={() => handleResolve(alert.id)}>
                                  <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                  Resolve
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Pagination — unified shared footer (server-side) */}
              <PaginationFooter
                page={alertsPage}
                setPage={setAlertsPage}
                totalItems={alertsTotal}
                itemsPerPage={alertsPageSize}
                setItemsPerPage={setAlertsPageSize}
                testIdPrefix="alerts"
                zeroIndexed
                disabled={alertsLoading}
                alwaysShow
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== HISTORY TAB ==================== */}
      {activeTab === 'history' && hasHistory && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">History</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">Review and replay past trips with detailed telemetry.</p>
            </div>
          </div>

          <div className="flex flex-col gap-4 lg:flex-row" data-testid="analytics-history-mobile-responsive-workspace">
            {/* Left Sidebar - Trip Selection */}
            <div className={`${mobileHistoryView === 'list' ? 'block' : 'hidden'} w-full flex-shrink-0 space-y-3 lg:block lg:w-80`} data-testid="analytics-history-mobile-list-pane">
              {/* Device Selector & Filters */}
              <Card>
                <CardContent className="p-3 space-y-3">
                  {/* Searchable Device Selector */}
                  <div>
                    <Label className="text-xs font-medium mb-1 block">Search Asset Name / Unit Number</Label>
                    <Popover open={deviceDropdownOpen} onOpenChange={setDeviceDropdownOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-8 justify-between text-sm font-normal dark:bg-[#0D1425]" data-testid="history-device-select">
                          <span className="truncate">{selectedDeviceName || <span className="text-muted-foreground">Select Device</span>}</span>
                          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                            {historyDevice && (
                              <span
                                role="button"
                                onClick={(e) => { e.stopPropagation(); setHistoryDevice(''); }}
                                className="text-muted-foreground hover:text-foreground"
                              >
                                <X className="w-3.5 h-3.5" />
                              </span>
                            )}
                            <Search className="w-3.5 h-3.5 opacity-50" />
                          </div>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <div className="p-2 border-b">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            <Input
                              placeholder="Search by asset name / unit number..."
                              value={deviceSearch}
                              onChange={(e) => setDeviceSearch(e.target.value)}
                              className="h-8 pl-8 pr-8 text-base md:text-sm"
                              autoFocus
                              data-testid="device-picker-search"
                            />
                            {deviceSearch && (
                              <button onClick={() => setDeviceSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="device-picker-search-clear">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                        <ScrollArea className="h-[220px]">
                          {sortedFilteredDevices.length === 0 && (
                            <div className="p-4 text-center text-sm text-muted-foreground">No devices found</div>
                          )}
                          {sortedFilteredDevices.map(d => (
                            <button
                              key={d.id}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors ${historyDevice === d.id ? 'bg-[#2563eb]/10 text-[#2563eb] font-medium' : ''}`}
                              onClick={() => { setHistoryDevice(d.id); setDeviceDropdownOpen(false); setDeviceSearch(''); }}
                            >
                              {getHistoryDeviceLabel(d)}
                            </button>
                          ))}
                        </ScrollArea>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label className="text-xs font-medium mb-1 block">Search Timezone</Label>
                    <Select value={historyTimezoneMode} onValueChange={setHistoryTimezoneMode}>
                      <SelectTrigger className="h-8 dark:bg-[#172031]" data-testid="history-timezone-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="device">Device Timezone</SelectItem>
                        <SelectItem value="user">User Timezone</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className={`mt-1 text-[10px] ${historyTimezoneLabelClass}`} data-testid="history-active-timezone-label">
                      {historyTimezoneSourceLabel}: {historyTimezoneLabel}
                    </p>
                    {historyTimezoneNotice && (
                      <p className="mt-1 text-[10px] text-amber-600 dark:text-amber-400" data-testid="history-timezone-note">
                        {historyTimezoneNotice}
                      </p>
                    )}
                  </div>

                  {/* Date Presets */}
                  <div>
                    <Label className="text-xs font-medium mb-1 block">Date Range</Label>
                    <Select value={historyDatePreset} onValueChange={setHistoryDatePreset}>
                      <SelectTrigger className="h-8 dark:bg-[#172031]" data-testid="history-date-preset">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DATE_PRESETS.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Start Date & Time */}
                  <div>
                    <Label className="text-xs font-medium mb-1 block">Start Date & Time</Label>
                    <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-8 justify-between text-sm font-normal dark:bg-[#172031]">
                          {formatDT(historyDateRange.start, { timezone: activeHistoryTimezone, seconds: true })}
                          <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={historyDateRange.start}
                          onSelect={(date) => {
                            if (!date) return;
                            const prev = getZonedParts(historyDateRange.start, { timezone: activeHistoryTimezone, seconds: true });
                            const nextStart = zonedDateTimeToUtc({
                              year: date.getFullYear(),
                              month: date.getMonth() + 1,
                              day: date.getDate(),
                              hour: prev?.hour || 0,
                              minute: prev?.minute || 0,
                              second: 0,
                            }, activeHistoryTimezone);
                            setHistoryDateRange(r => ({ ...r, start: nextStart }));
                            setHistoryDatePreset('custom');
                          }}
                        />
                        <div className="border-t p-3 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                          <Input
                            type="time"
                            value={getTimeValueInTimezone(historyDateRange.start, { timezone: activeHistoryTimezone, seconds: true })}
                            onChange={(e) => {
                              const [h, m] = e.target.value.split(':').map(Number);
                              const prev = getZonedParts(historyDateRange.start, { timezone: activeHistoryTimezone, seconds: true });
                              const nextStart = zonedDateTimeToUtc({
                                year: prev?.year,
                                month: prev?.month,
                                day: prev?.day,
                                hour: h || 0,
                                minute: m || 0,
                                second: 0,
                              }, activeHistoryTimezone);
                              setHistoryDateRange(r => ({ ...r, start: nextStart }));
                              setHistoryDatePreset('custom');
                            }}
                            className="h-8 text-sm flex-1"
                          />
                          <Button size="sm" className="h-8 text-xs" onClick={() => setStartDateOpen(false)}>Done</Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* End Date & Time */}
                  <div>
                    <Label className="text-xs font-medium mb-1 block">End Date & Time</Label>
                    <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-8 justify-between text-sm font-normal dark:bg-[#172031]">
                          {formatDT(historyDateRange.end, { timezone: activeHistoryTimezone, seconds: true })}
                          <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={historyDateRange.end}
                          onSelect={(date) => {
                            if (!date) return;
                            const prev = getZonedParts(historyDateRange.end, { timezone: activeHistoryTimezone, seconds: true });
                            const nextEnd = zonedDateTimeToUtc({
                              year: date.getFullYear(),
                              month: date.getMonth() + 1,
                              day: date.getDate(),
                              hour: prev?.hour || 23,
                              minute: prev?.minute || 59,
                              second: 59,
                            }, activeHistoryTimezone);
                            setHistoryDateRange(r => ({ ...r, end: nextEnd }));
                            setHistoryDatePreset('custom');
                          }}
                        />
                        <div className="border-t p-3 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                          <Input
                            type="time"
                            value={getTimeValueInTimezone(historyDateRange.end, { timezone: activeHistoryTimezone, seconds: true })}
                            onChange={(e) => {
                              const [h, m] = e.target.value.split(':').map(Number);
                              const prev = getZonedParts(historyDateRange.end, { timezone: activeHistoryTimezone, seconds: true });
                              const nextEnd = zonedDateTimeToUtc({
                                year: prev?.year,
                                month: prev?.month,
                                day: prev?.day,
                                hour: h || 0,
                                minute: m || 0,
                                second: 59,
                              }, activeHistoryTimezone);
                              setHistoryDateRange(r => ({ ...r, end: nextEnd }));
                              setHistoryDatePreset('custom');
                            }}
                            className="h-8 text-sm flex-1"
                          />
                          <Button size="sm" className="h-8 text-xs" onClick={() => setEndDateOpen(false)}>Done</Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Split Mode */}
                  <div>
                    <Label className="text-xs font-medium mb-1 block">View Mode</Label>
                    <Select value={splitMode} onValueChange={(val) => {
                      setSplitMode(val);
                      setTrips([]);
                      setSelectedTrip(null);
                      setTimelineEvents([]);
                      setDetectedStops([]);
                      setDetectedIdles([]);
                      setHistoryEvents([]);
                      setSelectedTimelineIdx(null);
                      setHighlightedSegment(null);
                      setHighlightedStopIdx(null);
                      setHighlightedMarker(null);
                      setHighlightedEventIdx(null);
                    }}>
                      <SelectTrigger className="h-8 dark:bg-[#172031]" data-testid="history-split-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ignition">Trips - Ignition ON</SelectItem>
                        <SelectItem value="none">Whole Period</SelectItem>
                      </SelectContent>
                    </Select>
                    {splitMode === 'none' && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <Label className="text-[10px] text-muted-foreground whitespace-nowrap">Stop threshold:</Label>
                        <NumberInput
                          min={0}
                          value={Math.floor(stopThresholdSecs / 60)}
                          onValueChange={(m) => setStopThresholdSecs(Math.max(10, m * 60 + (stopThresholdSecs % 60)))}
                          className="h-7 w-24 text-xs text-center dark:bg-[#172031]"
                        />
                        <span className="text-[10px] text-muted-foreground">m</span>
                        <NumberInput
                          min={0}
                          max={59}
                          value={stopThresholdSecs % 60}
                          onValueChange={(s) => setStopThresholdSecs(Math.max(10, Math.floor(stopThresholdSecs / 60) * 60 + Math.min(59, s)))}
                          className="h-7 w-16 text-xs text-center dark:bg-[#172031]"
                        />
                        <span className="text-[10px] text-muted-foreground">s</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 mt-2">
                      <Label className="text-[10px] text-muted-foreground whitespace-nowrap">Idle threshold:</Label>
                      <NumberInput
                        min={0}
                        value={Math.floor(idleThresholdSecs / 60)}
                        onValueChange={(m) => setIdleThresholdSecs(Math.max(10, m * 60 + (idleThresholdSecs % 60)))}
                        className="h-7 w-24 text-xs text-center dark:bg-[#172031]"
                      />
                      <span className="text-[10px] text-muted-foreground">m</span>
                      <NumberInput
                        min={0}
                        max={59}
                        value={idleThresholdSecs % 60}
                        onValueChange={(s) => setIdleThresholdSecs(Math.max(10, Math.floor(idleThresholdSecs / 60) * 60 + Math.min(59, s)))}
                        className="h-7 w-16 text-xs text-center dark:bg-[#172031]"
                      />
                      <span className="text-[10px] text-muted-foreground">s</span>
                    </div>
                  </div>

                  {/* Search + Clear Map */}
                  <div className="flex items-center gap-2">
                    <Button size="sm"
                      className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                      onClick={fetchTrips}
                      disabled={!historyDevice || historyLoading}
                      data-testid="history-search-btn"
                    >
                      {historyLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                      Search
                    </Button>
                    <Button size="sm"
                      variant="outline"

                      onClick={clearHistoryMap}
                      disabled={historyLoading || (trips.length === 0 && historyEvents.length === 0)}
                      data-testid="history-clear-map-btn"
                      title="Clear routes and events from the map"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Clear Map
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Trip List / Timeline Panel */}
              <Card>
                <CardHeader className="pb-1 px-3 pt-3">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    <span>{splitMode === 'none' ? 'Timeline' : 'Trips'}</span>
                    {historyLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                    {!historyLoading && splitMode === 'ignition' && <Badge variant="outline" className="text-[10px]">{trips.length}</Badge>}
                    {!historyLoading && splitMode === 'none' && <Badge variant="outline" className="text-[10px]" data-testid="timeline-events-count">{historyEvents.length} events</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[640px]">
                    {!historyDevice && (
                      <div className="p-6 text-center text-sm text-muted-foreground">Select a device to view history</div>
                    )}
                    {historyDevice && !historyLoading && trips.length === 0 && (
                      <div className="p-6 text-center text-sm text-muted-foreground">
                        {splitMode === 'ignition' ? 'No ignition trips found' : 'No data for this period'}
                      </div>
                    )}

                    {/* Ignition ON mode — Trip list with engine idle sub-items */}
                    {splitMode === 'ignition' && (
                      <div className="divide-y">
                        {trips.map((trip, tripIdx) => {
                          const currDayKey = getTimelineDayKey(trip.start_time, activeHistoryTimezone);
                          const prevDayKey = tripIdx > 0
                            ? getTimelineDayKey(trips[tripIdx - 1].start_time, activeHistoryTimezone)
                            : null;
                          const showDayHeader = !!currDayKey && currDayKey !== prevDayKey;
                          return (
                          <React.Fragment key={trip.trip_id}>
                            {showDayHeader && (
                              <div
                                className="sticky top-0 z-10 bg-background/95 backdrop-blur border-y border-slate-200 dark:border-slate-700 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300"
                                data-testid={`trip-day-header-${currDayKey}`}
                              >
                                {formatTimelineDayHeader(trip.start_time, activeHistoryTimezone)}
                              </div>
                            )}
                          <div>
                            <div
                              className={`p-3 cursor-pointer hover:bg-muted/50 transition-colors flex items-start gap-2.5 ${
                                selectedTrip?.trip_id === trip.trip_id ? 'bg-[#2563eb]/10 border-l-2 border-l-[#2563eb]' : ''
                              }`}
                              onClick={() => handleSelectTrip(trip)}
                              data-testid={`trip-item-${trip.trip_index}`}
                            >
                              <div className="flex-shrink-0 mt-0.5" style={{ width: 22, height: 22 }}>
                                <svg width="22" height="22" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg"><g><path fill="#9ca3af" d="M275.121,393.164c-5.771-10.938-1.521-24.918,9.718-30.692c10.935-5.776,24.907-1.521,30.679,9.724v0.306c5.77,10.939,1.52,24.92-9.418,30.696c-11.24,5.774-24.909,1.52-30.984-9.726v-0.308H275.121z"/><path fill="#9ca3af" d="M273.034,327.438c-12.454,0.308-22.78-9.417-23.388-22.183v-0.303c-0.306-12.458,9.719-23.097,22.479-23.397c12.454-0.306,22.78,9.726,23.087,22.185v0.307C295.514,316.806,285.795,327.138,273.034,327.438z"/><path fill="#9ca3af" d="M251.517,123.416c12.761-0.606,23.087,9.418,23.693,21.878v0.306c0.306,12.766-9.72,23.097-22.173,23.398c-12.455,0.607-23.087-9.418-23.388-21.877v-0.307C229.343,134.355,239.062,123.722,251.517,123.416z"/><path fill="#9ca3af" d="M222.497,76.388c5.771,11.245,1.521,24.918-9.418,30.995c-11.239,5.776-24.908,1.521-30.984-9.418v-0.307c-6.076-11.245-1.821-24.918,9.418-30.995c10.933-5.776,24.907-1.521,30.677,9.418L222.497,76.388z"/><path fill="#16a34a" d="M149.963,106.436c11.926,11.925,11.926,31.669,0,43.603s-31.655,11.927-43.582,0l-26.724-26.739l-27.132,26.739c-11.926,11.927-31.248,11.927-43.582,0c-11.926-11.925-11.926-31.67,0-43.603l27.132-26.74L8.944,52.548c-11.926-11.927-11.926-31.67,0-43.604c12.333-11.926,31.655-11.926,43.582,0l27.132,27.147l26.724-27.147c11.926-11.926,31.655-11.926,43.582,0c11.926,11.927,11.926,31.67,0,43.604l-26.725,27.147L149.963,106.436z"/><path fill="#dc2626" d="M492.327,446.849c11.927,11.926,11.927,31.669,0,43.604c-11.926,11.934-31.654,11.926-43.581,0l-26.724-26.74l-27.133,26.74c-11.926,11.926-31.248,11.926-43.581,0c-11.926-11.926-11.926-31.67,0-43.604l27.132-26.74l-27.132-27.146c-11.926-11.928-11.926-31.67,0-43.604c12.333-11.926,31.655-11.926,43.581,0l27.133,27.147l26.724-27.147c11.927-11.926,31.655-11.926,43.581,0c11.927,11.927,11.927,31.67,0,43.604l-26.725,27.146L492.327,446.849z"/><path fill="#9ca3af" d="M261.517,201.739c12.761-0.607,23.087,9.418,23.693,21.877v0.307c0.306,12.765-9.72,23.097-22.173,23.398c-12.455,0.607-23.087-9.418-23.388-21.876v-0.307C239.343,212.678,249.062,202.045,261.517,201.739z"/></g></svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-xs text-slate-700 dark:text-slate-300">
                                    {formatTimeOnly(trip.start_time, { timezone: activeHistoryTimezone, seconds: true })} — {formatTimeOnly(trip.end_time, { timezone: activeHistoryTimezone, seconds: true })}
                                  </span>
                                  <Badge variant="outline" className="text-[10px]">{formatDistance(trip.distance_km)} | {formatDurationExact(trip.duration_secs)}</Badge>
                                </div>
                                <div className="text-[10px] text-slate-500 dark:text-slate-400 flex gap-2 flex-wrap">
                                  <span>Avg {formatSpeed(trip.avg_speed)} | Max {formatSpeed(trip.max_speed)}</span>
                                  <span>{trip.idle_count} idles</span>
                                  {trip.event_count > 0 && <span className="text-[#7F00FF] dark:text-purple-300">{trip.event_count} events</span>}
                                  <span>{trip.point_count} pts</span>
                                </div>
                              </div>
                            </div>
                            {selectedTrip?.trip_id === trip.trip_id && trip.children?.length > 0 && (
                              <div className="pl-8 pr-3 pb-2 space-y-0.5">
                                {trip.children.map((child, childIdx) => {
                                  if (child.kind === 'idle') {
                                    const visibleIdx = visibleNonMovingSegments.findIndex(seg =>
                                      seg.state === child.state && seg.start_time === child.start_time && seg.end_time === child.end_time
                                    );
                                    const isStopHighlighted = highlightedStopIdx === visibleIdx;
                                    const idleMeta = stopStateMeta[child.state] || stopStateMeta.idle;
                                    return (
                                      <div
                                        key={`trip-child-${childIdx}`}
                                        className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition-colors ${
                                          isStopHighlighted ? 'bg-[#2563eb]/15' : 'hover:bg-muted/40'
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (visibleIdx >= 0) setHighlightedStopIdx(visibleIdx);
                                          setSelectedTrip(trip);
                                          setHighlightedMarker(null);
                                          if (historyMapType === 'osm' && historyLeafletMapRef.current) {
                                            historyLeafletMapRef.current.panTo([child.lat, child.lng]);
                                          } else if (historyMapRef.current) {
                                            historyMapRef.current.panTo({ lat: child.lat, lng: child.lng });
                                          }
                                          setMapBubble({ type: 'stop', lat: child.lat, lng: child.lng, data: child });
                                        }}
                                        data-testid={`trip-child-${trip.trip_index}-${childIdx}`}
                                      >
                                        <div className="flex-shrink-0" style={{ width: 16, height: 16 }}>
                                          <HistoryStopIcon state={child.state} size={16} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-[10px]"><span className={`${idleMeta.textClass} font-semibold`}>{idleMeta.label}</span> <span className="text-slate-500 dark:text-slate-400">— {formatDurationExact(child.duration_secs)}</span></p>
                                          <p className="text-[9px] text-slate-500 dark:text-slate-400">{formatTimeOnly(child.start_time, { timezone: activeHistoryTimezone, seconds: true })} — {formatTimeOnly(child.end_time, { timezone: activeHistoryTimezone, seconds: true })}</p>
                                        </div>
                                      </div>
                                    );
                                  }
                                  // event child
                                  const evIdx = (trip.events || []).findIndex(e => e.id === child.id);
                                  const isEvHighlighted = highlightedEventIdx === evIdx;
                                  return (
                                    <div
                                      key={`trip-child-${childIdx}`}
                                      className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition-colors ${
                                        isEvHighlighted ? 'bg-purple-500/15' : 'hover:bg-muted/40'
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTrip(trip);
                                        if (evIdx >= 0) setHighlightedEventIdx(evIdx);
                                        setHighlightedStopIdx(null);
                                        setHighlightedMarker(null);
                                        if (child.lat && child.lng) {
                                          if (historyMapType === 'osm' && historyLeafletMapRef.current) {
                                            historyLeafletMapRef.current.panTo([child.lat, child.lng]);
                                          } else if (historyMapRef.current) {
                                            historyMapRef.current.panTo({ lat: child.lat, lng: child.lng });
                                          }
                                          setMapBubble({ type: 'event', lat: child.lat, lng: child.lng, data: child });
                                        }
                                      }}
                                      data-testid={`trip-child-${trip.trip_index}-${childIdx}`}
                                      data-event-id={child.id}
                                    >
                                      <div className="flex-shrink-0" style={{ width: 16, height: 16 }}>
                                        <svg width="16" height="16" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="#7F00FF" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.51-125.673c11.637-9.31,20.943-23.272,20.943-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M189.555,314.22V116.897h146.309v33.381H229.396v43.744h99.064v33.246h-99.064v53.705h110.236v33.246H189.555z"/></svg>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-[10px]">
                                          <span className="text-[#7F00FF] dark:text-purple-300 font-semibold">{child.event_rule_name}</span>
                                          {child.speed_kmh != null && <span className="text-red-600 dark:text-red-400"> — {formatSpeed(child.speed_kmh)}</span>}
                                        </p>
                                        <p className="text-[9px] text-slate-500 dark:text-slate-400">{formatTimeOnly(child.triggered_at, { timezone: activeHistoryTimezone, seconds: true })}{child.duration_secs != null && !(child.metadata?.instant_event || (child.duration_secs === 0 && child.status === 'cleared')) ? ` — ${formatDurationExact(child.duration_secs)}` : ''}</p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          </React.Fragment>
                          );
                        })}
                      </div>
                    )}

                    {/* Whole Period mode — Timeline event list */}
                    {splitMode === 'none' && timelineEvents.length > 0 && (
                      <div className="py-1">
                        {timelineEvents.map((evt, idx) => {
                          const isSelected = selectedTimelineIdx === idx;
                          const stopMeta = stopStateMeta[evt.state] || stopStateMeta.stopped;
                          const stopValueClass = evt.state === 'idle'
                            ? 'text-[#d97706] dark:text-[#f1a809]'
                            : 'text-[#6b7280] dark:text-[#d1d5db]';
                          const currTs = getTimelineEventPrimaryTs(evt);
                          const currDayKey = getTimelineDayKey(currTs, activeHistoryTimezone);
                          const prevDayKey = idx > 0
                            ? getTimelineDayKey(getTimelineEventPrimaryTs(timelineEvents[idx - 1]), activeHistoryTimezone)
                            : null;
                          const showDayHeader = !!currDayKey && currDayKey !== prevDayKey;
                          return (
                            <React.Fragment key={idx}>
                              {showDayHeader && (
                                <div
                                  className="sticky top-0 z-10 bg-background/95 backdrop-blur border-y border-slate-200 dark:border-slate-700 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300"
                                  data-testid={`timeline-day-header-${currDayKey}`}
                                >
                                  {formatTimelineDayHeader(currTs, activeHistoryTimezone)}
                                </div>
                              )}
                              <div className="flex items-stretch px-3">
                              {/* Vertical timeline line + icon */}
                              <div className="flex flex-col items-center mr-2.5 flex-shrink-0" style={{ width: 28 }}>
                                {idx > 0 && <div className="w-0.5 flex-1 bg-slate-300 dark:bg-slate-600" />}
                                {/* Icon — pin SVG matching map markers */}
                                <div className="flex-shrink-0" style={{ width: 22, height: 22 }}>
                                  {evt.type === 'start' && (
                                    <svg width="22" height="22" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="#16a34a" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.218c0,16.291,6.982,30.254,20.945,39.563L257.224,512l188.509-125.673c11.637-9.31,20.945-23.272,20.945-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M356.491,195.132l-157.37-108.53c-1.666-1.145-3.826-1.264-5.605-0.336c-1.785,0.939-2.903,2.783-2.903,4.802v217.061c0,2.018,1.118,3.868,2.903,4.809c0.792,0.412,1.66,0.618,2.523,0.618c1.08,0,2.155-0.326,3.083-0.961l157.37-108.53c1.471-1.015,2.344-2.681,2.344-4.466S357.956,196.147,356.491,195.132z"/></svg>
                                  )}
                                  {evt.type === 'end' && (
                                    <svg width="22" height="22" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="#dc2626" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.218c0,16.291,6.982,30.254,20.945,39.563L257.224,512l188.509-125.673c11.637-9.31,20.945-23.272,20.945-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M334.743,122.647H179.705c-2.518,0-4.561,2.038-4.561,4.56v155.039c0,2.521,2.043,4.561,4.561,4.561h155.038c2.519,0,4.561-2.039,4.561-4.561V127.207C339.304,124.685,337.262,122.647,334.743,122.647z"/></svg>
                                  )}
                                  {evt.type === 'stop' && (
                                    <HistoryStopIcon state={evt.state} size={22} />
                                  )}
                                  {evt.type === 'drive' && (
                                    <svg width="22" height="22" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg"><g><path fill="#9ca3af" d="M275.121,393.164c-5.771-10.938-1.521-24.918,9.718-30.692c10.935-5.776,24.907-1.521,30.679,9.724v0.306c5.77,10.939,1.52,24.92-9.418,30.696c-11.24,5.774-24.909,1.52-30.984-9.726v-0.308H275.121z"/><path fill="#9ca3af" d="M273.034,327.438c-12.454,0.308-22.78-9.417-23.388-22.183v-0.303c-0.306-12.458,9.719-23.097,22.479-23.397c12.454-0.306,22.78,9.726,23.087,22.185v0.307C295.514,316.806,285.795,327.138,273.034,327.438z"/><path fill="#9ca3af" d="M251.517,123.416c12.761-0.606,23.087,9.418,23.693,21.878v0.306c0.306,12.766-9.72,23.097-22.173,23.398c-12.455,0.607-23.087-9.418-23.388-21.877v-0.307C229.343,134.355,239.062,123.722,251.517,123.416z"/><path fill="#9ca3af" d="M222.497,76.388c5.771,11.245,1.521,24.918-9.418,30.995c-11.239,5.776-24.908,1.521-30.984-9.418v-0.307c-6.076-11.245-1.821-24.918,9.418-30.995c10.933-5.776,24.907-1.521,30.677,9.418L222.497,76.388z"/><path fill="#16a34a" d="M149.963,106.436c11.926,11.925,11.926,31.669,0,43.603s-31.655,11.927-43.582,0l-26.724-26.739l-27.132,26.739c-11.926,11.927-31.248,11.927-43.582,0c-11.926-11.925-11.926-31.67,0-43.603l27.132-26.74L8.944,52.548c-11.926-11.927-11.926-31.67,0-43.604c12.333-11.926,31.655-11.926,43.582,0l27.132,27.147l26.724-27.147c11.926-11.926,31.655-11.926,43.582,0c11.926,11.927,11.926,31.67,0,43.604l-26.725,27.147L149.963,106.436z"/><path fill="#dc2626" d="M492.327,446.849c11.927,11.926,11.927,31.669,0,43.604c-11.926,11.934-31.654,11.926-43.581,0l-26.724-26.74l-27.133,26.74c-11.926,11.926-31.248,11.926-43.581,0c-11.926-11.926-11.926-31.67,0-43.604l27.132-26.74l-27.132-27.146c-11.926-11.928-11.926-31.67,0-43.604c12.333-11.926,31.655-11.926,43.581,0l27.133,27.147l26.724-27.147c11.927-11.926,31.655-11.926,43.581,0c11.927,11.927,11.927,31.67,0,43.604l-26.725,27.146L492.327,446.849z"/><path fill="#9ca3af" d="M261.517,201.739c12.761-0.607,23.087,9.418,23.693,21.877v0.307c0.306,12.765-9.72,23.097-22.173,23.398c-12.455,0.607-23.087-9.418-23.388-21.876v-0.307C239.343,212.678,249.062,202.045,261.517,201.739z"/></g></svg>
                                  )}
                                  {evt.type === 'event' && (
                                    <svg width="22" height="22" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="#7F00FF" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.51-125.673c11.637-9.31,20.943-23.272,20.943-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M189.555,314.22V116.897h146.309v33.381H229.396v43.744h99.064v33.246h-99.064v53.705h110.236v33.246H189.555z"/></svg>
                                  )}
                                </div>
                                {idx < timelineEvents.length - 1 && <div className="w-0.5 flex-1 bg-slate-300 dark:bg-slate-600" />}
                              </div>
                              {/* Content */}
                              <div
                                className={`flex-1 py-1.5 cursor-pointer rounded-md px-2 my-0.5 transition-colors ${
                                  isSelected ? 'bg-[#2563eb]/10' : 'hover:bg-muted/50'
                                }`}
                                onClick={() => handleTimelineClick(evt, idx)}
                                data-testid={`timeline-event-${idx}`}
                              >
                                {evt.type === 'start' && (
                                  <div>
                                    <p className="text-xs font-semibold" style={{ color: '#16a34a' }}>Start</p>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400">{formatTimeOnly(evt.timestamp, { timezone: activeHistoryTimezone, seconds: true })}</p>
                                  </div>
                                )}
                                {evt.type === 'end' && (
                                  <div>
                                    <p className="text-xs font-semibold" style={{ color: '#dc2626' }}>End</p>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400">{formatTimeOnly(evt.timestamp, { timezone: activeHistoryTimezone, seconds: true })}</p>
                                  </div>
                                )}
                                {evt.type === 'stop' && (
                                  <div>
                                    <p className="text-xs font-semibold">
                                      <span className={stopMeta.textClass}>{stopMeta.label}</span>
                                      <span className={stopMeta.textClass}> — </span>
                                      <span className={stopValueClass}>{formatDurationExact(evt.duration_secs)}</span>
                                    </p>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400">{formatTimeOnly(evt.start_time, { timezone: activeHistoryTimezone, seconds: true })} — {formatTimeOnly(evt.end_time, { timezone: activeHistoryTimezone, seconds: true })}</p>
                                  </div>
                                )}
                                {evt.type === 'drive' && (
                                  <div>
                                    <p className="text-xs font-semibold"><span className="text-green-600 dark:text-green-400">Drive</span><span className="text-green-600 dark:text-green-400"> — </span><span className="text-blue-700 dark:text-blue-400">{formatDistance(evt.distance_km)} | {formatDurationExact(evt.duration_secs)} | Max {formatSpeed(evt.max_speed)}</span></p>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                      {formatTimeOnly(evt.start_time, { timezone: activeHistoryTimezone, seconds: true })} — {formatTimeOnly(evt.end_time, { timezone: activeHistoryTimezone, seconds: true })} | Avg {formatSpeed(evt.avg_speed)}
                                    </p>
                                    {evt.children?.length > 0 && (
                                      <div className="mt-1.5 pl-4 space-y-0.5 border-l border-slate-200 dark:border-slate-700">
                                        {evt.children.map((child, childIdx) => {
                                          if (child.kind === 'idle') {
                                            const idleMeta = stopStateMeta[child.state] || stopStateMeta.idle;
                                            const visibleIdx = visibleNonMovingSegments.findIndex(seg =>
                                              seg.state === child.state && seg.start_time === child.start_time && seg.end_time === child.end_time
                                            );
                                            const isIdleHighlighted = highlightedStopIdx === visibleIdx;
                                            return (
                                              <div
                                                key={`timeline-drive-child-${childIdx}`}
                                                className={`flex items-center gap-2 py-1 px-2 rounded transition-colors ${isIdleHighlighted ? 'bg-[#2563eb]/15' : 'hover:bg-muted/40'}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setSelectedTimelineIdx(idx);
                                                  if (evt.point_start_idx != null && evt.point_end_idx != null) {
                                                    setHighlightedSegment({ start: evt.point_start_idx, end: evt.point_end_idx });
                                                  }
                                                  if (visibleIdx >= 0) setHighlightedStopIdx(visibleIdx);
                                                  setHighlightedMarker(null);
                                                  if (historyMapType === 'osm' && historyLeafletMapRef.current) {
                                                    historyLeafletMapRef.current.panTo([child.lat, child.lng]);
                                                  } else if (historyMapRef.current) {
                                                    historyMapRef.current.panTo({ lat: child.lat, lng: child.lng });
                                                  }
                                                  setMapBubble({ type: 'stop', lat: child.lat, lng: child.lng, data: child });
                                                }}
                                                data-testid={`timeline-drive-child-${idx}-${childIdx}`}
                                              >
                                                <div className="flex-shrink-0" style={{ width: 16, height: 16 }}>
                                                  <HistoryStopIcon state={child.state} size={16} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <p className="text-[10px]"><span className={`${idleMeta.textClass} font-semibold`}>{idleMeta.label}</span> <span className="text-slate-500 dark:text-slate-400">— {formatDurationExact(child.duration_secs)}</span></p>
                                                  <p className="text-[9px] text-slate-500 dark:text-slate-400">{formatTimeOnly(child.start_time, { timezone: activeHistoryTimezone, seconds: true })} — {formatTimeOnly(child.end_time, { timezone: activeHistoryTimezone, seconds: true })}</p>
                                                </div>
                                              </div>
                                            );
                                          }
                                          // event child
                                          const eventsArr = selectedTrip?.events || historyEvents || [];
                                          const globalEvIdx = eventsArr.findIndex(e => e.id === child.id);
                                          const isEvHighlighted = highlightedEventIdx === globalEvIdx;
                                          return (
                                            <div
                                              key={`timeline-drive-child-${childIdx}`}
                                              className={`flex items-center gap-2 py-1 px-2 rounded transition-colors ${isEvHighlighted ? 'bg-purple-500/15' : 'hover:bg-muted/40'}`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedTimelineIdx(idx);
                                                if (evt.point_start_idx != null && evt.point_end_idx != null) {
                                                  setHighlightedSegment({ start: evt.point_start_idx, end: evt.point_end_idx });
                                                }
                                                if (globalEvIdx >= 0) setHighlightedEventIdx(globalEvIdx);
                                                setHighlightedStopIdx(null);
                                                setHighlightedMarker(null);
                                                if (child.lat && child.lng) {
                                                  if (historyMapType === 'osm' && historyLeafletMapRef.current) {
                                                    historyLeafletMapRef.current.panTo([child.lat, child.lng]);
                                                  } else if (historyMapRef.current) {
                                                    historyMapRef.current.panTo({ lat: child.lat, lng: child.lng });
                                                  }
                                                  setMapBubble({ type: 'event', lat: child.lat, lng: child.lng, data: child });
                                                }
                                              }}
                                              data-testid={`timeline-drive-child-${idx}-${childIdx}`}
                                              data-event-id={child.id}
                                            >
                                              <div className="flex-shrink-0" style={{ width: 16, height: 16 }}>
                                                <svg width="16" height="16" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="#7F00FF" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.51-125.673c11.637-9.31,20.943-23.272,20.943-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M189.555,314.22V116.897h146.309v33.381H229.396v43.744h99.064v33.246h-99.064v53.705h110.236v33.246H189.555z"/></svg>
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <p className="text-[10px]">
                                                  <span className="text-[#7F00FF] dark:text-purple-300 font-semibold">{child.event_rule_name}</span>
                                                  {child.speed_kmh != null && <span className="text-red-600 dark:text-red-400"> — {formatSpeed(child.speed_kmh)}</span>}
                                                </p>
                                                <p className="text-[9px] text-slate-500 dark:text-slate-400">{formatTimeOnly(child.triggered_at, { timezone: activeHistoryTimezone, seconds: true })}{child.duration_secs != null && !(child.metadata?.instant_event || (child.duration_secs === 0 && child.status === 'cleared')) ? ` — ${formatDurationExact(child.duration_secs)}` : ''}</p>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {evt.type === 'event' && evt.event && (
                                  <div>
                                    <p className="text-xs font-semibold">
                                      <span className="text-[#7F00FF] dark:text-purple-300">{evt.event.event_rule_name}</span>
                                      {evt.event.severity && <span className={`ml-1.5 text-[9px] px-1 py-0 rounded ${evt.event.severity === 'critical' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' : evt.event.severity === 'warning' ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-slate-100 text-slate-600'}`}>{evt.event.severity}</span>}
                                    </p>
                                    {evt.event.speed_kmh != null && (
                                      <p className="text-[10px] text-red-600 dark:text-red-400">{formatSpeed(evt.event.speed_kmh)}{evt.event.threshold_kmh ? ` (Limit: ${formatSpeed(evt.event.threshold_kmh)})` : ''}</p>
                                    )}
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                      {formatTimeOnly(evt.event.triggered_at, { timezone: activeHistoryTimezone, seconds: true })}
                                      {evt.event.duration_secs != null && !(evt.event.metadata?.instant_event || (evt.event.duration_secs === 0 && evt.event.status === 'cleared')) ? ` — ${evt.event.duration_secs >= 60 ? `${Math.floor(evt.event.duration_secs / 60)}m ${Math.round(evt.event.duration_secs % 60)}s` : `${Math.round(evt.event.duration_secs)}s`}` : ''}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Right Content - Map & Stats */}
            <div className={`${mobileHistoryView === 'map' ? 'block' : 'hidden'} min-w-0 flex-1 space-y-4 lg:block`} data-testid="analytics-history-mobile-map-pane">
              {/* Map */}
              <Card className="overflow-hidden">
                <div className="h-[calc(100vh-19rem)] min-h-[30rem] lg:h-[600px] bg-slate-100 dark:bg-slate-800 relative overflow-hidden isolate" data-testid="history-map-container">
                  {/* Map Provider Toggle - TOP CENTER
                      Logic: 
                      - OSM ❌ + Google ❌ = Show OSM only (fallback, no toggle needed)
                      - OSM ✅ + Google ❌ = Show OSM only (no toggle needed)
                      - OSM ✅ + Google ✅ = Show both buttons (toggle visible)
                      - OSM ❌ + Google ✅ = Show Google only (no toggle needed)
                  */}
                  {hasOSMMaps && hasGoogleMaps && (
                    <div className="absolute top-[3.75rem] left-3 right-3 z-[500] sm:top-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2">
                      <div className="flex justify-center gap-2">
                        {/* OSM Button */}
                        <button
                          onClick={() => setHistoryMapType('osm')}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-lg ${
                            historyMapType === 'osm'
                              ? 'bg-[#2563eb] text-white'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-[#2563eb] hover:text-white'
                          }`}
                        >
                          OSM
                        </button>
                        {/* Google Button */}
                        <button
                          onClick={() => setHistoryMapType('roadmap')}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-lg ${
                            historyMapType !== 'osm'
                              ? 'bg-[#2563eb] text-white'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-[#2563eb] hover:text-white'
                          }`}
                        >
                          Google
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Map Type Controls - TOP LEFT (icon-only) - ONLY FOR GOOGLE MAPS */}
                  {hasGoogleMaps && historyMapType !== 'osm' && (
                    <div className="absolute top-[3.75rem] left-3 z-[500] flex gap-1 sm:top-4 sm:left-4">
                      <button
                        onClick={() => setHistoryMapType('roadmap')}
                        className={`w-9 h-9 rounded-md transition-all flex items-center justify-center ${
                          historyMapType === 'roadmap'
                            ? 'bg-[#2563eb] text-white shadow-lg'
                            : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg'
                        }`}
                        title="Street Map"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
                          <line x1="9" y1="3" x2="9" y2="18" />
                          <line x1="15" y1="6" x2="15" y2="21" />
                        </svg>
                      </button>

                      {hasSatelliteView && (
                        <button
                          onClick={() => setHistoryMapType('satellite')}
                          className={`w-9 h-9 rounded-md transition-all flex items-center justify-center ${
                            historyMapType === 'satellite'
                              ? 'bg-[#2563eb] text-white shadow-lg'
                              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg'
                          }`}
                          title="Satellite View"
                        >
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16.201 7.79899C17.8024 9.40034 20.3987 9.40034 22 7.79899L16.201 2C14.5997 3.60135 14.5997 6.19764 16.201 7.79899Z" />
                            <path d="M16 8L14.5 9.5" />
                            <path d="M14.8322 13.001C15.4344 12.3988 15.7354 12.0977 15.7354 11.7236C15.7354 11.3494 15.4344 11.0484 14.8322 10.4462L13.5548 9.16879C12.9526 8.56662 12.6515 8.26553 12.2774 8.26553C11.9033 8.26553 11.6022 8.56662 11 9.16879L6.79367 13.3751C5.73544 14.4334 5.73544 16.1491 6.79367 17.2073C7.8519 18.2656 9.56763 18.2656 10.6259 17.2073L14.8322 13.001Z" />
                            <path d="M15.4688 16.8563L16.8563 15.4688C17.5104 14.8147 17.8374 14.4877 18.2438 14.4877C18.6502 14.4877 18.9773 14.8147 19.6314 15.4688L21.0189 16.8563C21.673 17.5104 22 17.8374 22 18.2438C22 18.6502 21.673 18.9773 21.0189 19.6314L19.6314 21.0189C18.9773 21.673 18.6502 22 18.2438 22C17.8374 22 17.5104 21.673 16.8563 21.0189L15.4688 19.6314C14.8147 18.9773 14.4877 18.6502 14.4877 18.2438C14.4877 17.8374 14.8147 17.5104 15.4688 16.8563Z" />
                            <path d="M2.98112 4.36864L4.36864 2.98112C5.02273 2.32704 5.34977 2 5.75616 2C6.16256 2 6.4896 2.32704 7.14368 2.98112L8.5312 4.36864C9.18528 5.02273 9.51233 5.34977 9.51233 5.75616C9.51233 6.16256 9.18528 6.4896 8.5312 7.14368L7.14368 8.5312C6.4896 9.18528 6.16256 9.51233 5.75616 9.51233C5.34977 9.51233 5.02273 9.18528 4.36864 8.5312L2.98112 7.14368C2.32704 6.4896 2 6.16256 2 5.75616C2 5.34977 2.32704 5.02273 2.98112 4.36864Z" />
                            <path d="M16 16L14 14M9.99999 10L8 8" />
                            <path d="m 3.6955907,16.837439 c 0.11884,1.5706 1.56973,3.0084 3.1517,3.1305 m -5.811874,-3.06153 c -0.15384005,3.0281 2.74794,5.9038 5.79835,5.7565" />
                          </svg>
                        </button>
                      )}

                      {hasTerrainView && (historyMapType === 'roadmap' || historyMapType === 'terrain') && (
                        <button
                          onClick={() => setHistoryMapType('terrain')}
                          className={`w-9 h-9 rounded-md transition-all flex items-center justify-center ${
                            historyMapType === 'terrain'
                              ? 'bg-[#2563eb] text-white shadow-lg'
                              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg'
                          }`}
                          title="Terrain View"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
                          </svg>
                        </button>
                      )}

                      {(historyMapType === 'satellite' || historyMapType === 'hybrid') && (
                        <button
                          onClick={() => setHistoryMapType('hybrid')}
                          className={`w-9 h-9 rounded-md transition-all flex items-center justify-center ${
                            historyMapType === 'hybrid'
                              ? 'bg-[#2563eb] text-white shadow-lg'
                              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg'
                          }`}
                          title="Hybrid View"
                        >
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
                            <path d="M18.8295 14.2542C19.1276 13.9312 19.2766 13.7698 19.435 13.6756C19.8171 13.4483 20.2876 13.4412 20.6761 13.6569C20.8372 13.7463 20.9908 13.9032 21.298 14.2171C21.6053 14.531 21.7589 14.6879 21.8464 14.8524C22.0575 15.2492 22.0506 15.7299 21.8281 16.1203C21.7359 16.2821 21.5779 16.4343 21.2617 16.7388L17.5003 20.3617C16.9012 20.9387 16.6016 21.2273 16.2272 21.3735C15.8528 21.5197 15.4413 21.509 14.6181 21.4874L14.5062 21.4845C14.2556 21.478 14.1303 21.4747 14.0574 21.392C13.9846 21.3094 13.9945 21.1817 14.0144 20.9265L14.0252 20.7879C14.0812 20.0694 14.1092 19.7102 14.2495 19.3873C14.3898 19.0644 14.6318 18.8022 15.1158 18.2778L18.8295 14.2542Z" />
                            <path d="M22 10.5V9.71749C22 7.77787 22 6.80807 21.4142 6.2055C20.8284 5.60294 19.8856 5.60294 18 5.60294H15.9214C15.004 5.60294 14.9964 5.10116 14.1715 5.18834L10.8399 3.52114C9.44884 2.82504 8.75332 2.47699 8.01238 2.50118C7.27143 2.52537 6.59877 2.91808 5.25345 3.70351L4.02558 4.42037C3.03739 4.99729 2.54329 5.28576 2.27164 5.76564C2 6.24553 2 6.32993 2 7.99873V16.2157C2 17.7514 2 18.5193 2.34226 18.9467C2.57001 19.231 2.88916 19.4222 3.242 19.4856C3.77226 19.5808 4.42148 19.2018 5.71987 18.4437C6.60156 17.929 7.45011 17.3944 8.50487 17.5394C9.38869 17.6608 10.21 18.2185 11 18.6138" />
                            <path d="M8 2.5L8 17.5" />
                            <path d="M15 5.5V13.5" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Custom Zoom Controls - TOP LEFT (below map type) */}
                  <div className="absolute top-[6.5rem] left-3 z-[500] flex flex-col gap-2 sm:top-[80px] sm:left-4">
                    <button
                      onClick={handleHistoryZoomIn}
                      className="w-9 h-9 bg-white dark:bg-slate-800 rounded-md shadow-lg hover:shadow-xl hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-center transition-all border border-slate-300 dark:border-slate-600 cursor-pointer"
                      title="Zoom in"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                        <line x1="11" y1="8" x2="11" y2="14" />
                        <line x1="8" y1="11" x2="14" y2="11" />
                      </svg>
                    </button>
                    <button
                      onClick={handleHistoryZoomOut}
                      className="w-9 h-9 bg-white dark:bg-slate-800 rounded-md shadow-lg hover:shadow-xl hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-center transition-all border border-slate-300 dark:border-slate-600 cursor-pointer"
                      title="Zoom out"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                        <line x1="8" y1="11" x2="14" y2="11" />
                      </svg>
                    </button>
                  </div>

                  {/* Middle Left Feature Toggles */}
                  {hasGoogleMaps && historyMapType !== 'osm' && (
                    <div className="absolute top-1/2 -translate-y-1/2 left-4 z-[500] flex flex-col gap-2">
                      {/* POI Labels Toggle - Hide for Satellite */}
                      {historyMapType !== 'satellite' && (
                        <button
                          onClick={handleHistoryTogglePOI}
                          className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center ${
                            historyShowPOI
                              ? 'bg-[#2563eb] text-white'
                              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                          }`}
                          title="Toggle POI Labels"
                        >
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 10V9.21749C22 7.27787 22 6.30807 21.4142 5.7055C20.8284 5.10294 19.8856 5.10294 18 5.10294H15.9214C15.004 5.10294 14.9964 5.10116 14.1715 4.68834L10.8399 3.02114C9.44884 2.32504 8.75332 1.97699 8.01238 2.00118C7.27143 2.02537 6.59877 2.41808 5.25345 3.20351L4.02558 3.92037C3.03739 4.49729 2.54329 4.78576 2.27164 5.26564C2 5.74553 2 6.32993 2 7.49873V15.7157C2 17.2514 2 18.5193 2.34226 18.4467C2.57001 18.731 2.88916 18.9222 3.242 18.9856C3.77226 19.0808 4.42148 18.7018 5.71987 17.9437C6.60156 17.429 7.45011 16.8944 8.50487 17.0394C9.38869 17.1608 10.21 17.7185 11 18.1138" />
                            <path d="M8 2L8 17" />
                            <path d="M15 5V9.5" />
                            <path d="M18.3083 21.6835C18.0915 21.8865 17.8017 22 17.5001 22C17.1985 22 16.9087 21.8865 16.6919 21.6835C14.7063 19.813 12.0455 17.7235 13.3431 14.6898C14.0447 13.0496 15.7289 12 17.5001 12C19.2713 12 20.9555 13.0496 21.6571 14.6898C22.9531 17.7196 20.2988 19.8194 18.3083 21.6835Z" />
                            <path d="M17.5 16.5H17.509" />
                          </svg>
                        </button>
                      )}

                      {/* Dark Mode Map Toggle — same style as Live Tracking */}
                      {hasDarkMode && (
                        <button
                          onClick={() => {
                            // Capture current map position before dark mode toggle forces recreation
                            if (historyMapRef.current) {
                              const c = historyMapRef.current.getCenter();
                              const z = historyMapRef.current.getZoom();
                              if (c) {
                                setHistoryMapCenter({ lat: c.lat(), lng: c.lng() });
                                setHistoryMapZoom(z);
                              }
                            }
                            const next = !historyDarkMode;
                            setMapThemeOverride(next, isDark);
                            setHistoryDarkMode(next);
                          }}
                          className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center ${
                            historyDarkMode
                              ? 'bg-[#2563eb] text-white'
                              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                          }`}
                          title={historyDarkMode ? "Switch to Light Map" : "Switch to Dark Map"}
                          data-testid="history-toggle-dark-mode-btn"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                          </svg>
                        </button>
                      )}

                      {/* Fullscreen Toggle */}
                      <button
                        onClick={handleHistoryToggleFullscreen}
                        className="hidden w-9 h-9 rounded-md bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg transition-all sm:flex items-center justify-center"
                        title={historyIsFullscreen ? "Exit Full Screen" : "Full Screen"}
                        data-testid="history-toggle-fullscreen-btn"
                      >
                        {historyIsFullscreen ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" />
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" />
                          </svg>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Address Search - Top Right */}
                  <div className="absolute top-3 left-3 right-3 z-[500] sm:top-4 sm:left-auto sm:right-4">
                    <AddressAutocomplete
                      value={historyAddressSearch}
                      onChange={setHistoryAddressSearch}
                      onSelect={(s) => {
                        const lat = s.lat, lng = s.lon;
                        setHistorySearchMarker({ lat, lng });
                        if (historyMapType === 'osm' && historyLeafletMapRef.current) {
                          historyLeafletMapRef.current.flyTo([lat, lng], 16);
                        } else if (historyMapRef.current) {
                          historyMapRef.current.panTo({ lat, lng });
                          historyMapRef.current.setZoom(16);
                        }
                        toast.success('Location found');
                      }}
                      onClear={() => setHistorySearchMarker(null)}
                      placeholder="Search address..."
                      inputClassName="h-9 w-full sm:w-56 text-base md:text-sm bg-white dark:bg-[#0D1425]"
                      data-testid="history-address-search"
                    />
                  </div>

                  {/* Map Rendering - Conditional based on map type */}
                  {historyMapType === 'osm' ? (
                    /* Leaflet Map for OSM */
                    <MapContainer
                      center={[historyMapCenter.lat || -33.8688, historyMapCenter.lng || 151.2093]}
                      zoom={historyMapZoom || 12}
                      key={`osm-${historyMapCenter.lat}-${historyMapCenter.lng}-${historyMapZoom}`}
                      className="h-full w-full"
                      scrollWheelZoom={true}
                      zoomControl={false}
                    >
                      <TileLayer
                        attribution='&copy; OpenStreetMap'
                        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                      />
                      <LeafletMapController 
                        center={null}
                        zoom={null}
                        onMapReady={(map) => { historyLeafletMapRef.current = map; flushPendingMapFitRef.current?.(); }}
                      />

                      {/* Search Result Marker for OSM */}
                      {historySearchMarker && (
                        <LeafletMarker
                          position={[historySearchMarker.lat, historySearchMarker.lng]}
                          icon={L.icon({
                            iconUrl: '/leaflet/marker-icon-red.png',
                            shadowUrl: '/leaflet/marker-shadow.png',
                            iconSize: [25, 41],
                            iconAnchor: [12, 41],
                            popupAnchor: [1, -34],
                            shadowSize: [41, 41]
                          })}
                        >
                          <Popup>
                            <div className="p-1">
                              <p className="font-semibold text-sm">Search Result</p>
                              <p className="text-xs text-gray-600">{historyAddressSearch}</p>
                            </div>
                          </Popup>
                        </LeafletMarker>
                      )}
                      {/* Route Polyline */}
                      {showRouteOverlay && selectedTrip?.points?.length >= 2 && (
                        <LeafletPolyline
                          positions={snapToRoadEnabled && snappedRoutePath ? snappedRoutePath.map(p => [p.lat, p.lng]) : selectedTrip.points.map(p => [p.lat, p.lng])}
                          pathOptions={{ color: snapToRoadEnabled && snappedRoutePath ? '#06b6d4' : '#22c55e', weight: 4, opacity: 0.9 }}
                          eventHandlers={{ click: (e) => handleMapRouteClick({ lat: e.latlng.lat, lng: e.latlng.lng }) }}
                        />
                      )}
                      {/* Highlighted drive segment (Whole Period) */}
                      {highlightedSegment && selectedTrip?.points && (() => {
                        const segPts = selectedTrip.points.slice(highlightedSegment.start, highlightedSegment.end + 1);
                        return segPts.length >= 2 ? (
                          <LeafletPolyline
                            positions={segPts.map(p => [p.lat, p.lng])}
                            pathOptions={{ color: '#f59e0b', weight: 8, opacity: 1 }}
                            eventHandlers={{ click: (e) => handleMapRouteClick({ lat: e.latlng.lat, lng: e.latlng.lng }) }}
                          />
                        ) : null;
                      })()}
                      {/* Start Marker */}
                      {selectedTrip?.points?.length >= 1 && (() => {
                        const glowOn = highlightedMarker === 'start';
                        const glowStyle = glowOn ? 'filter:drop-shadow(0 0 8px #22c55e) drop-shadow(0 0 16px #22c55e) drop-shadow(0 0 24px #22c55e);' : '';
                        return (
                          <LeafletMarker
                            position={[selectedTrip.points[0].lat, selectedTrip.points[0].lng]}
                            icon={L.divIcon({ className: '', html: `<div style="width:28px;height:28px;${glowStyle}"><svg width="28" height="28" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="#16a34a" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.218c0,16.291,6.982,30.254,20.945,39.563L257.224,512l188.509-125.673c11.637-9.31,20.945-23.272,20.945-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M356.491,195.132l-157.37-108.53c-1.666-1.145-3.826-1.264-5.605-0.336c-1.785,0.939-2.903,2.783-2.903,4.802v217.061c0,2.018,1.118,3.868,2.903,4.809c0.792,0.412,1.66,0.618,2.523,0.618c1.08,0,2.155-0.326,3.083-0.961l157.37-108.53c1.471-1.015,2.344-2.681,2.344-4.466S357.956,196.147,356.491,195.132z"/></svg></div>`, iconSize: [28, 28], iconAnchor: [14, 28] })}
                            zIndexOffset={glowOn ? 2000 : 500}
                            eventHandlers={{ click: () => handleMapStartEndClick('start') }}
                          />
                        );
                      })()}
                      {/* End Marker */}
                      {selectedTrip?.points?.length >= 2 && (() => {
                        const glowOn = highlightedMarker === 'end';
                        const glowStyle = glowOn ? 'filter:drop-shadow(0 0 8px #dc2626) drop-shadow(0 0 16px #dc2626) drop-shadow(0 0 24px #dc2626);' : '';
                        return (
                          <LeafletMarker
                            position={[selectedTrip.points[selectedTrip.points.length - 1].lat, selectedTrip.points[selectedTrip.points.length - 1].lng]}
                            icon={L.divIcon({ className: '', html: `<div style="width:28px;height:28px;${glowStyle}"><svg width="28" height="28" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="#dc2626" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.218c0,16.291,6.982,30.254,20.945,39.563L257.224,512l188.509-125.673c11.637-9.31,20.945-23.272,20.945-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M334.743,122.647H179.705c-2.518,0-4.561,2.038-4.561,4.56v155.039c0,2.521,2.043,4.561,4.561,4.561h155.038c2.519,0,4.561-2.039,4.561-4.561V127.207C339.304,124.685,337.262,122.647,334.743,122.647z"/></svg></div>`, iconSize: [28, 28], iconAnchor: [14, 28] })}
                            zIndexOffset={glowOn ? 2000 : 500}
                            eventHandlers={{ click: () => handleMapStartEndClick('end') }}
                          />
                        );
                      })()}
                      {/* Stop / Idle markers */}
                      {visibleNonMovingSegments.filter(s => (s.state === 'idle' ? showIdleIcons : showStoppedIcons)).map((stop, sIdx) => {
                        const isHighlighted = highlightedStopIdx === sIdx;
                        const stopSvg = getHistoryStopSvgMarkup(stop.state, 28);
                        const glowStyle = isHighlighted ? `filter:${getHistoryStopGlowFilter(stop.state)};` : '';
                        return (
                          <LeafletMarker
                            key={`stop-${sIdx}`}
                            position={[stop.lat, stop.lng]}
                            icon={L.divIcon({ className: '', html: `<div style="width:28px;height:28px;${glowStyle}">${stopSvg}</div>`, iconSize: [28, 28], iconAnchor: [14, 28] })}
                            zIndexOffset={isHighlighted ? 1000 : 0}
                            eventHandlers={{ click: () => handleMapStopClick(sIdx) }}
                          />
                        );
                      })}
                      {/* Event markers — purple shield "E" icon */}
                      {showEventIcons && (() => {
                        const eventsToShow = selectedTrip?.events || historyEvents || [];
                        const eventSvg = `<svg width="28" height="28" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="#7F00FF" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.51-125.673c11.637-9.31,20.943-23.272,20.943-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M189.555,314.22V116.897h146.309v33.381H229.396v43.744h99.064v33.246h-99.064v53.705h110.236v33.246H189.555z"/></svg>`;
                        return eventsToShow.filter(ev => ev.lat && ev.lng).map((ev, eIdx) => {
                          const isHighlighted = highlightedEventIdx === eIdx;
                          const glowStyle = isHighlighted ? 'filter:drop-shadow(0 0 8px #7F00FF) drop-shadow(0 0 16px #7F00FF) drop-shadow(0 0 24px #7F00FF);' : '';
                          return (
                            <LeafletMarker
                              key={`event-${ev.id || eIdx}`}
                              position={[ev.lat, ev.lng]}
                              icon={L.divIcon({ className: '', html: `<div style="width:28px;height:28px;${glowStyle}">${eventSvg}</div>`, iconSize: [28, 28], iconAnchor: [14, 28] })}
                              zIndexOffset={isHighlighted ? 1500 : 800}
                              eventHandlers={{ click: () => handleMapEventClick(ev) }}
                            />
                          );
                        });
                      })()}
                      {/* Data Point markers (forensic) — filtered to current trip, CircleMarker for performance */}
                      {showDataPoints && forensicData?.points?.map((pt, ptIdx) => {
                        if (tripForensicIndexSet && !tripForensicIndexSet.has(ptIdx)) return null;
                        const isSelected = selectedHistoryPoint === ptIdx;
                        return (
                          <LeafletCircleMarker
                            key={`dp-${ptIdx}`}
                            center={[pt.lat, pt.lng]}
                            radius={isSelected ? 6 : 3}
                            pathOptions={{
                              fillColor: isSelected ? '#f59e0b' : '#3b82f6',
                              fillOpacity: isSelected ? 1 : 0.7,
                              color: isSelected ? '#fff' : 'rgba(255,255,255,0.7)',
                              weight: isSelected ? 2 : 1,
                            }}
                            eventHandlers={{ click: () => { selectHistoryPointManual(ptIdx); setMapBubble({ type: 'point', lat: pt.lat, lng: pt.lng, data: pt }); } }}
                          />
                        );
                      })}
                      {/* Selected data point marker — always visible when a forensic point is selected */}
                      {!showDataPoints && selectedHistoryPoint !== null && forensicData?.points?.[selectedHistoryPoint] && (() => {
                        const pt = forensicData.points[selectedHistoryPoint];
                        return (
                          <LeafletCircleMarker
                            center={[pt.lat, pt.lng]}
                            radius={6}
                            pathOptions={{
                              fillColor: '#f59e0b',
                              fillOpacity: 1,
                              color: '#fff',
                              weight: 2,
                            }}
                            eventHandlers={{ click: () => setMapBubble({ type: 'point', lat: pt.lat, lng: pt.lng, data: pt }) }}
                          />
                        );
                      })()}
                      {/* Playback Vehicle Marker (OSM) — directional arrow (non-interactive so it never blocks marker clicks) */}
                      {currentPlaybackPoint && (
                        <LeafletMarker
                          position={[currentPlaybackPoint.lat, currentPlaybackPoint.lng]}
                          interactive={false}
                          icon={L.divIcon({
                            className: '',
                            html: `<div style="width:0;height:0;overflow:visible;"><div style="transform:translate(-18px,-18px) rotate(${currentPlaybackPoint.heading || 0}deg);width:36px;height:36px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
                              <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 3 L29 31 L18 25 L7 31 Z" fill="#2563eb" stroke="#fff" stroke-width="2" stroke-linejoin="round"/>
                              </svg></div></div>`,
                            iconSize: [0, 0],
                            iconAnchor: [0, 0],
                          })}
                          zIndexOffset={3000}
                        />
                      )}
                      {/* Marker info bubble (OSM) */}
                      {mapBubble && (
                        <Popup
                          position={[mapBubble.lat, mapBubble.lng]}
                          offset={[0, -24]}
                          maxWidth={260}
                          autoPan={false}
                          className="history-map-bubble"
                          eventHandlers={{ remove: () => setMapBubble(null) }}
                        >
                          <div className="space-y-1.5">
                            <div className="pr-4">{renderMapBubbleTitle(mapBubble)}</div>
                            {renderMapBubbleBody(mapBubble)}
                          </div>
                        </Popup>
                      )}
                    </MapContainer>
                  ) : (
                    /* Google Map for Google layers */
                    <div key={`gmap-${gmapEpoch}-${historyShowPOI ? 'poi' : 'nopoi'}-${historyDarkMode ? 'dark' : 'light'}`} style={{ width: '100%', height: '100%' }}>
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                      <GoogleMapComponent
                        defaultCenter={historyMapCenter}
                        defaultZoom={historyMapZoom}
                        mapId={historyShowPOI ? GOOGLE_MAPS_MAP_ID : GOOGLE_MAPS_MAP_ID_NO_POI}
                        colorScheme={historyDarkMode ? 'DARK' : 'LIGHT'}
                        style={{ width: '100%', height: '100%' }}
                        gestureHandling="greedy"
                        disableDefaultUI={true}
                        zoomControl={false}
                        mapTypeControl={false}
                        scaleControl={false}
                        streetViewControl={false}
                        rotateControl={false}
                        fullscreenControl={false}
                        mapTypeId={historyMapType === 'satellite' ? 'satellite' : 
                                   historyMapType === 'hybrid' ? 'hybrid' :
                                   historyMapType === 'terrain' ? 'terrain' : 'roadmap'}
                        onClick={() => setMapBubble(null)}
                      >
                        <GoogleMapController onMapReady={handleHistoryMapLoad} />
                        <GoogleIdleListener onIdle={handleHistoryGoogleIdle} />
                        {/* Route Polyline */}
                        {showRouteOverlay && selectedTrip?.points?.length >= 2 && (
                          <GooglePolyline
                            path={snapToRoadEnabled && snappedRoutePath ? snappedRoutePath.map(p => ({ lat: p.lat, lng: p.lng })) : selectedTrip.points.map(p => ({ lat: p.lat, lng: p.lng }))}
                            options={{ strokeColor: snapToRoadEnabled && snappedRoutePath ? '#06b6d4' : '#22c55e', strokeOpacity: 0.9, strokeWeight: 4, clickable: true, zIndex: 1 }}
                            onClick={(e) => {
                              const lat = e?.latLng?.lat?.();
                              const lng = e?.latLng?.lng?.();
                              if (lat != null && lng != null) handleMapRouteClick({ lat, lng });
                            }}
                          />
                        )}
                        {/* Highlighted drive segment (Whole Period) */}
                        {highlightedSegment && selectedTrip?.points && (() => {
                          const segPts = selectedTrip.points.slice(highlightedSegment.start, highlightedSegment.end + 1);
                          return segPts.length >= 2 ? (
                            <GooglePolyline
                              path={segPts.map(p => ({ lat: p.lat, lng: p.lng }))}
                              options={{ strokeColor: '#f59e0b', strokeOpacity: 1, strokeWeight: 8, zIndex: 10, clickable: true }}
                              onClick={(e) => {
                                const lat = e?.latLng?.lat?.();
                                const lng = e?.latLng?.lng?.();
                                if (lat != null && lng != null) handleMapRouteClick({ lat, lng });
                              }}
                            />
                          ) : null;
                        })()}
                        {/* Start Marker */}
                        {selectedTrip?.points?.length >= 1 && (() => {
                          const glowOn = highlightedMarker === 'start';
                          const glowFilter = glowOn ? 'drop-shadow(0 0 8px #22c55e) drop-shadow(0 0 16px #22c55e) drop-shadow(0 0 24px #22c55e)' : 'none';
                          return (
                            <AdvancedMarker position={{ lat: selectedTrip.points[0].lat, lng: selectedTrip.points[0].lng }} zIndex={glowOn ? 100 : 30} onClick={() => handleMapStartEndClick('start')}>
                              <div style={{ width: 0, height: 0, overflow: 'visible' }}>
                                <div style={{ transform: 'translate(-50%, -100%)', width: 28, height: 28, filter: glowFilter }} dangerouslySetInnerHTML={{ __html: '<svg width="28" height="28" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="#16a34a" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.218c0,16.291,6.982,30.254,20.945,39.563L257.224,512l188.509-125.673c11.637-9.31,20.945-23.272,20.945-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M356.491,195.132l-157.37-108.53c-1.666-1.145-3.826-1.264-5.605-0.336c-1.785,0.939-2.903,2.783-2.903,4.802v217.061c0,2.018,1.118,3.868,2.903,4.809c0.792,0.412,1.66,0.618,2.523,0.618c1.08,0,2.155-0.326,3.083-0.961l157.37-108.53c1.471-1.015,2.344-2.681,2.344-4.466S357.956,196.147,356.491,195.132z"/></svg>' }} />
                              </div>
                            </AdvancedMarker>
                          );
                        })()}
                        {/* End Marker */}
                        {selectedTrip?.points?.length >= 2 && (() => {
                          const glowOn = highlightedMarker === 'end';
                          const glowFilter = glowOn ? 'drop-shadow(0 0 8px #dc2626) drop-shadow(0 0 16px #dc2626) drop-shadow(0 0 24px #dc2626)' : 'none';
                          return (
                            <AdvancedMarker position={{ lat: selectedTrip.points[selectedTrip.points.length - 1].lat, lng: selectedTrip.points[selectedTrip.points.length - 1].lng }} zIndex={glowOn ? 100 : 30} onClick={() => handleMapStartEndClick('end')}>
                              <div style={{ width: 0, height: 0, overflow: 'visible' }}>
                                <div style={{ transform: 'translate(-50%, -100%)', width: 28, height: 28, filter: glowFilter }} dangerouslySetInnerHTML={{ __html: '<svg width="28" height="28" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="#dc2626" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.218c0,16.291,6.982,30.254,20.945,39.563L257.224,512l188.509-125.673c11.637-9.31,20.945-23.272,20.945-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M334.743,122.647H179.705c-2.518,0-4.561,2.038-4.561,4.56v155.039c0,2.521,2.043,4.561,4.561,4.561h155.038c2.519,0,4.561-2.039,4.561-4.561V127.207C339.304,124.685,337.262,122.647,334.743,122.647z"/></svg>' }} />
                              </div>
                            </AdvancedMarker>
                          );
                        })()}
                        {/* Stop / Idle markers */}
                        {visibleNonMovingSegments.filter(s => (s.state === 'idle' ? showIdleIcons : showStoppedIcons)).map((stop, sIdx) => {
                          const isHighlighted = highlightedStopIdx === sIdx;
                          const stopSvg = getHistoryStopSvgMarkup(stop.state, 28);
                          const glowFilter = isHighlighted ? getHistoryStopGlowFilter(stop.state) : 'none';
                          return (
                            <AdvancedMarker key={`stop-${sIdx}`} position={{ lat: stop.lat, lng: stop.lng }}
                              onClick={() => handleMapStopClick(sIdx)}
                              zIndex={isHighlighted ? 50 : 10}
                            >
                              <div style={{ width: 0, height: 0, overflow: 'visible' }}>
                                <div style={{ transform: 'translate(-50%, -100%)', width: 28, height: 28, filter: glowFilter }} dangerouslySetInnerHTML={{ __html: stopSvg }} />
                              </div>
                            </AdvancedMarker>
                          );
                        })}
                        {/* Event markers — purple shield "E" icon (Google Maps) */}
                        {showEventIcons && (() => {
                          const eventsToShow = selectedTrip?.events || historyEvents || [];
                          const eventSvg = '<svg width="28" height="28" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="#7F00FF" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.51-125.673c11.637-9.31,20.943-23.272,20.943-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/><path fill="#FFF" d="M189.555,314.22V116.897h146.309v33.381H229.396v43.744h99.064v33.246h-99.064v53.705h110.236v33.246H189.555z"/></svg>';
                          return eventsToShow.filter(ev => ev.lat && ev.lng).map((ev, eIdx) => {
                            const isHighlighted = highlightedEventIdx === eIdx;
                            const glowFilter = isHighlighted ? 'drop-shadow(0 0 8px #7F00FF) drop-shadow(0 0 16px #7F00FF) drop-shadow(0 0 24px #7F00FF)' : 'none';
                            return (
                              <AdvancedMarker key={`event-${ev.id || eIdx}`} position={{ lat: ev.lat, lng: ev.lng }}
                                zIndex={isHighlighted ? 100 : 40}
                                onClick={() => handleMapEventClick(ev)}
                              >
                                <div style={{ width: 0, height: 0, overflow: 'visible' }}>
                                  <div style={{ transform: 'translate(-50%, -100%)', width: 28, height: 28, filter: glowFilter }} dangerouslySetInnerHTML={{ __html: eventSvg }} />
                                </div>
                              </AdvancedMarker>
                            );
                          });
                        })()}
                        {/* Data Point marker — selected point always visible when forensic data loaded */}
                        {selectedHistoryPoint !== null && forensicData?.points?.[selectedHistoryPoint] && (() => {
                          const pt = forensicData.points[selectedHistoryPoint];
                          return (
                            <AdvancedMarker
                              position={{ lat: pt.lat, lng: pt.lng }}
                              zIndex={200}
                              onClick={() => setMapBubble({ type: 'point', lat: pt.lat, lng: pt.lng, data: pt })}
                            >
                              <div style={{ width: 0, height: 0, overflow: 'visible' }}>
                                <div style={{
                                  transform: 'translate(-50%, -50%)',
                                  width: 14, height: 14,
                                  borderRadius: '50%',
                                  background: '#f59e0b',
                                  border: '2px solid #fff',
                                  boxShadow: '0 0 8px #f59e0b',
                                }} />
                              </div>
                            </AdvancedMarker>
                          );
                        })()}
                        {/* Playback Vehicle Marker (Google Maps) — directional arrow (non-interactive so it never blocks marker clicks) */}
                        {currentPlaybackPoint && (
                          <AdvancedMarker
                            position={{ lat: currentPlaybackPoint.lat, lng: currentPlaybackPoint.lng }}
                            zIndex={300}
                          >
                            <div style={{ width: 0, height: 0, overflow: 'visible', pointerEvents: 'none' }}>
                              <div style={{
                                transform: `translate(-18px, -18px) rotate(${currentPlaybackPoint.heading || 0}deg)`,
                                width: 36, height: 36,
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
                              }}>
                                <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M18 3 L29 31 L18 25 L7 31 Z" fill="#2563eb" stroke="#fff" strokeWidth="2" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            </div>
                          </AdvancedMarker>
                        )}
                        {/* Search Result Marker for Google Maps */}
                        {historySearchMarker && (
                          <AdvancedMarker
                            position={{ lat: historySearchMarker.lat, lng: historySearchMarker.lng }}
                            onClick={() => setHistoryInfoWindowOpen('search-marker')}
                          >
                            <img src="/leaflet/marker-icon-red.png" alt="Search" style={{ width: '25px', height: '41px' }} />
                          </AdvancedMarker>
                        )}
                        {historyInfoWindowOpen === 'search-marker' && historySearchMarker && (
                          <GInfoWindow
                            position={{ lat: historySearchMarker.lat, lng: historySearchMarker.lng }}
                            shouldFocus={false}
                            onClose={() => setHistoryInfoWindowOpen(null)}
                          >
                            <div className="p-1">
                              <p className="font-semibold text-sm">Search Result</p>
                              <p className="text-xs text-gray-600">{historyAddressSearch}</p>
                            </div>
                          </GInfoWindow>
                        )}
                        {/* Marker info bubble (Google) — title lives in the InfoWindow header next to the X */}
                        {mapBubble && (
                          <GInfoWindow
                            position={{ lat: mapBubble.lat, lng: mapBubble.lng }}
                            pixelOffset={HISTORY_BUBBLE_PIXEL_OFFSET}
                            maxWidth={280}
                            disableAutoPan={true}
                            shouldFocus={false}
                            headerContent={renderMapBubbleTitle(mapBubble)}
                            onClose={() => setMapBubble(null)}
                          >
                            {renderMapBubbleBody(mapBubble)}
                          </GInfoWindow>
                        )}
                      </GoogleMapComponent>
                    </APIProvider>
                    </div>
                  )}

                  {/* Playback Controls Overlay */}
                  {selectedTrip?.points?.length >= 2 && (
                  <div data-testid="playback-controls" className="absolute bottom-3 left-2 right-2 mx-auto max-w-[calc(100%-1rem)] bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 px-2.5 py-2 flex flex-wrap items-center justify-center gap-2 z-[500] sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:flex-nowrap sm:px-4 sm:py-2.5 sm:gap-3">
                    <Button data-testid="playback-skip-back" variant="ghost" size="icon" className="h-8 w-8"
                      disabled={playbackPosition <= 0}
                      onClick={() => { setPlaybackPosition(Math.max(0, playbackPosition - 1)); setIsPlaying(false); }}
                    >
                      <SkipBack className="w-4 h-4" />
                    </Button>
                    <Button 
                      data-testid="playback-play-pause"
                      size="icon" 
                      className="h-10 w-10 bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                      onClick={() => {
                        if (!isPlaying && playbackPosition >= selectedTrip.points.length - 1) {
                          setPlaybackPosition(0); // Reset if at end
                        }
                        setIsPlaying(!isPlaying);
                      }}
                    >
                      {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    </Button>
                    <Button data-testid="playback-skip-forward" variant="ghost" size="icon" className="h-8 w-8"
                      disabled={!selectedTrip?.points || playbackPosition >= selectedTrip.points.length - 1}
                      onClick={() => { setPlaybackPosition(Math.min(selectedTrip.points.length - 1, playbackPosition + 1)); setIsPlaying(false); }}
                    >
                      <SkipForward className="w-4 h-4" />
                    </Button>
                    <Button data-testid="playback-stop" variant="ghost" size="icon" className="h-8 w-8"
                      onClick={() => { setIsPlaying(false); setPlaybackPosition(0); }}
                    >
                      <Square className="w-4 h-4" />
                    </Button>
                    <div className="min-w-[8rem] flex-1 sm:w-40 sm:flex-none">
                      <Slider
                        data-testid="playback-slider"
                        value={[playbackPosition]}
                        onValueChange={(v) => { setPlaybackPosition(v[0]); if (isPlaying) setIsPlaying(false); }}
                        max={Math.max(0, (selectedTrip?.points?.length || 1) - 1)}
                        step={1}
                      />
                    </div>
                    <Select value={String(playbackSpeed)} onValueChange={(v) => setPlaybackSpeed(Number(v))}>
                      <SelectTrigger data-testid="playback-speed" className="w-[72px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0.25">0.25x</SelectItem>
                        <SelectItem value="0.5">0.5x</SelectItem>
                        <SelectItem value="1">1x</SelectItem>
                        <SelectItem value="2">2x</SelectItem>
                        <SelectItem value="5">5x</SelectItem>
                        <SelectItem value="10">10x</SelectItem>
                        <SelectItem value="25">25x</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="text-xs text-muted-foreground whitespace-nowrap font-mono min-w-[76px] text-center">
                      {currentPlaybackPoint ? formatTimeOnly(currentPlaybackPoint.timestamp, { timezone: activeHistoryTimezone, seconds: true }) : '--:--:--'}
                      <div className="text-[10px]">{playbackPosition + 1}/{selectedTrip?.points?.length || 0}</div>
                    </div>
                  </div>
                  )}
                </div>
              </Card>

              {/* Map Overlay Toggles — single row under the map (Geofencing Map Layers style); only after search has results */}
              {trips.length > 0 && (
                <div className="rounded-md border bg-card px-3 py-2" data-testid="map-overlay-toggles">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider mr-1">Map Overlays</span>
                    {[
                      { key: 'route', label: 'Route', icon: Route, checked: showRouteOverlay, toggle: setShowRouteOverlay },
                      { key: 'idle', label: 'Idle Icons', icon: Clock, checked: showIdleIcons, toggle: setShowIdleIcons },
                      { key: 'stopped', label: 'Stopped Parking Icons', icon: MapPin, checked: showStoppedIcons, toggle: setShowStoppedIcons },
                      { key: 'events', label: 'Event Icons', icon: Zap, checked: showEventIcons, toggle: setShowEventIcons },
                      ...(hasSnapToRoad ? [{ key: 'snap-to-road', label: snapLoading ? 'Snapping...' : 'Snap to Road', icon: Navigation, checked: snapToRoadEnabled, toggle: setSnapToRoadEnabled }] : []),
                      ...(hasForensicPanel ? [{ key: 'datapoints', label: 'Data Points', icon: Crosshair, checked: showDataPoints, toggle: setShowDataPoints }] : []),
                    ].map(({ key, label, icon: Icon, checked, toggle }) => (
                      <div
                        key={key}
                        className={`flex items-center gap-1.5 rounded-full border pl-2.5 pr-1 py-0.5 transition-colors cursor-pointer select-none ${checked ? 'bg-primary/10 border-primary/40' : 'bg-background/60 hover:bg-muted/60'}`}
                        onClick={() => toggle(!checked)}
                        data-testid={`toggle-${key}`}
                      >
                        <Icon className={`w-3.5 h-3.5 ${checked ? 'text-primary' : 'text-muted-foreground'}`} />
                        <span className="text-xs whitespace-nowrap">{label}</span>
                        <Switch checked={checked} onCheckedChange={toggle} onClick={(e) => e.stopPropagation()} className="scale-[0.65] -ml-0.5" data-testid={`toggle-${key}-switch`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trip Stats */}
              {selectedTrip && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Route className="w-5 h-5 mx-auto mb-1 text-[#2563eb]" />
                      <p className="text-lg font-bold">{formatDistance(selectedTrip.distance_km)}</p>
                      <p className="text-[10px] text-muted-foreground">Distance</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Timer className="w-5 h-5 mx-auto mb-1 text-[#2563eb]" />
                      <p className="text-lg font-bold">{formatDurationExact(selectedTrip.duration_secs)}</p>
                      <p className="text-[10px] text-muted-foreground">Duration</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Gauge className="w-5 h-5 mx-auto mb-1 text-[#2563eb]" />
                      <p className="text-lg font-bold">{formatSpeed(selectedTrip.avg_speed)}</p>
                      <p className="text-[10px] text-muted-foreground">Avg Speed</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Gauge className="w-5 h-5 mx-auto mb-1 text-red-500" />
                      <p className="text-lg font-bold">{formatSpeed(selectedTrip.max_speed)}</p>
                      <p className="text-[10px] text-muted-foreground">Max Speed</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <MapPin className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                      <p className="text-lg font-bold">{splitMode === 'ignition' ? selectedTrip.idle_count : selectedTrip.stop_count}</p>
                      <p className="text-[10px] text-muted-foreground">{splitMode === 'ignition' ? 'Idle Events' : 'Stops'}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Clock className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-lg font-bold">{formatDurationExact(splitMode === 'ignition' ? selectedTrip.idle_secs : selectedTrip.non_moving_secs)}</p>
                      <p className="text-[10px] text-muted-foreground">{splitMode === 'ignition' ? 'Engine Idle Time' : 'Non-moving Time'}</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Export & Print — dropdown buttons under Stats Cards */}
              {selectedTrip && (
                <div className="flex flex-wrap items-center gap-2" data-testid="export-panel">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mr-1">Export</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" data-testid="export-trips-menu"><Upload className="w-3 h-3" /> Trips</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => handleExport('trips', 'csv')}>CSV</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('trips', 'html')}>HTML</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('trips', 'pdf')}>PDF</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" data-testid="export-trip-points-menu"><Upload className="w-3 h-3" /> Trip Points</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => handleExport('trip-points', 'csv')}>CSV</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('trip-points', 'html')}>HTML</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('trip-points', 'pdf')}>PDF</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" data-testid="export-trip-detail-menu"><Upload className="w-3 h-3" /> Trip Detail</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => handleExport('trip-detail', 'csv')} data-testid="export-trip-detail-csv">CSV</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('trip-detail', 'html')} data-testid="export-trip-detail-html">HTML</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('trip-detail', 'pdf')} data-testid="export-trip-detail-pdf">PDF</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {hasForensicPanel && forensicData && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" data-testid="export-messages-menu"><Upload className="w-3 h-3" /> Messages</Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem onClick={() => handleExport('messages', 'csv')}>CSV</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport('messages', 'html')}>HTML</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport('messages', 'pdf')}>PDF</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {hasForensicPanel && graphChartData.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" data-testid="export-graph-menu"><Upload className="w-3 h-3" /> Graph</Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem onClick={() => handleExport('graph', 'html')}>HTML</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport('graph', 'pdf')}>PDF</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" data-testid="export-map-menu"><Upload className="w-3 h-3" /> Map</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => handleExport('map', 'html')}>HTML</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('map', 'pdf')}>PDF</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" data-testid="export-route-menu"><Route className="w-3 h-3" /> Route</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => openRouteExportDialog('geojson')} data-testid="export-route-geojson">Download GeoJSON</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openRouteExportDialog('direct')} data-testid="export-route-direct">Save to Route Geofences</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Dialog open={!!routeExportDialog} onOpenChange={(o) => { if (!o) setRouteExportDialog(null); }}>
                    <DialogContent className="sm:max-w-md" data-testid="route-export-dialog">
                      <DialogHeader>
                        <DialogTitle className="text-sm">{routeExportDialog?.mode === 'direct' ? 'Save Trip as Route Geofence' : 'Export Trip as Route GeoJSON'}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Route Name</Label>
                          <Input value={routeExportForm.name} onChange={(e) => setRouteExportForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-sm" data-testid="route-export-name-input" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Route Group</Label>
                          <Select value={routeExportForm.group} onValueChange={(v) => setRouteExportForm(f => ({ ...f, group: v }))}>
                            <SelectTrigger className="h-8 text-sm" data-testid="route-export-group-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Ungrouped</SelectItem>
                              {routeExportGroups.map((g) => (
                                <SelectItem key={g.id} value={g.id} data-testid={`route-export-group-${g.id}`}>{g.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Color</Label>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {PRESET_COLORS.map((preset) => (
                              <button
                                key={preset.value}
                                type="button"
                                title={preset.label}
                                onClick={() => setRouteExportForm(f => ({ ...f, color: preset.value }))}
                                className={`w-6 h-6 rounded-full shrink-0 transition-all ${routeExportForm.color === preset.value ? 'border-[3px] border-blue-500 scale-110' : 'border-2 border-transparent hover:border-gray-400'}`}
                                style={{ backgroundColor: preset.value }}
                                data-testid={`route-export-color-${preset.value.replace('#', '')}`}
                              />
                            ))}
                            <input
                              type="color"
                              value={routeExportForm.color}
                              onChange={(e) => setRouteExportForm(f => ({ ...f, color: e.target.value }))}
                              className="w-6 h-6 rounded cursor-pointer border-2 border-gray-300 p-0"
                              title="Custom color"
                              data-testid="route-export-custom-color"
                            />
                            <span className="text-[10px] text-muted-foreground">Custom</span>
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Deviation Distance (m)</Label>
                          <Input type="number" min={1} value={routeExportForm.deviation} onChange={(e) => setRouteExportForm(f => ({ ...f, deviation: e.target.value }))} className="h-8 text-sm w-32" data-testid="route-export-deviation-input" />
                          <p className="text-[10px] text-muted-foreground">Alert when a vehicle strays further than this from the route.</p>
                        </div>
                        <div className="text-[10px] text-muted-foreground" data-testid="route-export-point-count">{(selectedTrip?.points || []).length.toLocaleString()} GPS points will be included.</div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" size="sm" onClick={() => setRouteExportDialog(null)} data-testid="route-export-cancel">Cancel</Button>
                        <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" onClick={confirmRouteExport} data-testid="route-export-confirm">{routeExportDialog?.mode === 'direct' ? 'Save Route' : 'Download GeoJSON'}</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}

              {/* ══════════════════ FORENSIC ANALYSIS PANEL ══════════════════ */}
              {hasForensicPanel && forensicLoading && !(forensicData && forensicData.total_points > 0) && (
                <Card className="mt-4" data-testid="forensic-loading-card">
                  <CardContent className="py-10 flex items-center justify-center gap-3 text-sm text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin text-[#2563eb]" />
                    Loading forensic data… large date ranges may take a moment
                  </CardContent>
                </Card>
              )}
              {hasForensicPanel && forensicData && forensicData.total_points > 0 && (
                <Card data-testid="forensic-panel" className="mt-4 relative">
                  {forensicLoading && (
                    <div className="absolute inset-0 z-10 bg-background/60 backdrop-blur-[1px] flex items-center justify-center rounded-lg" data-testid="forensic-loading-overlay">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Loader2 className="w-5 h-5 animate-spin text-[#2563eb]" />
                        Loading forensic data…
                      </div>
                    </div>
                  )}
                  <CardHeader className="pb-2 px-4 pt-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <Search className="w-4 h-4 text-[#2563eb]" />
                        Forensic Analysis
                        <Badge variant="outline" className="text-[10px] ml-1">{tripForensicIndices ? tripForensicIndices.length : forensicData.total_points} pts</Badge>
                        {forensicLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                      </CardTitle>
                      {/* Point navigation + Jump to point */}
                      <div className="flex items-center gap-1">
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          const input = e.target.elements.jumpPoint;
                          const val = parseInt(input.value, 10);
                          if (tripForensicIndices && val >= 1 && val <= tripForensicIndices.length) {
                            selectHistoryPointManual(tripForensicIndices[val - 1]);
                            input.value = '';
                          } else if (!tripForensicIndices && val >= 1 && val <= forensicData.total_points) {
                            selectHistoryPointManual(val - 1);
                            input.value = '';
                          }
                        }} className="flex items-center gap-1">
                          <Input
                            name="jumpPoint"
                            type="number"
                            min={1}
                            max={tripForensicIndices ? tripForensicIndices.length : forensicData.total_points}
                            placeholder="Go to #"
                            className="h-7 w-20 text-xs text-center"
                            data-testid="forensic-jump-input"
                          />
                          <Button type="submit" variant="outline" size="sm" className="h-7 text-xs px-2" data-testid="forensic-jump-btn">
                            Show
                          </Button>
                        </form>
                        {selectedHistoryPoint !== null && (
                          <>
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7"
                              disabled={!tripForensicIndices || tripForensicIndices.indexOf(selectedHistoryPoint) <= 0}
                              onClick={() => navigateForensicPoint(-1)}
                              data-testid="forensic-prev-point"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <span className="text-xs font-mono text-muted-foreground min-w-[60px] text-center">
                              {tripForensicIndices ? `${tripForensicIndices.indexOf(selectedHistoryPoint) + 1} / ${tripForensicIndices.length}` : `${selectedHistoryPoint + 1} / ${forensicData.total_points}`}
                            </span>
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7"
                              disabled={!tripForensicIndices || tripForensicIndices.indexOf(selectedHistoryPoint) >= tripForensicIndices.length - 1}
                              onClick={() => navigateForensicPoint(1)}
                              data-testid="forensic-next-point"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Tab bar */}
                    <div className="flex gap-1 mt-2">
                      {[
                        { id: 'telemetry', label: 'Telemetry Data', icon: Radio },
                        { id: 'graphical', label: 'Graphical Data', icon: Activity },
                        { id: 'messages', label: 'Message Data', icon: FileText },
                      ].map(({ id, label, icon: Icon }) => (
                        <button
                          key={id}
                          onClick={() => setForensicActiveTab(id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            forensicActiveTab === id
                              ? 'bg-[#2563eb] text-white'
                              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                          }`}
                          data-testid={`forensic-tab-${id}`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {label}
                        </button>
                      ))}
                    </div>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 pt-0">
                    {/* Tab 1: Telemetry Data */}
                    {forensicActiveTab === 'telemetry' && (
                      <div data-testid="forensic-telemetry-tab">
                        <ForensicTelemetryCard
                          point={selectedForensicPoint}
                          timezone={activeHistoryTimezone}
                          ioMappings={forensicData?.io_mappings}
                          hasIgnitionMapping={forensicData?.has_ignition_mapping !== false}
                          emptyHint='Click a data point on the map, a timeline item, or use the navigation arrows to select a point. Enable "Data Points" in Map Overlays to see clickable points on the map.'
                        />
                      </div>
                    )}

                    {/* Tab 2: Graphical Data */}
                    {forensicActiveTab === 'graphical' && (
                      <div data-testid="forensic-graphical-tab">
                        {graphChartData.length > 0 ? (
                          <div className="space-y-3">
                            {/* Field selector */}
                            <div className="flex flex-wrap gap-2 items-center">
                              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mr-1">Series:</span>
                              {(forensicData?.graph_fields || []).map((gf) => {
                                const isOn = graphEnabledFields.includes(gf.key);
                                const effectiveColor = getGraphColor(gf);
                                return (
                                  <div key={gf.key} className="inline-flex items-center gap-0.5">
                                    <button
                                      onClick={() => setGraphEnabledFields(prev =>
                                        prev.includes(gf.key) ? prev.filter(k => k !== gf.key) : [...prev, gf.key]
                                      )}
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${
                                        isOn ? 'border-transparent text-white' : 'border-slate-300 dark:border-slate-600 text-muted-foreground hover:bg-muted/50'
                                      }`}
                                      style={isOn ? { backgroundColor: effectiveColor } : {}}
                                      data-testid={`graph-field-${gf.key}`}
                                    >
                                      <span className={`w-2 h-2 rounded-full ${isOn ? 'bg-white/80' : ''}`} style={!isOn ? { backgroundColor: effectiveColor } : {}} />
                                      {gf.label}{gf.units ? ` (${gf.units})` : ''}
                                    </button>
                                    {isOn && (
                                      <input
                                        type="color"
                                        value={effectiveColor}
                                        onChange={(e) => setGraphCustomColors(prev => ({ ...prev, [gf.key]: e.target.value }))}
                                        className="w-4 h-4 rounded-full cursor-pointer border-0 p-0"
                                        style={{ appearance: 'none', WebkitAppearance: 'none', background: 'none' }}
                                        title={`Change ${gf.label} color`}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Downsample note + zoom reset */}
                            {graphIsDownsampled && (
                              <div className="flex justify-center">
                                <span className="text-[10px] text-muted-foreground" data-testid="graph-downsample-note">
                                  Rendering {displayChartData.length.toLocaleString()} of {windowedChartData.length.toLocaleString()} points — drag the brush below to zoom in for full detail
                                </span>
                              </div>
                            )}
                            <div className="flex justify-center">
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!graphViewDomain}
                                className={`h-5 px-2 text-[10px] ${graphViewDomain
                                  ? 'text-red-600 dark:text-red-400 border-red-300 dark:border-red-500/50 hover:text-red-700 dark:hover:text-red-300'
                                  : 'text-muted-foreground opacity-50'}`}
                                onClick={() => setGraphViewDomain(null)}
                                data-testid="graph-reset-zoom"
                              >
                                Reset Zoom
                              </Button>
                            </div>

                            {/* Chart */}
                            <style>{`.forensic-graph .recharts-brush-texts text { font-size: 12px !important; }`}</style>
                            <div className="h-[300px] w-full forensic-graph" data-testid="forensic-graph-chart">
                              <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart
                                  data={displayChartData}
                                  onClick={(e) => {
                                    if (e?.activePayload?.[0]?.payload?.idx != null) {
                                      selectHistoryPointManual(e.activePayload[0].payload.idx);
                                    }
                                  }}
                                  margin={{ top: 5, right: 43, left: 33, bottom: 20 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                                  <XAxis
                                    dataKey="ts"
                                    type="number"
                                    domain={['dataMin', 'dataMax']}
                                    tickFormatter={(ts) => {
                                      const d = new Date(ts);
                                      return formatTimeOnly(d.toISOString(), { timezone: activeHistoryTimezone, seconds: true });
                                    }}
                                    tick={{ fontSize: 10, fill: 'currentColor' }}
                                    stroke="rgba(148,163,184,0.5)"
                                  />
                                  {/* Left Y axis for numeric fields */}
                                  <YAxis
                                    yAxisId="numeric"
                                    tick={{ fontSize: 10, fill: 'currentColor' }}
                                    stroke="rgba(148,163,184,0.5)"
                                    width={40}
                                  />
                                  {/* Right Y axis for binary fields */}
                                  {graphEnabledFields.some(k => forensicData?.graph_fields?.find(gf => gf.key === k && gf.type === 'binary')) && (
                                    <YAxis
                                      yAxisId="binary"
                                      orientation="right"
                                      domain={[-0.1, 1.3]}
                                      ticks={[0, 1]}
                                      allowDataOverflow={true}
                                      tickFormatter={(v) => v === 1 ? 'ON' : 'OFF'}
                                      tick={{ fontSize: 10, fill: 'currentColor' }}
                                      stroke="rgba(148,163,184,0.5)"
                                      width={35}
                                    />
                                  )}
                                  <RechartsTooltip
                                    contentStyle={{ fontSize: 11, backgroundColor: 'rgba(15,23,42,0.9)', border: 'none', borderRadius: 8, color: '#fff' }}
                                    labelFormatter={(ts, payload) => {
                                      const d = new Date(ts);
                                      const ptIdx = payload?.[0]?.payload?.idx;
                                      const pointLabel = ptIdx != null ? `Point ${ptIdx + 1}  —  ` : '';
                                      return pointLabel + formatDT(d.toISOString(), { timezone: activeHistoryTimezone, seconds: true });
                                    }}
                                    formatter={(value, name, props) => {
                                      const gf = forensicData?.graph_fields?.find(f => f.key === name);
                                      if (gf?.type === 'binary') {
                                        // Look up actual mapped label from the point's mapped_values
                                        const ptIdx = props?.payload?.idx;
                                        if (ptIdx != null && forensicData?.points?.[ptIdx]) {
                                          const mv = Object.values(forensicData.points[ptIdx].mapped_values || {}).find(v => v.io_id === gf.io_id);
                                          if (mv?.type === 'state') return [mv.value, gf.label];
                                        }
                                        return [value === 1 ? 'ON' : 'OFF', gf.label];
                                      }
                                      return [typeof value === 'number' ? value.toFixed(1) : value, gf?.label || name];
                                    }}
                                  />
                                  {/* Selected point reference line — attach to whichever Y axis is active */}
                                  {selectedHistoryPoint !== null && graphChartDataByIdx[selectedHistoryPoint] && (() => {
                                    const hasNumericAxis = graphEnabledFields.some(k => {
                                      const gf = forensicData?.graph_fields?.find(f => f.key === k);
                                      return gf && gf.type !== 'binary';
                                    });
                                    return (
                                      <ReferenceLine
                                        x={graphChartDataByIdx[selectedHistoryPoint].ts}
                                        yAxisId={hasNumericAxis ? 'numeric' : 'binary'}
                                        stroke="#f59e0b"
                                        strokeWidth={2}
                                        strokeDasharray="4 2"
                                      />
                                    );
                                  })()}
                                  {/* Render enabled series */}
                                  {graphEnabledFields.map((fieldKey) => {
                                    const gf = forensicData?.graph_fields?.find(f => f.key === fieldKey);
                                    if (!gf) return null;
                                    const isBinary = gf.type === 'binary';
                                    return (
                                      <Line
                                        key={fieldKey}
                                        type={isBinary ? 'stepAfter' : 'monotone'}
                                        dataKey={fieldKey}
                                        yAxisId={isBinary ? 'binary' : 'numeric'}
                                        stroke={getGraphColor(gf)}
                                        strokeWidth={isBinary ? 2 : 1.5}
                                        dot={false}
                                        activeDot={{ r: 4, strokeWidth: 2 }}
                                        name={fieldKey}
                                        connectNulls={false}
                                        isAnimationActive={false}
                                      />
                                    );
                                  })}
                                  <Brush
                                    dataKey="ts"
                                    height={28}
                                    stroke="#3b82f6"
                                    fill="rgba(59,130,246,0.05)"
                                    onChange={handleGraphBrushChange}
                                    tickFormatter={(ts) => {
                                      const d = new Date(ts);
                                      return formatTimeOnly(d.toISOString(), { timezone: activeHistoryTimezone, seconds: true });
                                    }}
                                    tick={{ fontSize: 6, fill: 'currentColor' }}
                                  />
                                </ComposedChart>
                              </ResponsiveContainer>
                            </div>

                            {/* Current point summary below chart */}
                            {selectedForensicPoint && (
                              <div className="flex items-center gap-3 flex-wrap text-xs border-t pt-2">
                                <span className="text-muted-foreground">Point {tripForensicIndices ? tripForensicIndices.indexOf(selectedHistoryPoint) + 1 : selectedHistoryPoint + 1}/{tripForensicIndices ? tripForensicIndices.length : forensicData.total_points}:</span>
                                <span className="font-mono">{formatDT(selectedForensicPoint.timestamp, { timezone: activeHistoryTimezone, seconds: true })}</span>
                                {graphEnabledFields.map((fieldKey) => {
                                  const gf = forensicData?.graph_fields?.find(f => f.key === fieldKey);
                                  if (!gf) return null;
                                  const val = graphChartDataByIdx[selectedHistoryPoint]?.[fieldKey];
                                  let displayVal;
                                  if (gf.type === 'binary') {
                                    // Use actual mapped label from point data
                                    const mv = Object.values(selectedForensicPoint.mapped_values || {}).find(v => v.io_id === gf.io_id);
                                    displayVal = mv?.type === 'state' ? mv.value : (val === 1 ? 'ON' : 'OFF');
                                  } else {
                                    displayVal = val != null ? `${val} ${gf.units}` : '—';
                                  }
                                  return (
                                    <span key={fieldKey} className="inline-flex items-center gap-1">
                                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getGraphColor(gf) }} />
                                      <span className="text-muted-foreground">{gf.label}:</span>
                                      <span className="font-semibold">{displayVal}</span>
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="py-8 text-center text-sm text-muted-foreground">
                            <Activity className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                            <p>No graphical data available. Run a search first.</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Tab 3: Message Data */}
                    {forensicActiveTab === 'messages' && (
                      <div data-testid="forensic-messages-tab">
                        {forensicData?.points?.length > 0 ? (
                          <div className="space-y-2">
                            {/* Controls: mode toggle + pagination info */}
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="flex items-center gap-1">
                                {['mapped', 'raw'].map((mode) => (
                                  <button
                                    key={mode}
                                    onClick={() => setMessageViewMode(mode)}
                                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                                      messageViewMode === mode
                                        ? 'bg-[#2563eb] text-white'
                                        : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                                    }`}
                                    data-testid={`message-mode-${mode}`}
                                  >
                                    {mode === 'mapped' ? 'IO Mapped Data' : 'Raw Data'}
                                  </button>
                                ))}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>{sortedMessagePoints.length} messages</span>
                                <span>|</span>
                                <span>Page {messagePage + 1} of {messagePageCount}</span>
                              </div>
                            </div>

                            {/* Table */}
                            <div className="border rounded-lg overflow-hidden" data-testid="message-data-table-shell">
                              <div className="border-b bg-slate-50/70 py-1 dark:bg-slate-900/30" data-testid="message-data-horizontal-scroll-guide">
                                <div
                                  ref={messageTopScrollRef}
                                  className="h-4 overflow-x-auto overflow-y-hidden"
                                  onScroll={() => syncMessageHorizontalScroll('top')}
                                  data-testid="message-data-top-horizontal-scroll"
                                >
                                  <div className="h-1" style={{ width: `${messageTableWidth}px`, minWidth: '100%' }} />
                                </div>
                              </div>
                              <div
                                ref={messageBodyScrollRef}
                                className="scrollbar-corner-neutral overflow-x-auto"
                                onScroll={() => syncMessageHorizontalScroll('body')}
                                data-testid="message-data-bottom-horizontal-scroll"
                              >
                                <div ref={messageTableContentRef} className="inline-block min-w-full align-top" data-testid="message-data-table-content">
                                <table className="w-max min-w-full text-xs" style={{ tableLayout: 'auto' }} data-testid="message-data-table">
                                  <thead>
                                    <tr className="bg-muted/50 border-b">
                                      <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground w-8">#</th>
                                      <th
                                        className="px-2 py-1.5 text-left font-semibold text-muted-foreground cursor-pointer hover:text-foreground select-none"
                                        onClick={() => setMessageSortAsc(prev => !prev)}
                                        data-testid="message-sort-time"
                                      >
                                        Time (Position) {messageSortAsc ? '▲' : '▼'}
                                      </th>
                                      <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Time (Server)</th>
                                      {messageViewMode === 'raw' ? (
                                        <>
                                          <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Lat</th>
                                          <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Lng</th>
                                          <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Alt</th>
                                          <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Angle</th>
                                          <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Speed</th>
                                          <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Sats / Fix</th>
                                          <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Protocol</th>
                                          <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Event</th>
                                          <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Raw IO</th>
                                        </>
                                      ) : (
                                        <>
                                          <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Position</th>
                                          <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Speed</th>
                                          {forensicData.has_ignition_mapping !== false && (
                                            <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Ignition</th>
                                          )}
                                          <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">State</th>
                                          {/* Dynamic IO mapped columns */}
                                          {(forensicData.io_mappings || []).filter(m => m.show_telemetry !== false).map((m) => (
                                            <th key={m.io_id} className="px-2 py-1.5 text-left font-semibold text-muted-foreground">{m.display_name || m.io_type}</th>
                                          ))}
                                        </>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pagedMessagePoints.map((pt) => {
                                      const isSelected = selectedHistoryPoint === pt.idx;
                                      return (
                                        <tr
                                          key={pt.idx}
                                          className={`border-b cursor-pointer transition-colors ${
                                            isSelected
                                              ? 'bg-amber-50 dark:bg-amber-900/20 border-l-2 border-l-amber-500'
                                              : 'hover:bg-muted/30'
                                          }`}
                                          onClick={() => {
                                            selectHistoryPointManual(pt.idx);
                                            // Pan map to selected point
                                            if (historyMapType === 'osm' && historyLeafletMapRef.current) {
                                              historyLeafletMapRef.current.panTo([pt.lat, pt.lng]);
                                            } else if (historyMapRef.current) {
                                              historyMapRef.current.panTo({ lat: pt.lat, lng: pt.lng });
                                            }
                                          }}
                                          data-testid={`message-row-${pt.idx}`}
                                        >
                                          <td className="px-2 py-1 text-muted-foreground font-mono">{pt.idx + 1}</td>
                                          <td className="px-2 py-1 font-mono whitespace-nowrap">{formatDT(pt.timestamp, { timezone: activeHistoryTimezone, seconds: true })}</td>
                                          <td className="px-2 py-1 font-mono whitespace-nowrap text-muted-foreground">{formatDT(pt.server_timestamp, { timezone: activeHistoryTimezone, seconds: true })}</td>
                                          {messageViewMode === 'raw' ? (
                                            <>
                                              <td className="px-2 py-1 font-mono">
                                                <a href={`https://www.google.com/maps?q=${pt.lat},${pt.lng}`} target="_blank" rel="noopener noreferrer" className="text-[#2563eb] hover:underline">{pt.lat.toFixed(6)}</a>
                                              </td>
                                              <td className="px-2 py-1 font-mono">
                                                <a href={`https://www.google.com/maps?q=${pt.lat},${pt.lng}`} target="_blank" rel="noopener noreferrer" className="text-[#2563eb] hover:underline">{pt.lng.toFixed(6)}</a>
                                              </td>
                                              <td className="px-2 py-1">{pt.altitude}</td>
                                              <td className="px-2 py-1">{pt.heading}°</td>
                                              <td className="px-2 py-1">{pt.speed}</td>
                                              <td className="px-2 py-1">
                                                {pt.gps_fix ? (
                                                  <span
                                                    className={!pt.gps_fix.fix_valid ? 'text-red-500' : pt.gps_fix.old_fix ? 'text-amber-600' : ''}
                                                    title={`GPS fix: ${pt.gps_fix.fix_valid ? `valid${pt.gps_fix.fix_3d ? ' · 3D' : ''}` : 'INVALID'}${pt.gps_fix.old_fix ? ' · old/cached' : ''}${pt.gps_fix.position_accuracy_m != null ? ` · ±${pt.gps_fix.position_accuracy_m}m` : ''}${pt.gps_fix.pdop != null ? ` · PDOP ${pt.gps_fix.pdop}` : ''}${pt.gps_fix.gps_timestamp ? ` · Fix time: ${formatDT(pt.gps_fix.gps_timestamp, { timezone: activeHistoryTimezone, seconds: true })}` : ''} · Satellites: Not reported`}
                                                  >
                                                    {!pt.gps_fix.fix_valid ? 'invalid' : `0x${Number(pt.gps_fix.status_flags ?? 0).toString(16).toUpperCase().padStart(2, '0')}`}
                                                    {pt.gps_fix.position_accuracy_m != null ? ` ±${pt.gps_fix.position_accuracy_m}m` : ''}
                                                  </span>
                                                ) : (pt.satellites ?? '—')}
                                              </td>
                                              <td className="px-2 py-1">{pt.protocol}</td>
                                              <td className="px-2 py-1">{pt.event_id}</td>
                                              <td className="px-2 py-1 font-mono text-[10px]" title={JSON.stringify(pt.raw_io)}>
                                                {Object.keys(pt.raw_io || {}).length > 0
                                                  ? Object.entries(pt.raw_io).map(([k, v]) => `${k}:${v}`).join(', ')
                                                  : '—'}
                                              </td>
                                            </>
                                          ) : (
                                            <>
                                              <td className="px-2 py-1 font-mono text-[10px]">
                                                <a href={`https://www.google.com/maps?q=${pt.lat},${pt.lng}`} target="_blank" rel="noopener noreferrer" className="text-[#2563eb] hover:underline">
                                                  {pt.lat.toFixed(5)}, {pt.lng.toFixed(5)}
                                                </a>
                                              </td>
                                              <td className="px-2 py-1">{formatSpeed(pt.speed)}</td>
                                              {forensicData.has_ignition_mapping !== false && (
                                              <td className="px-2 py-1">
                                                <span className={pt.ignition ? 'text-green-600' : 'text-red-500'}>{pt.ignition ? 'ON' : 'OFF'}</span>
                                              </td>
                                              )}
                                              <td className="px-2 py-1">
                                                <Badge variant="outline" className={`text-[9px] ${
                                                  pt.motion_state === 'moving' ? 'border-green-400 text-green-600' :
                                                  pt.motion_state === 'idle' ? 'border-amber-400 text-amber-600' :
                                                  'border-red-400 text-red-600'
                                                }`}>
                                                  {pt.motion_state}
                                                </Badge>
                                              </td>
                                              {(forensicData.io_mappings || []).filter(m => m.show_telemetry !== false).map((m) => {
                                                const mv = Object.values(pt.mapped_values || {}).find(v => v.io_id === m.io_id);
                                                const mvColor = mv ? getMappedValueColor(m.io_id, mv.value, mv.raw) : null;
                                                return (
                                                  <td key={m.io_id} className="px-2 py-1">
                                                    {mv ? (
                                                      mv.type === 'state'
                                                        ? <span style={mvColor ? { color: mvColor } : undefined} className={!mvColor ? (mv.value === 'ON' ? 'text-green-600' : mv.value === 'OFF' ? 'text-red-500' : '') : ''}>{mv.value}</span>
                                                        : <span style={mvColor ? { color: mvColor } : undefined}>{mv.value} {mv.units}</span>
                                                    ) : '—'}
                                                  </td>
                                                );
                                              })}
                                            </>
                                          )}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                </div>
                              </div>
                            </div>

                            {/* Pagination */}
                            {messagePageCount > 1 && (
                              <div className="flex items-center justify-between pt-1">
                                <Button
                                  variant="outline" size="sm" className="h-7 text-xs"
                                  disabled={messagePage <= 0}
                                  onClick={() => setMessagePage(p => p - 1)}
                                  data-testid="message-page-prev"
                                >
                                  <ChevronLeft className="w-3 h-3 mr-1" /> Previous
                                </Button>
                                <div className="flex items-center gap-1">
                                  {Array.from({ length: Math.min(messagePageCount, 7) }, (_, i) => {
                                    let pageNum;
                                    if (messagePageCount <= 7) {
                                      pageNum = i;
                                    } else if (messagePage < 3) {
                                      pageNum = i;
                                    } else if (messagePage > messagePageCount - 4) {
                                      pageNum = messagePageCount - 7 + i;
                                    } else {
                                      pageNum = messagePage - 3 + i;
                                    }
                                    return (
                                      <button
                                        key={pageNum}
                                        onClick={() => setMessagePage(pageNum)}
                                        className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                                          messagePage === pageNum ? 'bg-[#2563eb] text-white' : 'hover:bg-muted text-muted-foreground'
                                        }`}
                                      >
                                        {pageNum + 1}
                                      </button>
                                    );
                                  })}
                                </div>
                                <Button
                                  variant="outline" size="sm" className="h-7 text-xs"
                                  disabled={messagePage >= messagePageCount - 1}
                                  onClick={() => setMessagePage(p => p + 1)}
                                  data-testid="message-page-next"
                                >
                                  Next <ChevronRight className="w-3 h-3 ml-1" />
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="py-8 text-center text-sm text-muted-foreground">
                            <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                            <p>No message data available. Run a search first.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== INVESTIGATIONS TAB ==================== */}
      {activeTab === 'investigations' && hasInvestigations && (
        <div className="space-y-4" data-testid="investigations-tab">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Search className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">Investigations</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">Find which vehicles were near a specific location at a given time.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="analytics-investigations-mobile-responsive-workspace">
            {/* Search Form */}
            <Card className={`${mobileInvestigationView === 'search' ? 'block' : 'hidden'} lg:block`} data-testid="analytics-investigations-mobile-search-pane">
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-medium">Vehicle Proximity Search</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-3">
                {/* Address Input */}
                <div>
                  <Label className="text-xs font-medium mb-1 block">Address / Location</Label>
                  <AddressAutocomplete
                    value={invAddress}
                    onChange={setInvAddress}
                    onSelect={(s) => {
                      const lat = parseFloat(s.lat);
                      const lng = parseFloat(s.lon);
                      setInvLat(lat);
                      setInvLng(lng);
                      reverseGeocode(lat, lng).then((addr) => {
                        if (addr) setInvAddress(addr);
                      });
                      toast.success(`Location found`);
                    }}
                    placeholder="Enter address to search..."
                    inputClassName="h-9 text-base md:text-sm"
                    data-testid="inv-address"
                  />
                  {invLat != null && invLng != null && (
                    <p className="text-[10px] text-green-600 mt-1">Location set: {invLat.toFixed(6)}, {invLng.toFixed(6)}</p>
                  )}
                </div>

                {/* GPS Coordinates */}
                <div>
                  <Label className="text-[10px] text-muted-foreground">GPS Coordinates</Label>
                  <Input
                    value={invLat != null && invLng != null ? `${invLat}, ${invLng}` : ''}
                    onChange={(e) => {
                      const parts = e.target.value.split(',').map(s => s.trim());
                      const lat = parts[0] ? parseFloat(parts[0]) : null;
                      const lng = parts[1] ? parseFloat(parts[1]) : null;
                      setInvLat(!isNaN(lat) ? lat : null);
                      setInvLng(parts[1] !== undefined && !isNaN(lng) ? lng : null);
                    }}
                    placeholder="-33.925087, 151.055512"
                    className="h-8 text-base md:text-xs font-mono"
                    data-testid="inv-coords"
                  />
                </div>

                {/* Search Timezone */}
                <div>
                  <Label className="text-xs font-medium mb-1 block">Search Timezone</Label>
                  <p className="text-[10px] text-[#2563eb]">User Timezone: {invTimezoneLabel}</p>
                </div>

                {/* Date Range Preset */}
                <div>
                  <Label className="text-xs font-medium mb-1 block">Date Range</Label>
                  <Select value={invDatePreset} onValueChange={setInvDatePreset}>
                    <SelectTrigger className="h-8 dark:bg-[#172031]" data-testid="inv-date-preset">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_PRESETS.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Start Date & Time */}
                <div>
                  <Label className="text-xs font-medium mb-1 block">Start Date & Time</Label>
                  <Popover open={invStartOpen} onOpenChange={setInvStartOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full h-8 justify-between text-sm font-normal dark:bg-[#172031]" data-testid="inv-start-date">
                        {formatDT(invDateRange.start, { timezone: invTimezone, seconds: true })}
                        <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={invDateRange.start}
                        onSelect={(date) => {
                          if (!date) return;
                          const prev = getZonedParts(invDateRange.start, { timezone: invTimezone });
                          const nextStart = zonedDateTimeToUtc({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(), hour: prev?.hour || 0, minute: prev?.minute || 0, second: 0 }, invTimezone);
                          setInvDateRange(r => ({ ...r, start: nextStart }));
                          setInvDatePreset('custom');
                        }}
                      />
                      <div className="border-t p-3 flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                        <Input
                          type="time"
                          value={getTimeValueInTimezone(invDateRange.start, { timezone: invTimezone })}
                          onChange={(e) => {
                            const [h, m] = e.target.value.split(':').map(Number);
                            const prev = getZonedParts(invDateRange.start, { timezone: invTimezone });
                            const nextStart = zonedDateTimeToUtc({ year: prev?.year, month: prev?.month, day: prev?.day, hour: h || 0, minute: m || 0, second: 0 }, invTimezone);
                            setInvDateRange(r => ({ ...r, start: nextStart }));
                            setInvDatePreset('custom');
                          }}
                          className="h-8 text-base md:text-sm flex-1"
                        />
                        <Button size="sm" className="h-8 text-xs" onClick={() => setInvStartOpen(false)}>Done</Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* End Date & Time */}
                <div>
                  <Label className="text-xs font-medium mb-1 block">End Date & Time</Label>
                  <Popover open={invEndOpen} onOpenChange={setInvEndOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full h-8 justify-between text-sm font-normal dark:bg-[#172031]" data-testid="inv-end-date">
                        {formatDT(invDateRange.end, { timezone: invTimezone, seconds: true })}
                        <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={invDateRange.end}
                        onSelect={(date) => {
                          if (!date) return;
                          const prev = getZonedParts(invDateRange.end, { timezone: invTimezone });
                          const nextEnd = zonedDateTimeToUtc({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(), hour: prev?.hour || 23, minute: prev?.minute || 59, second: 59 }, invTimezone);
                          setInvDateRange(r => ({ ...r, end: nextEnd }));
                          setInvDatePreset('custom');
                        }}
                      />
                      <div className="border-t p-3 flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                        <Input
                          type="time"
                          value={getTimeValueInTimezone(invDateRange.end, { timezone: invTimezone })}
                          onChange={(e) => {
                            const [h, m] = e.target.value.split(':').map(Number);
                            const prev = getZonedParts(invDateRange.end, { timezone: invTimezone });
                            const nextEnd = zonedDateTimeToUtc({ year: prev?.year, month: prev?.month, day: prev?.day, hour: h || 23, minute: m || 59, second: 59 }, invTimezone);
                            setInvDateRange(r => ({ ...r, end: nextEnd }));
                            setInvDatePreset('custom');
                          }}
                          className="h-8 text-base md:text-sm flex-1"
                        />
                        <Button size="sm" className="h-8 text-xs" onClick={() => setInvEndOpen(false)}>Done</Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Radius */}
                <div>
                  <Label className="text-xs font-medium mb-1 block">Search Radius</Label>
                  <Select value={String(invRadius)} onValueChange={(v) => setInvRadius(Number(v))}>
                    <SelectTrigger className="h-8 text-xs dark:bg-[#172031]" data-testid="inv-radius-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="100">100 m</SelectItem>
                      <SelectItem value="250">250 m</SelectItem>
                      <SelectItem value="500">500 m</SelectItem>
                      <SelectItem value="1000">1 km</SelectItem>
                      <SelectItem value="2000">2 km</SelectItem>
                      <SelectItem value="5000">5 km</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Search + Clear */}
                <div className="flex items-center gap-2">
                  <Button size="sm"
                    className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                    onClick={handleInvSearch}
                    disabled={invLoading || invLat == null || invLng == null}
                    data-testid="inv-search-btn"
                  >
                    {invLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Search Vehicles
                  </Button>
                  <Button size="sm"
                    variant="outline"

                    onClick={clearInvestigation}
                    disabled={invLoading || (!invAddress && invLat == null && invResults === null)}
                    data-testid="inv-clear-btn"
                    title="Clear the investigation (address, location, results and filters)"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <div className={`${mobileInvestigationView === 'results' ? 'block' : 'hidden'} lg:col-span-2 lg:block space-y-4`} data-testid="analytics-investigations-mobile-results-pane">
              {invResults === null && (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-16">
                    <Search className="w-12 h-12 text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">Enter an address, time range, and radius to find nearby vehicles</p>
                  </CardContent>
                </Card>
              )}

              {invResults && (
                <Card>
                  <CardHeader className="pb-2 px-4 pt-4">
                    <CardTitle className="text-sm font-medium flex items-center justify-between">
                      <span>Results</span>
                      <Badge variant={invResults.total > 0 ? 'default' : 'secondary'} className="text-xs">
                        {invResults.total} vehicle{invResults.total !== 1 ? 's' : ''} found
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    {invResults.results.length === 0 ? (
                      <div className="py-8 text-center text-sm text-muted-foreground">
                        <Car className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                        <p>No vehicles found within {invResults.radius_m >= 1000 ? `${invResults.radius_m / 1000} km` : `${invResults.radius_m} m`} of the specified location during the given time range.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {invResults.results.map((r, i) => (
                          <div
                            key={r.device_id}
                            className="flex flex-col gap-3 p-3 rounded-lg border bg-card transition-colors sm:flex-row sm:items-center sm:justify-between"
                            data-testid={`inv-result-${i}`}
                          >
                            <div className="flex min-w-0 items-center gap-3">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#2563eb]/10 text-[#2563eb] font-bold text-sm">
                                {i + 1}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate" data-testid={`inv-result-device-${i}`}>{formatRecordDeviceLabel(r, devicesById)}</p>
                                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                                  {r.registration && <span>Rego: {r.registration}</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-start gap-2 sm:items-end sm:text-right">
                              <p className="text-sm font-bold text-[#2563eb]">
                                {r.distance_m >= 1000 ? `${(r.distance_m / 1000).toFixed(2)} km` : `${r.distance_m} m`}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                {formatSpeed(r.closest_point.speed)} at {formatDT(r.closest_point.timestamp, { seconds: true })}
                              </p>
                              <a
                                href={`https://www.google.com/maps?q=${r.closest_point.lat},${r.closest_point.lng}`}
                                target="_blank" rel="noopener noreferrer"
                                className="text-[10px] text-[#2563eb] hover:underline"
                              >
                                {r.closest_point.lat.toFixed(5)}, {r.closest_point.lng.toFixed(5)}
                              </a>
                              <Button
                                type="button"
                                size="sm"
                                className="h-8 bg-[#2563eb] px-3 text-xs text-white hover:bg-[#1d4ed8]"
                                onClick={() => handleOpenInvestigationInHistory(r)}
                                data-testid={`inv-open-history-${i}`}
                              >
                                <History className="w-3.5 h-3.5 mr-1.5" />
                                Open in History
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
